/* =============================================================================
 * VentureWrist simulation Engine — lifted verbatim from the smartwatch-sim.html
 * prototype (Phase 0). This is the "crown jewel": deterministic game logic.
 *
 * DO NOT hand-edit game math here without a corresponding golden-master update.
 * The code below is the exact closure of constants, config, tech tree, game
 * factories (newGame/newProduct) and the Engine object, extracted unchanged
 * from the prototype so its behavior can be frozen by tests before any TS port.
 *
 * Source ranges (prototype lines): 381-1170, 1330-1409, 1410-3086.
 * ===========================================================================*/
/* eslint-disable */

const fmtM = v => (v<0?'-':'') + '$' + Math.abs(v/1e6).toFixed(Math.abs(v)>=1e7?1:2) + 'M';
const fmtK = v => Math.abs(v)>=1e6 ? (v/1e6).toFixed(2)+'M' : Math.abs(v)>=1e3 ? Math.round(v/1e3)+'k' : Math.round(v);
const fmt$ = v => '$' + Math.round(v).toLocaleString();
const clamp = (v,a,b)=>Math.max(a,Math.min(b,v));
const uid = ()=>Math.random().toString(36).slice(2,10);
// relative time + html escaping for the team layer
const tago = ts=>{ if(!ts) return '—'; const s=Math.max(0,(Date.now()-ts)/1000);
  if(s<60) return 'just now'; if(s<3600) return Math.floor(s/60)+'m ago'; if(s<864e2) return Math.floor(s/3600)+'h ago';
  const d=Math.floor(s/864e2); return d+'d ago'; };
const esc = s=>String(s==null?'':s).replace(/[&<>"]/g,c=>({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;'}[c]));
// countdown to a deadline (ms epoch) → "2d 4h" / "3h 12m" / "closed"
const cdStr = ts=>{ if(!ts) return 'no deadline'; let s=(ts-Date.now())/1000; if(s<=0) return 'deadline passed';
  const d=Math.floor(s/86400); s-=d*86400; const h=Math.floor(s/3600); s-=h*3600; const m=Math.floor(s/60);
  return d>0? d+'d '+h+'h' : h>0? h+'h '+m+'m' : m+'m'; };

const COMPONENTS = {
  cpu: [
    {id:'c1', name:'EconChip E1',   cost:9,  perf:35, power:1.05, desc:'Entry dual-core'},
    {id:'c2', name:'Velocity V4',   cost:22, perf:62, power:1.15, desc:'Mid quad-core'},
    {id:'c3', name:'Apex X9',       cost:41, perf:88, power:1.30, desc:'Flagship SoC'},
    {id:'c4', name:'Apex X9-LP',    cost:46, perf:86, power:0.92, desc:'Low-power flagship', lock:'lowpower'},
    {id:'c5', name:'Apex X10',      cost:54, perf:94, power:0.85, desc:'3 nm next-gen', lock:'nmprocess'},
    {id:'c6', name:'NeuraCore N1',  cost:62, perf:99, power:0.72, desc:'On-device NPU silicon', lock:'neuracore'},
  ],
  display: [
    {id:'d1', name:'LCD 1.4"',        cost:8,  quality:38, power:1.0,  fashion:30},
    {id:'d2', name:'OLED 1.6"',       cost:19, quality:68, power:0.92, fashion:60},
    {id:'d3', name:'AMOLED LTPO 1.8"',cost:34, quality:92, power:0.78, fashion:85},
    {id:'d4', name:'MicroLED 1.9"',   cost:48, quality:97, power:0.60, fashion:95, lock:'microled'},
    {id:'d5', name:'Wrap AMOLED 2.1"',cost:58, quality:99, power:0.55, fashion:99, lock:'flexglass'},
  ],
  battery: [
    {id:'b1', name:'220 mAh', cost:5,  cap:220, weight:14},
    {id:'b2', name:'310 mAh', cost:8,  cap:310, weight:20},
    {id:'b3', name:'420 mAh', cost:13, cap:420, weight:28},
    {id:'b4', name:'Solid-State 460 mAh', cost:21, cap:460, weight:18, lock:'solidstate'},
    {id:'b5', name:'Graphene 520 mAh',    cost:28, cap:520, weight:15, lock:'graphene'},
  ],
  casing: [
    {id:'k1', name:'Polycarbonate',   cost:6,  fashion:30, durab:45, weight:18, color:0x2a2e36, metal:0, rough:.65},
    {id:'k2', name:'Aluminium',       cost:15, fashion:60, durab:65, weight:26, color:0x9aa3ad, metal:.85, rough:.38},
    {id:'k3', name:'Stainless Steel', cost:27, fashion:78, durab:85, weight:38, color:0xc8ccd4, metal:1, rough:.22},
    {id:'k4', name:'Titanium',        cost:46, fashion:92, durab:95, weight:24, color:0x8d9296, metal:.95, rough:.32},
    {id:'k5', name:'Ceramic Composite',cost:38, fashion:88, durab:92, weight:20, color:0xe8e6e1, metal:.2, rough:.30, lock:'ceramic'},
  ],
  strap: [
    {id:'s1', name:'Silicone Sport', cost:3,  fashion:35, comfort:80, color:null},
    {id:'s2', name:'Woven Fabric',   cost:6,  fashion:55, comfort:85, color:null},
    {id:'s3', name:'Leather',        cost:14, fashion:78, comfort:70, color:0x6b4226},
    {id:'s4', name:'Metal Link',     cost:24, fashion:90, comfort:55, color:0xb9bec7},
  ],
  sensors: [
    {id:'hr',   name:'Heart Rate',  cost:6,  health:25, power:.04},
    {id:'gps',  name:'GPS',         cost:9,  health:15, fit:30, power:.10},
    {id:'spo2', name:'SpO₂',        cost:7,  health:20, power:.04},
    {id:'slp',  name:'Sleep Suite', cost:5,  health:15, power:.03},
    {id:'ecg',  name:'ECG (medical-grade)', cost:16, health:35, power:.05, lock:'ecg'},
    {id:'tmp',  name:'Skin Temp',   cost:6,  health:12, power:.02},
    {id:'bp',   name:'Blood Pressure', cost:18, health:32, power:.05, lock:'bp'},
    {id:'glu',  name:'Glucose Trend',  cost:26, health:45, power:.06, lock:'glucose'},
  ],
  style: [
    {id:'sporty',  name:'Sporty',  shape:'round',  desc:'Bold bezel, athletic'},
    {id:'classic', name:'Classic', shape:'round',  desc:'Traditional round face'},
    {id:'modern',  name:'Modern',  shape:'square', desc:'Squircle, tech-forward'},
    {id:'luxury',  name:'Luxury',  shape:'round',  desc:'Polished, jewelry-like'},
  ],
  colors: [
    {id:'midnight', name:'Midnight', hex:0x1c1f26},
    {id:'silver',   name:'Silver',   hex:0xc9ced6},
    {id:'gold',     name:'Gold',     hex:0xd4af6a},
    {id:'ocean',    name:'Ocean',    hex:0x2563eb},
    {id:'rose',     name:'Rosé',     hex:0xd99a9a},
    {id:'forest',   name:'Forest',   hex:0x2f6f4f},
    {id:'purple',   name:'Purple',   hex:0x7c3aed},
    {id:'red',      name:'Crimson',  hex:0xdc2626},
    {id:'orange',   name:'Orange',   hex:0xf97316},
  ],
  sizes: [
    {id:'40', name:'40 mm', scale:.92, weight:-4, fashionMod:5},
    {id:'44', name:'44 mm', scale:1.0, weight:0, fashionMod:0},
    {id:'47', name:'47 mm', scale:1.1, weight:6, fashionMod:-5, batBonus:1.12},
  ],
};

/* Suppliers: per category, choice affects cost/quality/lead/reliability. lat/lon for map */
const SUPPLIERS = {
  chips: [
    {id:'tsw', name:'Formosa Semis', country:'Taiwan',   lat:23.7, lon:121.0, costM:1.18, qualM:1.12, lead:10, rel:.97},
    {id:'krc', name:'Hanu Logic',    country:'S. Korea', lat:36.5, lon:127.9, costM:1.08, qualM:1.06, lead:8,  rel:.95},
    {id:'cnc', name:'Shenzu Micro',  country:'China',    lat:22.6, lon:114.0, costM:0.86, qualM:0.92, lead:5,  rel:.88},
  ],
  display: [
    {id:'krd', name:'Sora Display',  country:'S. Korea', lat:37.4, lon:126.9, costM:1.12, qualM:1.12, lead:7, rel:.96},
    {id:'jpd', name:'Kyoshi Optics', country:'Japan',    lat:35.0, lon:135.7, costM:1.22, qualM:1.18, lead:9, rel:.98},
    {id:'cnd', name:'BrightOri',     country:'China',    lat:30.6, lon:104.1, costM:0.84, qualM:0.90, lead:5, rel:.90},
  ],
  battery: [
    {id:'krb', name:'VoltaCell',     country:'S. Korea', lat:35.2, lon:128.7, costM:1.10, qualM:1.10, lead:6, rel:.96},
    {id:'cnb', name:'Ningbo Power',  country:'China',    lat:29.9, lon:121.6, costM:0.82, qualM:0.90, lead:4, rel:.87},
    {id:'deb', name:'RheinEnergie',  country:'Germany',  lat:50.9, lon:6.96,  costM:1.28, qualM:1.16, lead:11, rel:.985},
  ],
  sensors: [
    {id:'jps', name:'Nidek Precision', country:'Japan',  lat:36.2, lon:137.9, costM:1.20, qualM:1.20, lead:9, rel:.98},
    {id:'des', name:'Schwarz Sensorik',country:'Germany',lat:48.7, lon:9.18,  costM:1.30, qualM:1.22, lead:11, rel:.985},
    {id:'cns', name:'Huaxin Sense',    country:'China',  lat:31.2, lon:121.5, costM:0.85, qualM:0.92, lead:5, rel:.89},
  ],
  assembly: [
    {id:'cna', name:'FoxLine (Shenzhen)', country:'China',   lat:22.5, lon:113.9, costM:1.00, qualM:1.00, lead:4, rel:.95, capacity:900},
    {id:'vna', name:'HanViet Mfg',        country:'Vietnam', lat:21.0, lon:105.8, costM:0.92, qualM:0.95, lead:6, rel:.92, capacity:600},
    {id:'ina', name:'Bharat Assembly',    country:'India',   lat:12.9, lon:77.6,  costM:0.88, qualM:0.92, lead:7, rel:.90, capacity:500},
    {id:'mxa', name:'Norte Ensamble',     country:'Mexico',  lat:25.7, lon:-100.3,costM:1.06, qualM:0.98, lead:5, rel:.93, capacity:400},
  ],
};

/* Regions are purely a grouping/reporting device — countries are the game entity. */
const REGIONS = [
  {id:'na',   name:'North America'},
  {id:'eu',   name:'Europe'},
  {id:'asia', name:'Asia-Pacific'},
  {id:'sa',   name:'South America'},
  {id:'mea',  name:'Middle East & Africa'},
];

/* Segment-mix profiles: share of a country's buyers in each segment (sums to 1) */
const PROFILES = {
  premium:   {budget:.20, fitness:.22, health:.22, pro:.20, fashion:.16},
  balanced:  {budget:.30, fitness:.22, health:.18, pro:.17, fashion:.13},
  value:     {budget:.48, fitness:.18, health:.12, pro:.12, fashion:.10},
  empremium: {budget:.35, fitness:.20, health:.15, pro:.17, fashion:.13}, // emerging-premium
};

/* Top 40 country markets by estimated smartwatch sales (Russia excluded per design decision).
   pop = millions · income = willingness-to-pay multiplier · ecom = e-commerce maturity (caps online coverage)
   urban = urbanization (stores needed per buyer) · profile = segment mix · lat/lon for the map */
const COUNTRIES = [
  // North America
  {id:'us',  name:'USA',          flag:'🇺🇸', region:'na',   pop:340,  income:1.25, ecom:.92, urban:.83, profile:'premium',   lat:39,   lon:-98},
  {id:'ca',  name:'Canada',       flag:'🇨🇦', region:'na',   pop:40,   income:1.15, ecom:.88, urban:.81, profile:'premium',   lat:56,   lon:-106},
  {id:'mx',  name:'Mexico',       flag:'🇲🇽', region:'na',   pop:130,  income:0.70, ecom:.60, urban:.81, profile:'empremium', lat:23,   lon:-102},
  // Europe
  {id:'de',  name:'Germany',      flag:'🇩🇪', region:'eu',   pop:84,   income:1.15, ecom:.85, urban:.78, profile:'premium',   lat:51,   lon:10},
  {id:'uk',  name:'UK',           flag:'🇬🇧', region:'eu',   pop:69,   income:1.15, ecom:.93, urban:.84, profile:'premium',   lat:54,   lon:-2},
  {id:'fr',  name:'France',       flag:'🇫🇷', region:'eu',   pop:66,   income:1.10, ecom:.85, urban:.81, profile:'premium',   lat:47,   lon:2},
  {id:'it',  name:'Italy',        flag:'🇮🇹', region:'eu',   pop:59,   income:1.00, ecom:.72, urban:.71, profile:'balanced',  lat:42,   lon:12},
  {id:'es',  name:'Spain',        flag:'🇪🇸', region:'eu',   pop:48,   income:1.00, ecom:.78, urban:.81, profile:'balanced',  lat:40,   lon:-4},
  {id:'tr',  name:'Turkey',       flag:'🇹🇷', region:'eu',   pop:87,   income:0.62, ecom:.62, urban:.77, profile:'empremium', lat:39,   lon:35},
  {id:'pl',  name:'Poland',       flag:'🇵🇱', region:'eu',   pop:38,   income:0.85, ecom:.78, urban:.60, profile:'balanced',  lat:52,   lon:19},
  {id:'nl',  name:'Netherlands',  flag:'🇳🇱', region:'eu',   pop:18,   income:1.20, ecom:.92, urban:.93, profile:'premium',   lat:52.3, lon:5},
  {id:'ch',  name:'Switzerland',  flag:'🇨🇭', region:'eu',   pop:9,    income:1.30, ecom:.88, urban:.74, profile:'premium',   lat:47,   lon:8},
  {id:'se',  name:'Sweden',       flag:'🇸🇪', region:'eu',   pop:11,   income:1.20, ecom:.90, urban:.88, profile:'premium',   lat:62,   lon:15},
  {id:'no',  name:'Norway',       flag:'🇳🇴', region:'eu',   pop:5.5,  income:1.30, ecom:.90, urban:.84, profile:'premium',   lat:61,   lon:9},
  {id:'be',  name:'Belgium',      flag:'🇧🇪', region:'eu',   pop:12,   income:1.15, ecom:.85, urban:.98, profile:'premium',   lat:50.6, lon:4.5},
  {id:'at',  name:'Austria',      flag:'🇦🇹', region:'eu',   pop:9,    income:1.15, ecom:.82, urban:.59, profile:'premium',   lat:47.5, lon:14},
  {id:'dk',  name:'Denmark',      flag:'🇩🇰', region:'eu',   pop:6,    income:1.25, ecom:.92, urban:.88, profile:'premium',   lat:56,   lon:10},
  {id:'cz',  name:'Czechia',      flag:'🇨🇿', region:'eu',   pop:11,   income:0.90, ecom:.80, urban:.74, profile:'balanced',  lat:49.8, lon:15.5},
  {id:'ie',  name:'Ireland',      flag:'🇮🇪', region:'eu',   pop:5,    income:1.20, ecom:.88, urban:.64, profile:'premium',   lat:53,   lon:-8},
  // Asia-Pacific
  {id:'cn',  name:'China',        flag:'🇨🇳', region:'asia', pop:1411, income:0.78, ecom:.95, urban:.65, profile:'empremium', lat:35,   lon:105},
  {id:'in',  name:'India',        flag:'🇮🇳', region:'asia', pop:1429, income:0.45, ecom:.70, urban:.36, profile:'value',     lat:21,   lon:78},
  {id:'jp',  name:'Japan',        flag:'🇯🇵', region:'asia', pop:124,  income:1.20, ecom:.85, urban:.92, profile:'premium',   lat:36,   lon:138},
  {id:'kr',  name:'S. Korea',     flag:'🇰🇷', region:'asia', pop:52,   income:1.10, ecom:.95, urban:.81, profile:'balanced',  lat:36.5, lon:128},
  {id:'au',  name:'Australia',    flag:'🇦🇺', region:'asia', pop:26,   income:1.20, ecom:.88, urban:.86, profile:'premium',   lat:-25,  lon:134},
  {id:'idn', name:'Indonesia',    flag:'🇮🇩', region:'asia', pop:281,  income:0.50, ecom:.65, urban:.58, profile:'value',     lat:-2,   lon:118},
  {id:'tw',  name:'Taiwan',       flag:'🇹🇼', region:'asia', pop:23,   income:1.05, ecom:.85, urban:.80, profile:'balanced',  lat:23.8, lon:121},
  {id:'th',  name:'Thailand',     flag:'🇹🇭', region:'asia', pop:72,   income:0.62, ecom:.70, urban:.53, profile:'value',     lat:15,   lon:101},
  {id:'my',  name:'Malaysia',     flag:'🇲🇾', region:'asia', pop:34,   income:0.72, ecom:.75, urban:.78, profile:'balanced',  lat:4,    lon:102},
  {id:'vn',  name:'Vietnam',      flag:'🇻🇳', region:'asia', pop:100,  income:0.48, ecom:.68, urban:.39, profile:'value',     lat:16,   lon:106},
  {id:'ph',  name:'Philippines',  flag:'🇵🇭', region:'asia', pop:117,  income:0.45, ecom:.62, urban:.48, profile:'value',     lat:13,   lon:122},
  {id:'sg',  name:'Singapore',    flag:'🇸🇬', region:'asia', pop:6,    income:1.30, ecom:.90, urban:1.0, profile:'premium',   lat:1.35, lon:103.8},
  {id:'hk',  name:'Hong Kong',    flag:'🇭🇰', region:'asia', pop:7.5,  income:1.25, ecom:.88, urban:1.0, profile:'premium',   lat:22.3, lon:114.2},
  // South America
  {id:'br',  name:'Brazil',       flag:'🇧🇷', region:'sa',   pop:212,  income:0.62, ecom:.68, urban:.87, profile:'empremium', lat:-10,  lon:-52},
  {id:'cl',  name:'Chile',        flag:'🇨🇱', region:'sa',   pop:20,   income:0.78, ecom:.72, urban:.88, profile:'balanced',  lat:-33,  lon:-71},
  {id:'ar',  name:'Argentina',    flag:'🇦🇷', region:'sa',   pop:46,   income:0.55, ecom:.65, urban:.92, profile:'balanced',  lat:-34,  lon:-64},
  {id:'co',  name:'Colombia',     flag:'🇨🇴', region:'sa',   pop:52,   income:0.55, ecom:.62, urban:.81, profile:'balanced',  lat:4,    lon:-73},
  // Middle East & Africa
  {id:'sau', name:'Saudi Arabia', flag:'🇸🇦', region:'mea',  pop:36,   income:0.90, ecom:.70, urban:.84, profile:'balanced',  lat:24,   lon:45},
  {id:'ae',  name:'UAE',          flag:'🇦🇪', region:'mea',  pop:10,   income:1.20, ecom:.85, urban:.87, profile:'premium',   lat:24,   lon:54},
  {id:'il',  name:'Israel',       flag:'🇮🇱', region:'mea',  pop:10,   income:1.05, ecom:.80, urban:.92, profile:'premium',   lat:31,   lon:35},
  {id:'za',  name:'South Africa', flag:'🇿🇦', region:'mea',  pop:63,   income:0.50, ecom:.60, urban:.68, profile:'value',     lat:-29,  lon:25},
];

/* Distribution methods — per-country activation. control: 1 = your campaigns apply, 0 = distributor's
   generic push, 0.6 = partial (carrier). segCov = how well the method reaches each segment. */
const METHODS = [
  {id:'online',  name:'Cross-border online', icon:'🌐', fee:.08, entry:0,    control:1,
   segCov:{budget:1, fitness:1, health:.85, pro:.9, fashion:.8},
   desc:'Default everywhere, free. 8% fulfilment cost. Reach capped by local e-commerce maturity (ecom × 45%).'},
  {id:'dist',    name:'Local distributor',   icon:'🤝', fee:.28, entry:3e5,  control:0,
   segCov:{budget:1, fitness:.9, health:.8, pro:.65, fashion:.45},
   desc:'$300k entry · 28% margin · up to 65% reach. Fast, cheap reach — but the distributor controls promotion: your campaigns don\'t apply and brand builds at half rate.'},
  {id:'stores',  name:'Own retail stores',   icon:'🏪', fee:.12, entry:5e5,  control:1, capex:3.5e5, opex:7e4,
   segCov:{budget:.8, fitness:.85, health:.9, pro:.95, fashion:.9},
   desc:'$500k subsidiary + $350k per store + $70k/qtr/store. Coverage scales with store count, up to 95%. Full marketing control & in-country brand.'},
  {id:'carrier', name:'Carrier bundling',    icon:'📡', fee:.32, entry:2.5e5, control:.6,
   segCov:{budget:1, fitness:1, health:0, pro:0, fashion:0},
   desc:'$250k per country · 32% carrier cut · up to 50% reach, budget & fitness segments only. Volume play in mid-income markets; partial marketing control.'},
];
const FLAGSHIP_COST = 7e5, FLAGSHIP_BRAND = 45, STORE_SEVERANCE = 6e4, STORE_BUILD_LIMIT = 8;

/* Freight $/unit by destination region (assembly in Asia, except Norte Ensamble in Mexico) */
const FREIGHT_RATES = {
  asia: {asia:5.5, na:7,   eu:7, sa:7,   mea:9},
  mx:   {asia:9,   na:5.5, eu:8, sa:6.5, mea:9},
};
/* DESIGN-insolvency-and-direct-fulfilment §5.1 (rules v2): when no warehouse can serve an
   online order it ships direct from assembly at full cross-border freight, but slow delivery
   loses buyers — only this share of the remaining online demand still converts. */
const DIRECT_ONLINE_CONV = 0.65;

/* ============================================================
   SHARED CITY ROSTER (DESIGN-assembly-factories.md §7) — single source of truth
   for BOTH owned assembly factories and warehouses. The production doc consumes
   factoryCities()/laborIdx/buildIdx; the logistics doc consumes whCities()/
   buildIdx and the REGION_FREIGHT matrix below.
============================================================ */
const CITIES = [
  // North America
  {id:'la',  city:'Los Angeles',      country:'USA',          cc:'us', region:'na',   lat:34.0,  lon:-118.2, whFit:3, factoryFit:0, laborIdx:1.50, buildIdx:1.55, note:'Pacific gateway; West Coast demand + cheap Asian inbound'},
  {id:'dal', city:'Dallas',           country:'USA',          cc:'us', region:'na',   lat:32.8,  lon:-96.8,  whFit:3, factoryFit:1, laborIdx:1.40, buildIdx:1.20, note:'Central US distribution; optional Made-in-USA micro-bonus'},
  {id:'nyc', city:'New York',         country:'USA',          cc:'us', region:'na',   lat:40.7,  lon:-74.0,  whFit:3, factoryFit:0, laborIdx:1.52, buildIdx:1.70, note:'Largest US market; Atlantic inbound; covers Canada'},
  {id:'gdl', city:'Guadalajara',      country:'Mexico',       cc:'mx', region:'na',   lat:20.7,  lon:-103.3, whFit:2, factoryFit:3, laborIdx:0.98, buildIdx:0.92, note:'Mexico electronics-mfg capital; fits Norte Ensamble'},
  {id:'mty', city:'Monterrey',        country:'Mexico',       cc:'mx', region:'na',   lat:25.7,  lon:-100.3, whFit:2, factoryFit:3, laborIdx:1.02, buildIdx:0.95, note:'Industrial hub + best US-border logistics'},
  // Europe
  {id:'rtm', city:'Rotterdam',        country:'Netherlands',  cc:'nl', region:'eu',   lat:51.9,  lon:4.5,    whFit:3, factoryFit:0, laborIdx:1.40, buildIdx:1.45, note:'Europe largest port; cheapest inbound'},
  {id:'fra', city:'Frankfurt',        country:'Germany',      cc:'de', region:'eu',   lat:50.1,  lon:8.7,    whFit:3, factoryFit:0, laborIdx:1.42, buildIdx:1.40, note:'Central + air-cargo hub; DACH core'},
  {id:'waw', city:'Warsaw',           country:'Poland',       cc:'pl', region:'eu',   lat:52.2,  lon:21.0,   whFit:2, factoryFit:2, laborIdx:1.18, buildIdx:1.12, note:'CEE distribution + low-cost tariff-free EU assembly'},
  {id:'bud', city:'Budapest',         country:'Hungary',      cc:'hu', region:'eu',   lat:47.5,  lon:19.0,   whFit:1, factoryFit:3, laborIdx:1.12, buildIdx:1.05, note:'Established EU electronics-assembly base'},
  {id:'mil', city:'Milan',            country:'Italy',        cc:'it', region:'eu',   lat:45.5,  lon:9.2,    whFit:2, factoryFit:1, laborIdx:1.30, buildIdx:1.30, note:'Southern-Europe demand + N. Italian mfg'},
  // Asia
  {id:'szx', city:'Shenzhen',         country:'China',        cc:'cn', region:'asia', lat:22.5,  lon:114.1,  whFit:2, factoryFit:3, laborIdx:0.86, buildIdx:0.85, note:'Consumer-electronics capital; next to components'},
  {id:'sha', city:'Shanghai',         country:'China',        cc:'cn', region:'asia', lat:31.2,  lon:121.5,  whFit:3, factoryFit:3, laborIdx:0.92, buildIdx:1.00, note:'Dual-use: top port + Yangtze mfg + central demand'},
  {id:'sin', city:'Singapore',        country:'Singapore',    cc:'sg', region:'asia', lat:1.35,  lon:103.8,  whFit:3, factoryFit:1, laborIdx:1.35, buildIdx:1.50, note:'Premier SE-Asia logistics hub; assembly now costly'},
  {id:'sgn', city:'Ho Chi Minh City', country:'Vietnam',      cc:'vn', region:'asia', lat:10.8,  lon:106.7,  whFit:1, factoryFit:3, laborIdx:0.80, buildIdx:0.78, note:'Fast-rising low-cost assembly (China +1)'},
  {id:'bom', city:'Mumbai',           country:'India',        cc:'in', region:'asia', lat:19.1,  lon:72.9,   whFit:2, factoryFit:2, laborIdx:0.82, buildIdx:0.82, note:'India demand gateway + growing manufacturing'},
  // South America
  {id:'sao', city:'São Paulo',        country:'Brazil',       cc:'br', region:'sa',   lat:-23.5, lon:-46.6,  whFit:3, factoryFit:2, laborIdx:1.05, buildIdx:1.10, note:'Dominant regional demand + logistics; can assemble'},
  {id:'bog', city:'Bogotá',           country:'Colombia',     cc:'co', region:'sa',   lat:4.7,   lon:-74.1,  whFit:2, factoryFit:1, laborIdx:0.95, buildIdx:0.95, note:'Andean/northern-SA demand coverage'},
  // Middle East & Africa
  {id:'dxb', city:'Dubai',            country:'UAE',          cc:'ae', region:'mea',  lat:25.2,  lon:55.3,   whFit:3, factoryFit:0, laborIdx:1.20, buildIdx:1.30, note:'Re-export hub; serves Gulf + reaches Africa/S.Asia'},
  {id:'jnb', city:'Johannesburg',     country:'South Africa', cc:'za', region:'mea',  lat:-26.2, lon:28.0,   whFit:2, factoryFit:0, laborIdx:0.98, buildIdx:1.00, note:'Sub-Saharan Africa gateway'},
];
const cityById = id => CITIES.find(c => c.id === id);
const factoryCities = () => CITIES.filter(c => c.factoryFit > 0).slice().sort((a,b)=>b.factoryFit-a.factoryFit); // factory builder list, best fit first
const whCities    = () => CITIES.filter(c => c.whFit   > 0).slice().sort((a,b)=>b.whFit-a.whFit);          // warehouse builder list

/* ---- country flags: render as <img> so they show on every OS.
   (Windows does NOT render flag EMOJI — it falls back to the 2-letter code like "CN".
   Using image assets keyed by ISO-3166 alpha-2 fixes this everywhere.) ---- */
const NAME2CC = (()=>{ const m={}; COUNTRIES.forEach(c=>{ m[c.name]=c.id; }); m['Hungary']='hu'; return m; })();
const ISO2_FIX = { uk:'gb', idn:'id', sau:'sa' };   // our country ids that differ from ISO alpha-2
function iso2(cc){ cc=String(cc||'').toLowerCase(); return ISO2_FIX[cc]||cc; }
function flagImg(cc, name){ const code=iso2(cc); if(!code) return '';
  return '<img src="https://flagcdn.com/'+code+'.svg" alt="'+(name||code)+'" title="'+(name||'')+'" '+
    'style="width:19px;height:14px;vertical-align:-2px;border-radius:2px;object-fit:cover;margin-right:4px;box-shadow:0 0 0 1px rgba(255,255,255,.12)" '+
    'loading="lazy" onerror="this.replaceWith(document.createTextNode(\''+code.toUpperCase()+' \'))">'; }
function flagCC(cc){ return flagImg(cc); }                              // by ISO code
function flagName(name){ return flagImg(NAME2CC[name], name); }         // by country name (suppliers/partners)
function cityFlag(c){ if(!c) return ''; return flagImg(c.cc||NAME2CC[c.country], c.country); } // by city record
function countryFlag(c){ return c? flagImg(c.id, c.name) : ''; }        // by COUNTRIES record
const PARTNER_CC = {China:'cn', Vietnam:'vn', India:'in', Mexico:'mx', Thailand:'th', Poland:'pl', Brazil:'br'};
function partnerFlag(o){ if(!o) return ''; const cc=o.cc||PARTNER_CC[o.country]||NAME2CC[o.country]; return flagImg(cc, o.country); } // manufacturing partner / outsourcer

/* inter-region $/unit (bulk inbound and parcel last-leg share the same distance bands) */
const REGION_FREIGHT = {
  na:   {na:2.0, eu:7.0, asia:7.5, sa:5.5, mea:9.0},
  eu:   {na:7.0, eu:2.0, asia:8.0, sa:7.5, mea:4.5},
  asia: {na:7.5, eu:8.0, asia:2.0, sa:7.0, mea:5.0},
  sa:   {na:5.5, eu:7.5, asia:7.0, sa:2.0, mea:8.5},
  mea:  {na:9.0, eu:4.5, asia:5.0, sa:8.5, mea:2.0},
};
const SAME_CITY_FREIGHT = 0.3;   // factory + warehouse in same city ⇒ near-zero inbound
/* last-leg: warehouse hub → destination region (strong hubs marginally more efficient) */
function lastLeg(hubId, destRegion){
  const h = cityById(hubId); if(!h) return 8;
  return REGION_FREIGHT[h.region][destRegion] * (1 - 0.04 * (h.whFit - 2));
}
/* inbound: assembly factory region → warehouse hub (bulk); co-located ⇒ flat SAME_CITY_FREIGHT */
function inbound(factoryCityId, hubId){
  if(factoryCityId === hubId) return SAME_CITY_FREIGHT;
  const p = cityById(factoryCityId), h = cityById(hubId); if(!p||!h) return 8;
  return REGION_FREIGHT[p.region][h.region];
}
/* total landed freight to a region, assembled at factory city p, shipped via warehouse w */
function landedFreight(factoryCityId, hubId, destRegion){
  return inbound(factoryCityId, hubId) + lastLeg(hubId, destRegion);
}

/* Latent outsourcers that unlock over time (DESIGN-production-expansion.md §10) */
const LATENT_OUTSOURCERS = [
  {id:'thai', name:'Siam Assembly',     country:'Thailand', cc:'th', region:'asia', lat:13.7, lon:100.5, costM:0.90, qualM:0.94, lead:6, rel:.91, capacity:450, unlockRound:5},
  {id:'pol',  name:'Wisła Electronics', country:'Poland',   cc:'pl', region:'eu',   lat:52.2, lon:21.0,  costM:1.10, qualM:1.05, lead:5, rel:.96, capacity:350, unlockRound:7},
  {id:'bra',  name:'Amazonia Montagem', country:'Brazil',   cc:'br', region:'sa',   lat:-3.1, lon:-60.0, costM:0.98, qualM:0.93, lead:8, rel:.89, capacity:300, unlockRound:9},
];
/* region of a seed outsourcer (SUPPLIERS.assembly) by its country, for freight sourcing */
const OUTSOURCER_REGION = {'China':'asia','Vietnam':'asia','India':'asia','Mexico':'na','Thailand':'asia','Poland':'eu','Brazil':'sa'};
const ASM_BASE = 12;   // baseline assembly $/unit (FoxLine costM 1.00 ⇒ 12×1.00, matches legacy "12×assembly.costM")
/* Build a new game's manufacturing-partner network. The instructor sets how many partners are
   available at the start (1–7, default 3); the rest unlock progressively (one every 4 quarters
   from Q5) so new manufacturers keep appearing as the game runs. */
function makeMfgNet(numPartners){
  const n = clamp(numPartners||3, 1, 7);
  const roster = [
    ...SUPPLIERS.assembly.map(a=>({id:a.id, name:a.name, country:a.country, cc:PARTNER_CC[a.country], lat:a.lat, lon:a.lon,
        costM:a.costM, qualM:a.qualM, lead:a.lead, rel:a.rel, capacity:a.capacity})),
    ...LATENT_OUTSOURCERS.map(o=>({...o}))
  ];
  const outsourcers=[], latent=[];
  roster.forEach((o,i)=>{
    if(i<n){ outsourcers.push({id:o.id, name:o.name, country:o.country, cc:o.cc, lat:o.lat, lon:o.lon,
        costM:o.costM, qualM:o.qualM, lead:o.lead, rel:o.rel, capacity:o.capacity, ordersHist:[], relationship:50, lastExpansion:null}); }
    else { const k=i-n; latent.push({...o, region:o.region||OUTSOURCER_REGION[o.country]||'asia', unlockRound: 5 + 4*k}); }
  });
  return {outsourcers, contracts:[], latent};
}

/* segments: attribute weights (sum 1), concern weights (Capitalism 2 style — price/quality/brand
   concern, sum 100), reference price. Market size now comes from the per-country demographics
   (pop × penetration × profile share). Concerns were calibrated from the former elasticities
   (2.3 / 1.4 / 1.1 / 0.9 / 0.7) so each segment keeps its personality. */
const SEGMENTS = [
  {id:'budget',  name:'Value Seekers', icon:'💸', color:'#34d399',
   w:{perf:.18,bat:.20,health:.10,fashion:.16,durab:.16,comfort:.20}, concerns:{pc:60,qc:30,bc:10}, refPrice:110,
   desc:'Students & first-time buyers. Price rules everything.'},
  {id:'fitness', name:'Athletes & Fitness', icon:'🏃', color:'#22d3ee',
   w:{perf:.14,bat:.24,health:.20,fashion:.08,durab:.22,comfort:.12}, concerns:{pc:35,qc:50,bc:15}, refPrice:240,
   desc:'Want GPS, durability, battery life for training.'},
  {id:'health',  name:'Health Guardians', icon:'❤️', color:'#f87171',
   w:{perf:.10,bat:.22,health:.36,fashion:.06,durab:.10,comfort:.16}, concerns:{pc:25,qc:55,bc:20}, refPrice:300,
   desc:'Older users & caregivers. Medical sensors matter most.'},
  {id:'pro',     name:'Professionals', icon:'💼', color:'#818cf8',
   w:{perf:.30,bat:.14,health:.08,fashion:.26,durab:.10,comfort:.12}, concerns:{pc:20,qc:45,bc:35}, refPrice:420,
   desc:'Executives. Performance + prestige, less price-sensitive.'},
  {id:'fashion', name:'Style Icons', icon:'✨', color:'#fbbf24',
   w:{perf:.08,bat:.08,health:.04,fashion:.52,durab:.08,comfort:.20}, concerns:{pc:10,qc:40,bc:50}, refPrice:520,
   desc:'Luxury buyers. Materials, design language, brand.'},
];
/* compact concern label, e.g. "P60·Q30·B10" */
function concernTag(seg){ const c=seg.concerns; return 'P'+c.pc+'·Q'+c.qc+'·B'+c.bc; }

/* Media channels (DESIGN-media-channels): 5 channels spanning the peaky↔broad spectrum.
   covers = the real-world media each channel represents (shown as a tooltip).
   Old save migration: legacy id 'tv' ("TV & Streaming") carries over to Linear TV. */
const AD_CHANNELS = [
  {id:'social', name:'Social & Influencer', icon:'📱',
   covers:'Instagram, TikTok, Facebook, creator sponsorships',
   match:{budget:.90,fitness:.85,health:.40,pro:.50,fashion:.90}},
  {id:'video',  name:'Online Video & Streaming', icon:'▶️',
   covers:'YouTube, Twitch, CTV / streaming-service ads',
   match:{budget:.80,fitness:.80,health:.55,pro:.65,fashion:.70}},
  {id:'tv',     name:'Linear TV', icon:'📺',
   covers:'Broadcast & cable — mass reach, older demographic',
   match:{budget:.50,fitness:.45,health:.90,pro:.60,fashion:.55}},
  {id:'sports', name:'Sports Sponsorship', icon:'🏟️',
   covers:'Events, athletes, team & stadium deals',
   match:{budget:.40,fitness:.98,health:.50,pro:.50,fashion:.35}},
  {id:'print',  name:'Print & OOH', icon:'🗞️',
   covers:'Magazines, airports, billboards, premium print',
   match:{budget:.25,fitness:.30,health:.60,pro:.85,fashion:.90}},
];
const CREATIVES = [
  {id:'value', name:'Value for Money', match:{budget:.95,fitness:.5,health:.5,pro:.25,fashion:.1}},
  {id:'tech',  name:'Technical Superiority', match:{budget:.4,fitness:.7,health:.6,pro:.9,fashion:.35}},
  {id:'life',  name:'Emotional Lifestyle', match:{budget:.6,fitness:.7,health:.7,pro:.6,fashion:.8}},
  {id:'celeb', name:'Celebrity Endorsement', match:{budget:.5,fitness:.75,health:.4,pro:.55,fashion:.95}},
  {id:'health',name:'Health & Longevity', match:{budget:.35,fitness:.7,health:.98,pro:.5,fashion:.25}},
];

/* ============================================================
   R&D TECH TREE (DESIGN-tech-tree) — 30 nodes, 4 branches × 5 tiers.
   Funding: a quarterly R&D budget converts to research points (RP) with
   diminishing returns. One active node at a time, plus a queue; switching
   banks accumulated RP. fx = effect object read by Engine.techFx.
   All tunables live here for instructor-facing balancing.
============================================================ */
const RND_TUNE = {
  rpConstant: 100,    // RP = rpConstant × (budget/$1M)^rpExp
  rpExp: 0.8,
  budgetCap: 4e6,     // $/qtr slider cap
  budgetStep: 0.1e6,
  tierPts: {1:6, 2:9, 3:13, 4:18, 5:24},   // innovation scorecard points per completed node
  rivalRP: {aurion:120, pulsefit:95, nexa:80, strato:90}, // base RP/qtr per rival (× ACFG.mods.tech)
};
function RP_PER_QTR(budget){ return budget>0 ? RND_TUNE.rpConstant*Math.pow(budget/1e6, RND_TUNE.rpExp) : 0; }

const TECH_TREE = [
  // ⚡ SILICON & POWER
  {id:'osopt',     branch:'silicon', tier:1, name:'WristOS Optimization',       rp:90,  icon:'🧠', req:[],                       fx:{perf:8},                          desc:'Firmware tuning lifts performance +8 on every product.'},
  {id:'lowpower',  branch:'silicon', tier:2, name:'Low-Power Silicon',          rp:160, icon:'⚡', req:['osopt'],                fx:{unlock:{cat:'cpu',id:'c4'}},      desc:'Unlocks the Apex X9-LP chip — flagship performance at lower power draw.'},
  {id:'bateff',    branch:'silicon', tier:2, name:'Battery Efficiency Program', rp:150, icon:'🔋', req:['osopt'],                fx:{batLife:1.18},                    desc:'+18% battery life on all products.'},
  {id:'nmprocess', branch:'silicon', tier:3, name:'3 nm Process Node',          rp:240, icon:'🔬', req:['lowpower'],             fx:{unlock:{cat:'cpu',id:'c5'}},      desc:'Unlocks the Apex X10 chip (3 nm).'},
  {id:'solidstate',branch:'silicon', tier:3, name:'Solid-State Battery',        rp:240, icon:'🪫', req:['bateff'],               fx:{unlock:{cat:'battery',id:'b4'}},  desc:'Unlocks the Solid-State 460 mAh battery.'},
  {id:'aicopro',   branch:'silicon', tier:4, name:'On-Device AI Coprocessor',   rp:370, icon:'🤖', req:['lowpower','solidstate'],fx:{perf:12, health:8},              desc:'+12 performance, +8 health (AI insights) on all products.'},
  {id:'graphene',  branch:'silicon', tier:4, name:'Graphene Battery',           rp:360, icon:'🔋', req:['solidstate'],           fx:{unlock:{cat:'battery',id:'b5'}},  desc:'Unlocks the Graphene 520 mAh battery.'},
  {id:'neuracore', branch:'silicon', tier:5, name:'NeuraCore NPU Silicon',      rp:520, icon:'🧬', req:['aicopro','nmprocess'],  fx:{unlock:{cat:'cpu',id:'c6'}},      desc:'Unlocks the NeuraCore N1 chip.', cap:true},
  {id:'harvest',   branch:'silicon', tier:5, name:'Energy Harvesting',          rp:500, icon:'☀️', req:['graphene'],             fx:{batLife:1.25},                    desc:'+25% battery life (kinetic + solar), stacks with Battery Efficiency.', cap:true},
  // 🫀 SENSORS & HEALTH
  {id:'biosense',  branch:'sensors', tier:1, name:'Advanced Optical Sensing',   rp:90,  icon:'🫀', req:[],                       fx:{health:6},                        desc:'+6 health on all products.'},
  {id:'analytics', branch:'sensors', tier:2, name:'Health Analytics Suite',     rp:150, icon:'📈', req:['biosense'],             fx:{health:10},                       desc:'+10 health on all products.'},
  {id:'gpsplus',   branch:'sensors', tier:2, name:'Dual-Band GPS',              rp:140, icon:'🛰️', req:['biosense'],             fx:{fitGps:10},                       desc:'Products carrying GPS gain +10 segment fit for Athletes.'},
  {id:'ecg',       branch:'sensors', tier:3, name:'ECG Medical Certification',  rp:250, icon:'🫀', req:['analytics'],            fx:{unlock:{cat:'sensors',id:'ecg'}}, desc:'Unlocks the medical-grade ECG sensor.'},
  {id:'hrv',       branch:'sensors', tier:3, name:'HRV & Stress Suite',         rp:230, icon:'😌', req:['analytics'],            fx:{health:8},                        desc:'+8 health on all products.'},
  {id:'bp',        branch:'sensors', tier:4, name:'Blood-Pressure Monitoring',  rp:380, icon:'🩸', req:['ecg'],                  fx:{unlock:{cat:'sensors',id:'bp'}},  desc:'Unlocks the Blood Pressure sensor.'},
  {id:'glucose',   branch:'sensors', tier:5, name:'Non-Invasive Glucose Sensing',rp:540,icon:'🩺', req:['bp'],                   fx:{unlock:{cat:'sensors',id:'glu'}}, desc:'Unlocks the Glucose Trend sensor.', cap:true},
  // 💎 MATERIALS & DESIGN
  {id:'water',     branch:'materials',tier:1,name:'Waterproofing 10 ATM',       rp:80,  icon:'🌊', req:[],                       fx:{durab:14},                        desc:'+14 durability on all products.'},
  {id:'sapphire',  branch:'materials',tier:2,name:'Sapphire Crystal Glass',     rp:160, icon:'💠', req:['water'],                fx:{fashion:8, durab:8},              desc:'+8 fashion, +8 durability on all products.'},
  {id:'lightweight',branch:'materials',tier:2,name:'Lightweight Alloys',        rp:150, icon:'🪶', req:['water'],                fx:{weightM:0.85},                    desc:'−15% casing+battery weight (comfort ↑).'},
  {id:'ceramic',   branch:'materials',tier:3,name:'Ceramic Composite Case',     rp:240, icon:'⬜', req:['sapphire'],             fx:{unlock:{cat:'casing',id:'k5'}},   desc:'Unlocks the Ceramic Composite case.'},
  {id:'sustain',   branch:'materials',tier:3,name:'Sustainable Materials Program',rp:220,icon:'♻️', req:['lightweight'],          fx:{fitAll:4},                        desc:'+4 segment fit across all segments (eco appeal).'},
  {id:'microled',  branch:'materials',tier:4,name:'MicroLED Display',           rp:360, icon:'🔆', req:['ceramic'],              fx:{unlock:{cat:'display',id:'d4'}},  desc:'Unlocks the MicroLED 1.9″ display.'},
  {id:'flexglass', branch:'materials',tier:5,name:'Flexible Display Glass',     rp:500, icon:'🌀', req:['microled'],             fx:{unlock:{cat:'display',id:'d5'}},  desc:'Unlocks the Wrap AMOLED 2.1″ display.', cap:true},
  // 🏭 MANUFACTURING
  {id:'qc',        branch:'manufacturing',tier:1,name:'Six Sigma Quality Program',rp:90,icon:'🎯', req:[],                       fx:{qualM:1.04},                      desc:'Quality ×1.04 on all products.'},
  {id:'process',   branch:'manufacturing',tier:2,name:'Lean Manufacturing',     rp:160, icon:'🏭', req:['qc'],                   fx:{costM:0.92},                      desc:'−8% unit production cost.'},
  {id:'supplyint', branch:'manufacturing',tier:2,name:'Supplier Co-Development', rp:150, icon:'🤝', req:['qc'],                   fx:{relia:10},                        desc:'All supplier reliability +10 pts (fewer shortfalls).'},
  {id:'automation',branch:'manufacturing',tier:3,name:'Assembly Automation',    rp:240, icon:'🦾', req:['process'],              fx:{costM:0.93, scaleM:0.75},         desc:'Additional −7% unit cost; scale discounts reached at 25% lower volume.'},
  {id:'digitwin',  branch:'manufacturing',tier:3,name:'Digital Twin Factory',   rp:230, icon:'🖥️', req:['supplyint'],            fx:{qualM:1.05, shortfallM:0.5},      desc:'Quality ×1.05; shipment shortfalls halved.'},
  {id:'flexfab',   branch:'manufacturing',tier:4,name:'Flexible Manufacturing', rp:350, icon:'⚙️', req:['automation'],           fx:{engCostM:0.6, genCap:4},          desc:'New-generation engineering cost −40%; generation cap 3 → 4.'},
  {id:'gigafab',   branch:'manufacturing',tier:5,name:'Gigafactory Scale',      rp:480, icon:'🏗️', req:['flexfab'],              fx:{costM:0.90},                      desc:'Additional −10% unit cost.', cap:true},
  // 🏭 OWNED-FACTORY lane (DESIGN-production-expansion §9) — inert until you operate a factory
  {id:'ownproc',   branch:'manufacturing',tier:3,name:'In-house Process Engineering',rp:210,icon:'🛠️',req:['process'],          fx:{ownCostM:0.90},                   desc:'Own-factory assembly cost ×0.90 — no effect until you operate a factory.'},
  {id:'factoryauto', branch:'manufacturing',tier:4,name:'Owned-Factory Robotics',    rp:340, icon:'🤖', req:['ownproc','automation'],  fx:{ownCostM:0.90, factoryRampFast:1}, desc:'Own-factory cost ×0.90 and factories mature 1 qtr faster — own factories only.'},
  {id:'qualitysys',branch:'manufacturing',tier:4,name:'Factory Quality System',    rp:330, icon:'🏅', req:['digitwin'],            fx:{factoryRel:0.05},                   desc:'Own-factory reliability +5 pts — own factories only.'},
];
const TECH_BY_ID = Object.fromEntries(TECH_TREE.map(n=>[n.id, n]));
const TECH_BRANCHES = [
  {id:'silicon',       name:'Silicon & Power',   icon:'⚡'},
  {id:'sensors',       name:'Sensors & Health',  icon:'🫀'},
  {id:'materials',     name:'Materials & Design',icon:'💎'},
  {id:'manufacturing', name:'Manufacturing',     icon:'🏭'},
];
/* memoized effect accessor: a completed-tech set → aggregate effect object */
const _txfCache = new Map();
function computeTechFx(done){
  done = done||[];
  const key = done.slice().sort().join(',');
  let fx = _txfCache.get(key); if(fx) return fx;
  fx = {perf:0, health:0, fashion:0, durab:0, batLife:1, weightM:1, costM:1, qualM:1,
        relia:0, shortfallM:1, fitAll:0, fitGps:0, scaleM:1, engCostM:1, genCap:3,
        ownCostM:1, factoryRel:0, factoryRampFast:0, unlocks:new Set()};
  for(const id of done){ const n=TECH_BY_ID[id]; if(!n||!n.fx) continue; const f=n.fx;
    if(f.perf)     fx.perf    += f.perf;
    if(f.health)   fx.health  += f.health;
    if(f.fashion)  fx.fashion += f.fashion;
    if(f.durab)    fx.durab   += f.durab;
    if(f.batLife)  fx.batLife *= f.batLife;
    if(f.weightM)  fx.weightM *= f.weightM;
    if(f.costM)    fx.costM   *= f.costM;
    if(f.qualM)    fx.qualM   *= f.qualM;
    if(f.relia)    fx.relia   += f.relia;
    if(f.shortfallM) fx.shortfallM *= f.shortfallM;
    if(f.fitAll)   fx.fitAll  += f.fitAll;
    if(f.fitGps)   fx.fitGps  += f.fitGps;
    if(f.scaleM)   fx.scaleM  *= f.scaleM;
    if(f.engCostM) fx.engCostM*= f.engCostM;
    if(f.genCap)   fx.genCap   = Math.max(fx.genCap, f.genCap);
    if(f.ownCostM) fx.ownCostM *= f.ownCostM;
    if(f.factoryRel) fx.factoryRel += f.factoryRel;
    if(f.factoryRampFast) fx.factoryRampFast = 1;
    if(f.unlock)   fx.unlocks.add(f.unlock.id);
  }
  _txfCache.set(key, fx); return fx;
}
function nodeEffectText(n){ return n.desc||''; }

const STRATEGIES = [
  {id:'cost',    name:'Cost Leadership', icon:'💸', desc:'Win Value Seekers at scale with aggressive pricing & efficient supply chain.', segs:['budget']},
  {id:'premium', name:'Premium Brand', icon:'👑', desc:'High-end materials, strong brand, Professionals & Style Icons.', segs:['pro','fashion']},
  {id:'fitness', name:'Fitness Niche', icon:'🏃', desc:'Own the athlete segment with GPS, durability and sports marketing.', segs:['fitness']},
  {id:'health',  name:'Health Tech Leader', icon:'❤️', desc:'Medical sensors and trust for the health-focused buyer.', segs:['health']},
  {id:'broad',   name:'Broad Differentiator', icon:'🎯', desc:'Multi-product portfolio covering several segments.', segs:['budget','fitness','pro']},
];

/* Brand strategies (Capitalism 2 manual ch.7) — how brand equity is built & shared */
const BRAND_STRATS = [
  {id:'range',  name:'Range Brand', icon:'🏷️',
   desc:'All your products share one brand. Every ad campaign builds the shared brand rating, which applies to all products — and new products launch with your established brand. The trade-off: quality inconsistency in any product damages the brand they all depend on.'},
  {id:'unique', name:'Unique Brand', icon:'🎫',
   desc:'Each product carries its own brand. Ad campaigns build only the advertised product\'s brand rating, and every new product starts from scratch — needing its own campaigns. The payoff: each brand can be tailored to its segment, and one product\'s failure never stains the others.'},
];

/* Competitors now carry a per-country distribution footprint:
   presence  = launch-day map {countryId:{m:method, s:strength 0–1}}
   expand    = scripted expansion priorities, ~1–2 countries/qtr, with expandMethod
   everywhere= (Nexa) implicit cheap distributor+online presence in all countries by profile
   covProfile= how well their channel mix reaches each segment (× strength = est. coverage) */
const COMPETITORS = [
  {id:'aurion', name:'Aurion', model:'Vela', tag:'Premium giant', color:'#a78bfa', brand:74,
   fin:{targetDE:1.2, payer:true},  // premium giant carries more debt to fund expansion, pays a steady dividend (§12)
   design:{cpu:'c3',display:'d3',battery:'b2',casing:'k4',strap:'s4',size:'44',style:'luxury',colorHex:0xd4af6a,sensors:['hr','spo2','ecg','slp','gps']},
   price:449, focus:['pro','fashion','health'], aw:.75, drift:{price:-3, qual:1.2},
   covProfile:{budget:.15, fitness:.45, health:.7, pro:.95, fashion:.95},
   presence:{us:{m:'stores',s:.9}, jp:{m:'stores',s:.8}, de:{m:'stores',s:.8}, uk:{m:'stores',s:.8}, fr:{m:'stores',s:.75},
             ch:{m:'stores',s:.85}, ae:{m:'stores',s:.8}, sg:{m:'stores',s:.8}, hk:{m:'stores',s:.8}, au:{m:'stores',s:.7},
             ca:{m:'stores',s:.7}, it:{m:'stores',s:.65}, nl:{m:'stores',s:.7}, se:{m:'stores',s:.7}, kr:{m:'stores',s:.65}},
   expand:['dk','no','at','ie','be','tw','es','il'], expandMethod:'stores'},
  {id:'pulsefit', name:'PulseFit', model:'Stride', tag:'Fitness specialist', color:'#22d3ee', brand:58,
   fin:{targetDE:0.8, payer:true},
   design:{cpu:'c2',display:'d2',battery:'b3',casing:'k2',strap:'s1',size:'47',style:'sporty',colorHex:0x1c1f26,sensors:['hr','gps','spo2','slp']},
   price:229, focus:['fitness','health'], aw:.65, drift:{price:-2, qual:1.0},
   covProfile:{budget:.55, fitness:.95, health:.6, pro:.4, fashion:.15},
   presence:{us:{m:'dist',s:.85}, ca:{m:'dist',s:.7}, uk:{m:'dist',s:.75}, de:{m:'dist',s:.75}, au:{m:'carrier',s:.8},
             jp:{m:'dist',s:.6}, kr:{m:'carrier',s:.7}, cn:{m:'dist',s:.5}, fr:{m:'dist',s:.7}, es:{m:'dist',s:.65},
             it:{m:'dist',s:.6}, nl:{m:'dist',s:.7}, se:{m:'dist',s:.75}, no:{m:'dist',s:.75}, dk:{m:'dist',s:.7},
             br:{m:'carrier',s:.55}, mx:{m:'carrier',s:.5}, pl:{m:'dist',s:.6}, cz:{m:'dist',s:.6}, ch:{m:'dist',s:.6},
             at:{m:'dist',s:.6}, be:{m:'dist',s:.6}, ie:{m:'dist',s:.6}, tw:{m:'dist',s:.55}, th:{m:'dist',s:.5}},
   expand:['my','cl','sau','tr','sg','hk','il','co'], expandMethod:'dist'},
  {id:'nexa', name:'Nexa', model:'Go', tag:'Budget volume player', color:'#34d399', brand:44,
   fin:{targetDE:0.5, payer:false},  // runs lean — low leverage, reinvests rather than paying out
   design:{cpu:'c1',display:'d1',battery:'b2',casing:'k1',strap:'s1',size:'40',style:'modern',colorHex:0x2563eb,sensors:['hr','slp']},
   price:79, focus:['budget'], aw:.7, drift:{price:-1, qual:0.9},
   covProfile:{budget:.95, fitness:.6, health:.4, pro:.25, fashion:.15},
   presence:{}, everywhere:{value:.7, empremium:.65, balanced:.5, premium:.35}},
  /* 4th rival — only in play when the instructor raises the competitor count to 4 */
  {id:'strato', name:'Strato', model:'Pulse', tag:'Carrier-channel challenger', color:'#fb923c', brand:50,
   fin:{targetDE:0.9, payer:false},
   design:{cpu:'c2',display:'d2',battery:'b2',casing:'k2',strap:'s2',size:'44',style:'modern',colorHex:0x37415e,sensors:['hr','spo2','slp']},
   price:159, focus:['budget','fitness'], aw:.6, drift:{price:-2, qual:1.05},
   covProfile:{budget:.85, fitness:.7, health:.4, pro:.3, fashion:.15},
   presence:{cn:{m:'carrier',s:.7}, in:{m:'carrier',s:.65}, idn:{m:'carrier',s:.6}, br:{m:'carrier',s:.6}, mx:{m:'carrier',s:.6},
             th:{m:'carrier',s:.55}, vn:{m:'carrier',s:.55}, ph:{m:'carrier',s:.55}, tr:{m:'carrier',s:.55}, pl:{m:'dist',s:.55},
             za:{m:'carrier',s:.5}, sau:{m:'carrier',s:.5}, co:{m:'carrier',s:.5}, ar:{m:'carrier',s:.5}, my:{m:'dist',s:.55}},
   expand:['us','es','it','kr','cl','cz','ae','tw'], expandMethod:'carrier'},
];

const EVENTS = [
  {id:'chipShort', icon:'📉', label:'Global chip shortage', text:'A foundry fire tightens CPU supply. Chip costs +25% this quarter; low-reliability suppliers hit hardest.', p:.10, mod:g=>({chipCost:1.25})},
  {id:'shipping', icon:'🚢', label:'Freight crunch', text:'Container rates spike. Logistics costs +40% this quarter.', p:.08, mod:g=>({freight:1.4})},
  {id:'fitTrend', icon:'🔥', label:'Viral fitness trend', text:'A social fitness challenge goes global. Athlete segment demand +35% this quarter.', p:.10, mod:g=>({segBoost:{fitness:1.35}})},
  {id:'healthReg', icon:'🏥', label:'Insurers subsidize wearables', text:'Health insurers begin subsidizing health wearables. Health Guardians demand +30%.', p:.08, mod:g=>({segBoost:{health:1.3}})},
  {id:'downturn', icon:'🌧️', label:'Consumer slowdown', text:'Consumer confidence dips. All demand -12%; price sensitivity up.', p:.08, mod:g=>({demand:.88, elast:1.15})},
  {id:'rivalRecall',icon:'⚠️', label:'Competitor recall', text:'A rival recalls devices over battery issues — their reputation suffers this quarter.', p:.07, mod:g=>({rivalHit:true})},
];

const LESSONS = [
  {id:'elast', icon:'📉', title:'Price Sensitivity & Concern Weights', tag:'Economics',
   body:`<p><b>Elasticity</b> measures how strongly demand responds to price in the real world. If a 10% price cut raises unit sales 23%, elasticity ≈ 2.3 — that's our <i>Value Seekers</i>. Style Icons behave like elasticity ≈ 0.7: cutting price barely adds buyers, it just burns margin (and can even cheapen the brand).</p>
   <p><b>In the sim</b> (modeled on Capitalism II): each segment splits 100 <b>concern points</b> between Price, Quality and Brand — Value Seekers put 60 on price, Style Icons only 10. Every product earns an <b>Overall Rating = Quality × Quality Concern⁄60 + Brand Rating × Brand Concern⁄60 + (ref − price)⁄ref × Price Concern</b>, and ratings compete for buyers. Pricing below the segment's reference price adds up to Price Concern points; pricing above subtracts them. High price concern is the rating-model cousin of high elasticity. Because points <i>add</i>, a price cut can compensate for a weak brand — and a strong brand can carry a premium price.</p>
   <p><b>Theory link:</b> Marshall's demand theory (revenue is maximized where elasticity = 1); multi-attribute utility models of consumer choice.</p>`},
  {id:'scale', icon:'🏗️', title:'Economies of Scale', tag:'Operations',
   body:`<p>Unit costs fall as volume rises: fixed costs spread over more units, suppliers grant <b>bulk discounts</b>, and learning effects kick in. In the sim, order volumes above 50k/quarter earn progressive component discounts up to ~18%.</p>
   <p><b>The catch:</b> big orders tie up cash and create <b>inventory risk</b> — if demand disappoints, you hold obsolete stock. This is the classic scale-vs-flexibility trade-off.</p>`},
  {id:'seg', icon:'🎯', title:'Market Segmentation & Targeting', tag:'Marketing',
   body:`<p>Markets aren't uniform. The sim's five segments weight product attributes differently — Athletes value battery & durability; Style Icons care about materials & design above all. The <b>STP framework</b> (Segment → Target → Position) says: pick segments where your strengths match what buyers weight heavily, then position your message there.</p>
   <p>A product "for everyone" usually scores mediocre fit with everyone. Check Market Intel to see price-adjusted demand scores per segment.</p>`},
  {id:'brand', icon:'⭐', title:'Brand Equity', tag:'Marketing',
   body:`<p>Brand has two halves (as in Capitalism II): <b>awareness</b> — do buyers know you exist? — and <b>loyalty</b> — do they come back? In the sim, <b>Brand Rating = 0.6 × loyalty + 40 × awareness</b>, and it earns Brand-Concern-weighted points in every product's Overall Rating. Ad campaigns buy awareness, which decays 20% per quarter; loyalty is earned only through sales, quality and satisfaction — money can buy attention, not love. Loyalty also unlocks distribution privileges (flagship stores need loyalty ≥ 45). Sales through distributors build loyalty at only half rate — it's their banner over the door, not yours.</p>
   <p><b>Brand strategy</b> (Capitalism II ch.7): under a <b>Range Brand</b> all your products share one brand rating — every campaign builds it, and it applies to every product, so new launches inherit your reputation but one bad product stains them all. Under a <b>Unique Brand</b> each product carries its own rating — campaigns must be run per product and new launches start from scratch, but each brand fits its segment and failures stay contained. Switching strategy mid-game resets all brand ratings to zero.</p>
   <p><b>Theory link:</b> Keller's Brand Equity pyramid; Aaker's brand-as-asset model. Note how Aurion charges $449 for components costing far less.</p>`},
  {id:'supply', icon:'🚢', title:'Lead Time & Supply Risk', tag:'Operations',
   body:`<p>Cheap suppliers often mean longer or less reliable delivery. A supplier with 88% reliability will short your shipment roughly one quarter in eight — causing <b>stockouts</b> that lose sales AND damage customer satisfaction.</p>
   <p>Resilience strategies: dual-sourcing, paying premiums for reliability, or holding safety stock (which costs working capital). After a chip-shortage event, compare suppliers' reliability scores.</p>`},
  {id:'tradeoff', icon:'⚖️', title:'Product Design Trade-offs', tag:'Product',
   body:`<p>Every component is a trade-off triangle of <b>cost, performance and weight/power</b>. A 420 mAh battery extends battery life but adds 28g — hurting comfort, which Value Seekers and Style Icons both weight heavily. Titanium looks premium and is light, but costs 7× polycarbonate.</p>
   <p>Great products aren't maximal — they're <b>coherent</b>: every choice serves the target segment.</p>`},
  {id:'fourp', icon:'🧩', title:'The Marketing Mix (4 Ps)', tag:'Marketing',
   body:`<p>Product, Price, Place, Promotion must reinforce each other. A luxury watch (Product) at $520 (Price) pushed through budget-focused distributors and carrier bundles (Place) with value-for-money ads (Promotion) sends contradictory signals and underperforms everywhere.</p>
   <p>In the sim, <b>Place is now a per-country choice</b>: each distribution method reaches segments differently — distributors reach Value Seekers well but Style Icons poorly; own stores excel with Professionals and Style Icons; carriers only reach budget & fitness buyers. Campaigns whose creative matches the target segment, distribution that reaches it, and prices near its reference all compound. Misalignment dilutes each dollar spent.</p>`},
  {id:'channel', icon:'🔗', title:'Channel Power: Control vs. Reach vs. Capital', tag:'Strategy',
   body:`<p>Every route to market trades off three things. <b>Reach</b> — how many buyers can physically get your product. <b>Control</b> — who decides how it's promoted and presented. <b>Capital</b> — what it costs to build.</p>
   <p><b>The distributor margin math:</b> a $240 watch through a distributor nets you $240 × (1−28%) ≈ $173; through your own store $240 × (1−12%) ≈ $211 — but the store network behind that sale costs ~$350k per store plus $70k/quarter to run, while the distributor costs $300k once. Full US coverage ≈ 120 stores ≈ $42M of capex — more than double your seed capital. That's why almost every real company sequences: <b>distributor first for reach, own retail in priority markets as cash allows</b>.</p>
   <p><b>The hidden price of the distributor:</b> they control promotion. In distributor-only countries your ad campaigns don't apply — their generic push counts as a flat 30% awareness in your Brand Rating — and sales there build loyalty at half rate. Cheap reach today can starve the brand you need tomorrow.</p>
   <p><b>Theory link:</b> vertical integration (forward integration into retail), transaction-cost economics, and channel conflict in distribution strategy. Compare Aurion's owned flagships with Nexa's distributor-everywhere model — opposite corners of the same triangle.</p>`},
  {id:'lifecycle', icon:'🔄', title:'Product Life Cycle & Generational Cadence', tag:'Product',
   body:`<p>Products move through <b>introduction → growth → maturity → decline</b>. Hardware makers manage this with <b>generations</b>: once a design ships, it's frozen — changing the CPU of watches already in stores is impossible. Improvements ship as a <i>new generation</i>, engineered at real cost and lead time.</p>
   <p><b>In the sim:</b> a <i>model</i> is your product line; a <i>generation</i> is a frozen version of its hardware. New generations cost $0.5M + $0.25M per changed component and launch the following quarter. Rivals refresh their hardware every 3 quarters and their quality improves each time — a design you never update falls steadily behind in <b>Quality points</b>.</p>
   <p><b>Price laddering:</b> keep last year's generation on sale at a lower price (Apple-style) to capture price-sensitive buyers — but watch <b>cannibalization</b>: your own generations compete for the same customers, and split volumes can forfeit scale discounts.</p>
   <p><b>The Osborne effect:</b> in 1983, Osborne Computer announced its next model long before it shipped — customers stopped buying the current one and the company collapsed. Time announcements and wind-downs carefully: sell through old inventory before the shine of the new generation kills its demand.</p>`},
  {id:'invent', icon:'📦', title:'Inventory & the Newsvendor Problem', tag:'Operations',
   body:`<p>Each quarter you choose production before knowing demand — the classic <b>newsvendor problem</b>. Overproduce and you pay holding costs (2.5%/qtr of inventory value) and risk obsolescence; underproduce and you lose sales and goodwill.</p>
   <p>Optimal stock balances the cost of overage vs. underage. Watch your stockout % and inventory turns in Reports.</p>`},
  {id:'pathdep', icon:'🧪', title:'Path Dependence & Technology Strategy', tag:'Strategy',
   body:`<p>The <b>Research tree</b> is a series of <b>real options</b>. Spending on an early node (say Low-Power Silicon) isn't just its own payoff — it <i>buys the right</i> to pursue the deeper nodes behind it (3 nm, then NeuraCore silicon). Because each tier costs more but pays more, <b>going deep on one branch beats spreading thin</b>: a full Tier-1→Tier-4 path is worth far more than four scattered Tier-1 techs, and the innovation scorecard now rewards depth (T1=6 pts … T5=24) rather than counting cheap wins.</p>
   <p><b>Diminishing returns on budget</b> (RP = 100 × (budget⁄$1M)<sup>0.8</sup>) mean two steady $1M quarters out-research one $2M crash quarter — a lab is a marathon. And once you commit to a branch, switching is costly in time, so your early budget choices <b>lock in</b> a trajectory: this is <i>path dependence</i>. Tier-5 capstones (glucose sensing, graphene-plus-harvesting power, gigafactory scale) are structurally out of reach in a standard 8-quarter game — they exist to reward the long campaign.</p>
   <p><b>Theory link:</b> real-options valuation (Dixit &amp; Pindyck); David's QWERTY path-dependence; the resource-based view of sustained advantage through hard-to-imitate capability stacks.</p>`},
  {id:'capstruct', icon:'⚖️', title:'Capital Structure & Leverage', tag:'Finance',
   body:`<p>The right-hand side of the balance sheet — how you <b>fund</b> the company — is a choice with real trade-offs. <b>Debt</b> is cheaper than equity: interest is tax-deductible and lenders don't take a share of your company. But debt adds <b>fixed claims</b> (you owe interest and principal whether or not you sell a single watch) and raises <b>bankruptcy risk</b>. <b>Equity</b> never has to be repaid and can't push you into insolvency, but it <b>dilutes</b> the owners.</p>
   <p><b>In the sim:</b> leverage is measured as <b>Debt ÷ Equity (D/E)</b>. The finance scorecard rewards a <i>healthy</i> structure — roughly 0.3–1.0× D/E with comfortable interest coverage — over both reckless over-borrowing and timid all-cash hoarding. "Took on sensible debt to fund growth" now scores better than "never financed anything."</p>
   <p><b>Theory link:</b> Modigliani–Miller (and why its frictionless irrelevance result breaks once you add taxes and bankruptcy costs); the trade-off theory of capital structure.</p>`},
  {id:'costdebt', icon:'🏦', title:'Cost of Debt & Credit Ratings', tag:'Finance',
   body:`<p>Lenders price risk. A firm that is heavily levered or barely covers its interest looks risky, so it pays a <b>higher rate</b>. Rating agencies compress this into a letter grade — <b>AAA</b> (safest) down through <b>BBB</b> (lowest investment grade) to <b>CCC</b> (deep junk).</p>
   <p><b>In the sim:</b> your rating is a function of <b>leverage</b> and <b>interest coverage</b> (EBIT ÷ interest). Each new loan's rate = a risk-free base + a rating spread + a tenor premium, and is <b>fixed at origination</b> — borrow cheaply while you're healthy and that rate is locked even if you lever up later. Banks also cap total borrowing at a <b>leverage covenant</b> (≈2.0× D/E): past that, raise equity or grow earnings first.</p>
   <p><b>Bonds vs. bank loans:</b> bonds are larger, longer and cheaper for well-rated firms, but carry an underwriting fee and a single balloon repayment — and the market may be <b>closed entirely to junk-rated issuers</b>, the classic investment-grade/high-yield divide.</p>
   <p><b>Theory link:</b> the credit spread as compensation for default risk; covenant-based lending.</p>
   <p><b>Running on the overdraft.</b> When cash falls below the $2M floor the bank covers the gap automatically — at a penalty rate, with a hit to your finance score that <i>grows</i> with the size of the overdraft relative to your equity, and a knock to the share price. If cumulative losses ever exceed the capital invested you are on <b>insolvency watch</b>: no new debt, no dividends, no buybacks until book equity is positive again. Real companies get a liquidity crisis; you get a warning and a way back.</p>`},
  {id:'dilution', icon:'🍕', title:'Equity Raises & Dilution', tag:'Finance',
   body:`<p>Selling new shares brings in cash that never has to be repaid and doesn't burden the balance sheet — but every new share shrinks the slice owned by existing holders. Your share <i>count</i> doesn't change; the <b>denominator</b> grows, so your <b>ownership %</b> falls. That's dilution.</p>
   <p><b>In the sim:</b> shares are sold at the live market price with a small placement discount. A raise lifts book equity (and often the stock, via lower leverage and higher BVPS), but spreads the same earnings over more shares — <b>EPS dilution</b> — so it is not a free lunch. The cost founders fear most is <b>losing control</b> (see the Corporate Control lesson). A <b>buyback</b> is the inverse: spend cash to retire shares, lifting both EPS and your ownership %.</p>
   <p><b>Theory link:</b> the pecking-order theory (why managers prefer internal funds, then debt, then equity last); signalling effects of seasoned equity offerings.</p>`},
  {id:'divpolicy', icon:'💸', title:'Dividend Policy', tag:'Finance',
   body:`<p>A <b>dividend</b> returns cash to shareholders. It's a <b>policy</b>, not a one-off: boards set a target <b>payout ratio</b> (share of net income paid out) and try to hold it steady. Paying out rewards owners today; retaining earnings funds tomorrow's growth.</p>
   <p><b>In the sim:</b> dividends are paid out of <b>retained earnings</b>, so book equity falls by the payout and a firm that distributes everything stops compounding its equity. A <b>sustainable</b> payout (covered by earnings, no emergency borrowing) earns a modest stock-price reward — income investors pay up for yield. An <b>unsustainable</b> one (paying while drawing the emergency overdraft, or above earnings) <b>reverses</b> that reward: the market reads it as a warning, not a gift. And every dollar paid out can't fund production, marketing, R&D or stores.</p>
   <p><b>Theory link:</b> Miller–Modigliani dividend irrelevance vs. the signalling and clientele effects that make payout matter in practice; dividend smoothing (Lintner).</p>`},
  {id:'control', icon:'👑', title:'Corporate Control', tag:'Finance',
   body:`<p>Ownership is power. Cross <b>50%</b> and you command the company outright; below it your grip weakens. <b>33–50%</b> is working control — practical, but vulnerable. <b>25–33%</b> leaves you only a blocking minority and invites activist pressure. Below <b>25%</b> you're effectively just another minority holder.</p>
   <p><b>In the sim:</b> your stake only moves two ways — <b>equity raises</b> push it down, <b>buybacks</b> push it up. Falling below majority does <b>not</b> end the game; instead a <b>graduated penalty</b> applies to your composite score (−4% in working control, −10% contested, −18% lost), so the lesson lands without a frustrating game-over. The escape routes are the same ones real founders use: buy back shares, or grow earnings so you can fund with debt instead of selling equity.</p>
   <p><b>Theory link:</b> the market for corporate control; founder dilution across financing rounds; dual-class structures as a control-preserving response.</p>`},
  {id:'mfgnet', icon:'🏭', title:'Build vs. Buy: Fixed vs. Marginal Cost', tag:'Operations',
   body:`<p>Your <b>manufacturing network</b> is a build-vs-buy decision. Outsourcing to a partner like FoxLine is <b>pure variable cost</b>: you pay a unit price and nothing when you don't order. Owning a factory swaps that for a <b>fixed cost</b> — a large up-front build plus a recurring quarterly charge you pay even at zero output — in exchange for a <b>lower marginal cost</b> that falls with the factory's built scale (a 500k mega-factory beats FoxLine per unit; a 50k boutique factory is pricier).</p>
   <p><b>The lesson that must bite:</b> owned capacity only pays off at <b>high utilization</b>. Spread the fixed cost over few units and your all-in cost balloons — below roughly 55% utilization, FoxLine is cheaper. This is why the auto-allocator counts a factory's fixed cost as <b>sunk this quarter</b> and prefers to <i>use</i> capacity you're already paying for before renting outside: a deliberate gap between <b>marginal</b> and <b>average</b> cost.</p>
   <p><b>Supplier leverage:</b> partners expand for customers who order heavily and predictably, and a <b>long-term volume commitment</b> earns a discount — paid for with a <b>take-or-pay</b> clause. Guarantees buy lower cost at the price of flexibility. Diversifying across manufacturers also reduces shortfall risk: an unreliable partner can only short the slice routed to it.</p>
   <p><b>Theory link:</b> vertical integration; operating leverage; the newsvendor's cousin — capacity planning under demand uncertainty.</p>`},
  {id:'landed', icon:'📦', title:'Landed Cost & Network Design', tag:'Operations',
   body:`<p><b>Landed cost</b> is what a unit truly costs delivered to the customer — here, two freight legs: <b>inbound</b> (factory → warehouse, cheap bulk) and <b>last-leg</b> (warehouse → customer, pricier parcel). A well-placed warehouse near demand shortens the expensive last leg; co-locating a factory and warehouse in the same city collapses inbound to ≈ 0.</p>
   <p><b>The decision-driving number</b> is <b>freight as a % of price</b>. At 4% it barely matters; at 22% it either guts your margin (if you absorb it) or visibly repels price-sensitive buyers (if you pass it through). The fix isn't picking a payer — it's fixing the <b>geography</b>: build a closer warehouse or localize assembly.</p>
   <p><b>Who pays:</b> <i>Absorb</i> keeps prices clean but drops your margin; <i>pass-through</i> protects margin but raises the effective price, so budget and fitness buyers (high price concern) punish it hardest. Capacity matters too — overflow past a warehouse's throughput spills to full-rate direct shipping.</p>
   <p><b>Theory link:</b> landed-cost analysis; hub-and-spoke network design; the operations lesson — concentrate inventory where demand concentrates, then expand.</p>
   <p><b>Why build a warehouse if you can ship direct?</b> You can — but a parcel crossing an ocean costs more and takes longer, and about a third of online buyers won't wait. A warehouse in the region cuts last-leg freight and captures those orders. Compare the freight-as-%-of-price table with the "extra online sales" estimate on the Logistics screen before you build.</p>`},
  {id:'compete', icon:'⚔️', title:'Competitive Dynamics', tag:'Strategy',
   body:`<p>When your rival is <b>three rows over in the lecture hall</b> rather than a script, strategy becomes interactive: your best move depends on theirs, and theirs on yours. This is the heart of <b>oligopoly</b> — a few firms, each large enough that its choices move the market.</p>
   <p><b>Price wars &amp; tit-for-tat.</b> Cut price to grab share and a rational rival matches you; both end up with the old shares at thinner margins — a <b>prisoner's dilemma</b>. The escape is <b>tit-for-tat</b>: signal restraint, match aggression once, and return to cooperation. Repeated play (round after round, same opponents) is exactly why real oligopolies usually <b>avoid mutual destruction</b> — everyone remembers last quarter.</p>
   <p><b>Differentiation vs head-on entry.</b> Attacking a strong incumbent where it is strongest (same segment, same price) invites retaliation and a margin bloodbath. <b>Differentiate</b> — a different segment, a quality tier they don't serve, a country they've ignored — and you compete on your terms, not theirs. White space beats a frontal assault.</p>
   <p><b>Signaling.</b> A price ladder, a flagship store, a public R&amp;D roadmap all <b>signal</b> intent. Credible signals shape rivals' expectations and can deter entry without a single shot fired — but a bluff that's called is expensive.</p>
   <p><b>The lesson that must bite:</b> in a shared market your scorecard is <b>relative</b>. A great quarter that provokes a price war you lose is worse than a steady one. Read the wire, anticipate the reaction, and choose your battles.</p>
   <p><b>Theory link:</b> Nash equilibrium &amp; the prisoner's dilemma; Bertrand vs Cournot competition; Axelrod's tit-for-tat; Porter's five forces (rivalry &amp; entry); credible commitment and market signaling.</p>`},
];

const QUIZ = [
  {q:'Demand for a product falls 20% when price rises 10%. Price elasticity is approximately…', opts:['0.5','1.0','2.0','20'], a:2,
   why:'Elasticity = %ΔQ / %ΔP = 20/10 = 2.0 — elastic demand.'},
  {q:'Which segment in this simulation is LEAST price-sensitive?', opts:['Value Seekers','Athletes','Style Icons','Health Guardians'], a:2,
   why:'Style Icons put only 10 of their 100 concern points on price — they buy on design and brand.'},
  {q:'Economies of scale primarily lower…', opts:['Total fixed costs','Average cost per unit','Marketing costs','Price elasticity'], a:1,
   why:'Scale spreads fixed costs and earns volume discounts, lowering average unit cost.'},
  {q:'A supplier with 88% reliability and 4-week lead time vs one with 98% and 11 weeks. Choosing the first increases…', opts:['Unit quality','Stockout risk from shipment shortfalls','Brand equity','Fixed costs'], a:1,
   why:'Lower reliability means more frequent shipment shortfalls → stockouts.'},
  {q:'Selling luxury watches through fitness specialty stores most likely causes…', opts:['Higher margins','Marketing-mix inconsistency','Stronger network effects','Lower lead time'], a:1,
   why:'Place must match Product/Price/Promotion — misaligned channels dilute positioning (4 Ps).'},
  {q:'Penetration pricing means launching…', opts:['High to skim early adopters','Below reference price to win share fast','At exactly unit cost','Only in premium channels'], a:1,
   why:'Penetration pricing sacrifices early margin to build share and scale economies.'},
  {q:'Producing more than demand mainly creates…', opts:['Stockouts','Holding costs & obsolescence risk','Higher brand equity','Shorter lead times'], a:1,
   why:'Excess inventory ties up cash, incurs holding cost, risks obsolescence (newsvendor problem).'},
  {q:'Brand equity in this sim does NOT directly…', opts:['Unlock distribution privileges','Raise willingness to pay','Reduce component costs','Multiply product appeal'], a:2,
   why:'Brand affects demand-side outcomes; component costs come from supply-side choices.'},
  {q:'A distributor takes a 28% margin vs 12% in your own stores, yet most companies enter new countries via distributors first because…', opts:['Distributors advertise your brand better','Reach is immediate and capital cost is tiny compared to a store network','Distributor margins fall over time','Stores have lower coverage'], a:1,
   why:'Channel sequencing: $300k buys up to 65% reach instantly; comparable store coverage costs tens of millions in capex. The price is losing marketing control.'},
  {q:'Opening your own retail stores instead of selling through intermediaries is an example of…', opts:['Horizontal integration','Outsourcing','Forward vertical integration','Conglomerate diversification'], a:2,
   why:'Moving downstream toward the customer (owning distribution/retail) is forward vertical integration — more control and margin, more capital at risk.'},
  {q:'Keeping last year\'s generation on sale at a reduced price alongside the new flagship is called…', opts:['Loss leadership','Price laddering across generations','Predatory pricing','Dynamic pricing'], a:1,
   why:'Price laddering uses older generations to serve price-sensitive buyers while the new generation defends the premium tier — but the gens cannibalize each other, so order the ladder carefully.'},
  {q:'Announcing a far better next-generation product too early can collapse sales of the current one. This is the…', opts:['Veblen effect','Ratchet effect','Osborne effect','Halo effect'], a:2,
   why:'The Osborne effect: buyers defer purchases to wait for the announced refresh, starving current-generation revenue — the cautionary tale of generational cadence.'},
  {q:'In a repeated game against the same rivals, why do oligopolies usually avoid all-out price wars?', opts:['Regulators forbid price cuts','Each firm remembers and retaliates next round, so cutting invites matching cuts that erase everyone\'s margin','Demand is perfectly inelastic','Prices are fixed by the market'], a:1,
   why:'Repeated interaction makes tit-for-tat credible: a price cut is matched, leaving the old shares at thinner margins. Memory of past rounds sustains tacit cooperation — the logic of the repeated prisoner\'s dilemma.'},
  {q:'A small entrant faces a dominant incumbent. The lower-risk competitive move is usually to…', opts:['Attack head-on in the incumbent\'s core segment at a lower price','Differentiate into a segment, tier or country the incumbent under-serves','Match the incumbent\'s price exactly','Announce a price war publicly'], a:1,
   why:'Head-on entry where the incumbent is strongest invites retaliation and a margin war. Differentiation into white space lets you compete on your own terms (Porter: rivalry & barriers to entry).'},
];

const QUARTER_NAMES = r => `Q${((r-1)%4)+1} ${2026+Math.floor((r-1)/4)}`;
/* ============================================================
   GAME CONFIGURATION (instructor-customizable)
============================================================ */
/* level scales for the instructor panel */
const CAP_LEVELS = {vlow:{label:'Very low',cash:8e6}, low:{label:'Low',cash:14e6}, medium:{label:'Medium',cash:20e6}, high:{label:'High',cash:30e6}, vhigh:{label:'Very high',cash:50e6}};
const COMP_LEVELS = {vlow:0.5, low:0.75, medium:1, high:1.25, vhigh:1.5};
const GROWTH_RATES = {slow:1.01, normal:1.022, fast:1.04};
const EVENT_RATES = {off:0, rare:0.5, normal:1, frequent:2};
const DEFAULT_GAME_CFG = {nCountries:10, manual:false, countries:null, years:5, numComps:3, numPartners:3,
  playerCap:'medium', compCap:'medium', compShare:'medium', compBrand:'medium', compTech:'medium',
  events:'normal', growth:'normal',
  /* ── Financing layer (DESIGN-corporate-finance) — instructor-configurable.
     The game is always a public company now; the private option is removed (§3). ──*/
  foundingStake:1,          // founder ownership at start (1 = 100%); seedHolders take the rest
  seedHolders:null,         // optional cap table: [{name, pct}] pre-dilution (§11.2)
  instruments:null,         // null = all on; else {loan,bond,equity,buyback,dividend:bool} (§11.2)
  maxDE:2.0,                // bank leverage covenant ceiling (§5.3)
  revolverPenalty:0.035,    // emergency overdraft rate /qtr (§5.4)
  controlSeverity:1,        // multiplier on the composite control penalty (§9.3)
  bondThreshold:40e6,       // book-equity gate for bond access (§6.1)
  /* Rule set a new game is created under (DESIGN-insolvency-and-direct-fulfilment §3).
     v1 = the lifted prototype rules; v2 adds direct online fulfilment, the scaled overdraft
     penalty and insolvency watch. Games created before the stamp existed read as v1. */
  rulesV:2};
/** The rule set a game resolves under. Absent stamp (old saves) ⇒ v1. */
function rulesV(g){ return (g && g.rulesV) || 1; }
/* the n largest markets by estimated smartwatch revenue (demand × income as a price proxy) */
function topCountryIds(n){
  return COUNTRIES.slice().sort((a,b)=>Engine.qDemand(b,1)*b.income - Engine.qDemand(a,1)*a.income)
    .slice(0,n).map(c=>c.id);
}
/* resolve a class's (or the standalone default) config into concrete new-game parameters */
function gameCfgOf(cls){
  const f = Object.assign({}, DEFAULT_GAME_CFG, (cls&&cls.gameCfg)||{});
  const rounds = (cls && !cls.gameCfg && cls.rounds)? cls.rounds : clamp(f.years,5,20)*4;
  let ids = (f.manual && Array.isArray(f.countries) && f.countries.length>=4)
    ? f.countries.filter(cc=>COUNTRIES.some(c=>c.id===cc))
    : topCountryIds(clamp(f.nCountries,4,COUNTRIES.length));
  if(ids.length<4) ids = topCountryIds(clamp(f.nCountries,4,COUNTRIES.length));
  return {rounds,
    cash: (CAP_LEVELS[f.playerCap]||CAP_LEVELS.medium).cash,
    countries: ids.length>=COUNTRIES.length? null : ids,
    numComps: clamp(f.numComps,1,COMPETITORS.length),
    numPartners: clamp(f.numPartners!=null? f.numPartners : 3, 1, 7),
    compMods: {capital:COMP_LEVELS[f.compCap]||1, share:COMP_LEVELS[f.compShare]||1,
               brand:COMP_LEVELS[f.compBrand]||1, tech:COMP_LEVELS[f.compTech]||1},
    growth: GROWTH_RATES[f.growth]||1.022,
    eventM: EVENT_RATES[f.events]!=null? EVENT_RATES[f.events] : 1,
    publicCo: true,                 // §3: every game is now a public company
    foundingStake: f.foundingStake!=null? clamp(f.foundingStake,0.05,1) : 1,
    seedHolders: Array.isArray(f.seedHolders)? f.seedHolders.slice() : null,
    instruments: f.instruments || null,
    maxDE: f.maxDE!=null? f.maxDE : 2.0,
    revolverPenalty: f.revolverPenalty!=null? f.revolverPenalty : 0.035,
    controlSeverity: f.controlSeverity!=null? f.controlSeverity : 1,
    bondThreshold: f.bondThreshold!=null? f.bondThreshold : 40e6,
    rulesV: f.rulesV!=null? f.rulesV : DEFAULT_GAME_CFG.rulesV};
}
/* which financing instruments are enabled for this game (null cfg = all on) */
function instrEnabled(g, key){
  const ins = g && g.instruments;
  if(!ins) return true;
  return ins[key]!==false;
}
/* active config of the game currently being simulated/rendered. Older saves (no countries
   field) keep the full map, 3 rivals and stock behaviour. */
let ACFG = {ids:null, n:3, mods:{share:1,brand:1,tech:1,capital:1}, growth:1.022, eventM:1};
function applyGameCfg(g){
  ACFG.ids   = (g && Array.isArray(g.countries) && g.countries.length)? new Set(g.countries) : null;
  ACFG.n     = (g && g.numComps)? clamp(g.numComps,1,COMPETITORS.length) : 3;
  ACFG.mods  = (g && g.compMods) || {share:1,brand:1,tech:1,capital:1};
  ACFG.growth= (g && g.growth) || 1.022;
  ACFG.eventM= (g && g.eventM!=null)? g.eventM : 1;
}
function ACTIVE(){ return ACFG.ids? COUNTRIES.filter(c=>ACFG.ids.has(c.id)) : COUNTRIES; }
function COMPS(){ return COMPETITORS.slice(0, ACFG.n); }
/* ============================================================
   PERSISTENCE & AUTH
============================================================ */
/* v3 → v4 migration: each product becomes a model whose current spec is Generation 1 (§3.4) */
function migrateGameV4(g){
  if(!g) return g;
  g.products = (g.products||[]).map(p=>{
    if(p.gens) return p; // already migrated
    const spec = {cpu:p.cpu, display:p.display, battery:p.battery, casing:p.casing, strap:p.strap,
                  size:p.size, style:p.style, color:p.color, sensors:(p.sensors||['hr']).slice()};
    return {id:p.id, name:p.name, targetSeg:p.targetSeg, active:p.active!==false,
      mkt:p.mkt||{budget:1.0, channel:'social', creative:'life'}, nextGen:2, draft:null,
      gens:[{gen:1, spec, price:p.price, production:p.production||0, inventory:p.inventory||0,
             launched:p.launched||1, lifetimeSold:0}]};
  });
  return g;
}
/* v4 → v5 migration (DESIGN-tech-tree §11): convert the flat-cost R&D model to budget→RP.
   Completed-tech ids are unchanged (all 7 legacy ids exist in the tree) and grandfathered. */
function migrateGameV5(g){
  if(!g) return g;
  Engine.normalizeRnd(g);
  g.rivalTech = g.rivalTech || {};
  if(!g._v5toast && g.round>1){ g._v5toast = true; // existing game: point at the new slider, budget starts at $0
    (g.news=g.news||[]).unshift({icon:'🧪', when:QUARTER_NAMES(g.round), text:'R&D is now a Research tree funded by a quarterly budget slider (it starts at $0). Set a budget and pick a path on the 🧪 Research screen.'});
  }
  return g;
}
/* v5 → v6 migration (DESIGN-production-expansion §10, logistics §9): manufacturing network +
   logistics. g.factory:true becomes one seed Shenzhen 300k factory; mfgNet seeded from
   SUPPLIERS.assembly; warehouses + absorb freight mode initialized (behaviour-identical
   to today until the player engages). */
function migrateGameV6(g){
  if(!g) return g;
  /* "Plant" → "Factory" rename migration: in-progress saves stored the manufacturing
     array under g.plants and the robotics tech under the id 'plantauto'. Port both so
     existing games keep their built factories and completed research after the rename. */
  if(g.plants && !g.factories){ g.factories = g.plants; delete g.plants; }
  if(g.rnd){
    const remap = id => id==='plantauto' ? 'factoryauto' : id;
    if(Array.isArray(g.rnd.done))   g.rnd.done   = g.rnd.done.map(remap);
    if(Array.isArray(g.rnd.queue))  g.rnd.queue  = g.rnd.queue.map(remap);
    if(g.rnd.active==='plantauto')  g.rnd.active = 'factoryauto';
    if(g.rnd.bank && g.rnd.bank.plantauto!=null){ g.rnd.bank.factoryauto = g.rnd.bank.plantauto; delete g.rnd.bank.plantauto; }
  }
  if(g.rivalTech){
    const remap = id => id==='plantauto' ? 'factoryauto' : id;
    for(const cid in g.rivalTech){ const rt=g.rivalTech[cid]; if(!rt) continue;
      if(Array.isArray(rt.done)) rt.done = rt.done.map(remap);
      if(rt.active==='plantauto') rt.active = 'factoryauto';
    }
  }
  if(!g.factories){
    g.factories = [];
    if(g.factory){ g.factories.push({id:'factory1', city:'szx', capacity:300, builtRound:1, status:'active', ramp:1}); }
  }
  if(!g.mfgNet){
    g.mfgNet = {
      outsourcers: SUPPLIERS.assembly.map(a=>({id:a.id, name:a.name, country:a.country, lat:a.lat, lon:a.lon,
        costM:a.costM, qualM:a.qualM, lead:a.lead, rel:a.rel, capacity:a.capacity,
        ordersHist:[], relationship:50, lastExpansion:null})),
      contracts: [],
      latent: LATENT_OUTSOURCERS.map(o=>({...o})),
    };
    // unlock any latent outsourcers whose round has already passed for in-progress saves
    const r=g.round||1;
    g.mfgNet.latent = g.mfgNet.latent.filter(o=>{
      if(o.unlockRound<=r){ g.mfgNet.outsourcers.push({...o, ordersHist:[], relationship:50, lastExpansion:null}); return false; }
      return true; });
  }
  if(!g.warehouses) g.warehouses = [];
  if(g.freightMode==null) g.freightMode = 'absorb';
  if(!g.freightModeByRegion) g.freightModeByRegion = {};
  return g;
}


function newGame(strategyId, companyName, maxRounds, brandStrat, cfg){
  cfg = cfg||{};
  const cash = cfg.cash!=null? cfg.cash : 20e6;
  const nC = cfg.countries? cfg.countries.length : COUNTRIES.length;
  return {
    company: companyName, strategy: strategyId, round:1, maxRounds: maxRounds||8, finished:false,
    /* brandStrat (Cap2 ch.7): 'range' = one shared brand across all products;
       'unique' = each product builds its own brand. */
    brandStrat: brandStrat||'range',
    cash, loan: 0, cumProfit:0, grossCumProfit:0, brand: 10,
    /* equity & stock (§ stock performance): founders' capital = paid-in equity.
       Every company IPOs at $10/share → shares = capital/10 and is publicly traded
       (DESIGN-corporate-finance §3 — the private-company path is removed). */
    startCash: cash, shares: Math.max(1, Math.round(cash/10)),
    publicCo: true,
    /* ── Financing layer (DESIGN-corporate-finance §4) ── */
    paidIn: cash,                                              // running paid-in capital; bookEquity reads this
    ownerShares: Math.max(1, Math.round(Math.max(1, Math.round(cash/10)) * (cfg.foundingStake!=null?cfg.foundingStake:1))),
    seedHolders: cfg.seedHolders || [],                        // instructor cap table (informational)
    debts: [],                                                 // term loans & bonds: {id,kind,face,rateQ,qLeft,qTotal,amort,issuedRound}
    divRatio: 0,                                               // dividend payout ratio of net income (0..0.8)
    ownerWealth: 0,                                            // cumulative dividends the founder pocketed
    divPaidCum: 0,                                             // cumulative dividends paid to all holders
    revolverUsedQ: false,                                      // was the emergency revolver tapped last quarter?
    /* instructor financing knobs (persisted so the running game is self-contained) */
    instruments: cfg.instruments || null,
    maxDE: cfg.maxDE!=null? cfg.maxDE : 2.0,
    revolverPenalty: cfg.revolverPenalty!=null? cfg.revolverPenalty : 0.035,
    controlSeverity: cfg.controlSeverity!=null? cfg.controlSeverity : 1,
    bondThreshold: cfg.bondThreshold!=null? cfg.bondThreshold : 40e6,
    rulesV: cfg.rulesV!=null? cfg.rulesV : DEFAULT_GAME_CFG.rulesV,
    insolvent: false, insolventSince: null,                    // insolvency watch (v2, §4.3)
    /* instructor game setup (null/absent = classic defaults — old saves keep working) */
    countries: cfg.countries? cfg.countries.slice() : null,
    numComps: cfg.numComps||3,
    numPartners: cfg.numPartners||3,
    compMods: cfg.compMods||null,
    growth: cfg.growth||1.022,
    eventM: cfg.eventM!=null? cfg.eventM : 1,
    awareness: Object.fromEntries(SEGMENTS.map(s=>[s.id,0.12])),
    products: [], nextProdNum:1,
    suppliers: {chips:'cnc', display:'cnd', battery:'cnb', sensors:'cns', assembly:'cna'},
    /* manufacturing network (DESIGN-production-expansion): owned factories + outsourcer relationships */
    factories: [],
    mfgNet: makeMfgNet(cfg.numPartners),
    /* logistics network (DESIGN-logistics-warehouses): warehouses + who-pays freight mode */
    warehouses: [],
    freightMode: 'absorb',         // 'absorb' | 'passthrough'
    freightModeByRegion: {},       // optional per-region override
    /* per-country distribution state: {dist, sub (subsidiary opened), n (stores), flagship, carrier, built (stores added this qtr)} */
    markets:{},
    rnd:{budget:0, active:null, queue:[], bank:{}, done:[]},
    rivalTech:{},
    history:[], log:[], news:[
      {icon:'🚀', when:'Q1 2026', text:'Your company is founded with '+fmtM(cash)+' in seed capital, competing in '+nC+' country markets against '+(cfg.numComps||3)+' rivals. You sell worldwide via cross-border online by default (full freight, and about a third of online buyers won\'t wait for the delivery — a regional warehouse fixes both) — open distributors, stores or carrier deals per country in the Distribution screen.'}
    ],
  };
}
function newProduct(g, seg){
  const tpl = {
    budget: {cpu:'c1',display:'d1',battery:'b2',casing:'k1',strap:'s1',size:'40',style:'modern',color:'ocean',sensors:['hr','slp'],price:99},
    fitness:{cpu:'c2',display:'d2',battery:'b3',casing:'k2',strap:'s1',size:'44',style:'sporty',color:'midnight',sensors:['hr','gps','slp'],price:219},
    health: {cpu:'c2',display:'d2',battery:'b3',casing:'k2',strap:'s2',size:'44',style:'classic',color:'silver',sensors:['hr','spo2','slp','tmp'],price:269},
    pro:    {cpu:'c3',display:'d3',battery:'b2',casing:'k3',strap:'s3',size:'44',style:'modern',color:'midnight',sensors:['hr','spo2','slp'],price:399},
    fashion:{cpu:'c2',display:'d3',battery:'b1',casing:'k4',strap:'s4',size:'40',style:'luxury',color:'gold',sensors:['hr'],price:499},
  }[seg]||{cpu:'c2',display:'d2',battery:'b2',casing:'k2',strap:'s1',size:'44',style:'modern',color:'midnight',sensors:['hr'],price:199};
  const {price, ...spec} = JSON.parse(JSON.stringify(tpl));
  return {id:uid(), name:'Model '+(['One','Two','Three','Four'][g.nextProdNum-1]||('M'+g.nextProdNum)),
    targetSeg:seg, active:true, mkt:{budget:1.0, channel:'social', creative:'life'},
    /* per-product brand (used under the Unique Brand strategy): the first product starts
       with the founding brand base; later products must build theirs from scratch (Cap2 ch.7) */
    bLoyal: g.nextProdNum===1? 10 : 5,
    bAw: Object.fromEntries(SEGMENTS.map(s=>[s.id, g.nextProdNum===1? 0.12 : 0.03])),
    nextGen:2, draft:null,
    /* proto: the quarter this brand-new model was created — its Gen 1 hardware stays fully
       editable (free) until that quarter ends and the design ships (§5) */
    gens:[{gen:1, spec, price, production:40, inventory:0, launched:g.round, lifetimeSold:0, proto:g.round}]};
}

/* ============================================================
   SIMULATION ENGINE
============================================================ */

const Engine = {
  comp(cat,id){ return COMPONENTS[cat].find(x=>x.id===id); },
  sup(cat,id){ return SUPPLIERS[cat].find(x=>x.id===id); },

  /* ===== R&D tech tree (DESIGN-tech-tree) ===== */
  techFx(g){ return computeTechFx(g && g.rnd && g.rnd.done ? g.rnd.done : []); },
  genCap(g){ return this.techFx(g).genCap; },
  /* normalize any save's g.rnd to the budget→RP model {budget,active,queue,bank,done} */
  normalizeRnd(g){
    if(!g) return;
    let r = g.rnd;
    if(!r){ g.rnd = {budget:0, active:null, queue:[], bank:{}, done:[]}; return g.rnd; }
    if(r.bank===undefined || r.queue===undefined){
      const done = (r.done||[]).slice();
      const nr = {budget:0, active:null, queue:[], bank:{}, done};
      // convert an in-flight legacy project to banked RP (bank = rp × progress/legacyRounds)
      if(r.active && TECH_BY_ID[r.active]){
        const legacyRounds = {water:1, osopt:1, lowpower:2, bateff:2, analytics:2, process:2, ecg:3}[r.active] || 2;
        nr.active = r.active;
        nr.bank[r.active] = TECH_BY_ID[r.active].rp * Math.min(1, (r.progress||0)/legacyRounds);
      }
      g.rnd = nr;
    }
    if(!g.rnd.queue) g.rnd.queue = [];
    if(!g.rnd.bank)  g.rnd.bank  = {};
    if(g.rnd.budget==null) g.rnd.budget = 0;
    return g.rnd;
  },
  /* available = all prereqs done, not already done/active/queued */
  techAvailable(g, id){
    const n=TECH_BY_ID[id]; if(!n) return false;
    const r=g.rnd; if(r.done.includes(id)||r.active===id||(r.queue||[]).includes(id)) return false;
    return n.req.every(q=>r.done.includes(q));
  },
  techLocked(g, id){ const n=TECH_BY_ID[id]; return !!n && !n.req.every(q=>g.rnd.done.includes(q)); },
  techEtaQtrs(g, id){
    const n=TECH_BY_ID[id]; if(!n) return Infinity;
    const rp=RP_PER_QTR(g.rnd.budget||0); if(rp<=0) return Infinity;
    const have=(g.rnd.bank&&g.rnd.bank[id])||0;
    return Math.max(1, Math.ceil((n.rp-have)/rp));
  },

  /* spec = the nine frozen hardware fields {cpu,display,battery,casing,strap,size,style,color,sensors} (§4) */
  attrs(spec, g){
    const p = spec;
    const cpu=this.comp('cpu',p.cpu), d=this.comp('display',p.display), b=this.comp('battery',p.battery),
          k=this.comp('casing',p.casing), s=this.comp('strap',p.strap), sz=COMPONENTS.sizes.find(x=>x.id===p.size),
          st=COMPONENTS.style.find(x=>x.id===p.style);
    const sens = p.sensors.map(id=>this.comp('sensors',id));
    const fx = this.techFx(g);
    const perf = clamp(cpu.perf + fx.perf, 0, 100);
    const draw = cpu.power * d.power * (1 + sens.reduce((a,x)=>a+x.power,0));
    const hours = b.cap * (sz.batBonus||1) * fx.batLife / (draw*7);
    const bat = clamp(100*(1-Math.exp(-hours/55)), 0, 100);
    const health = clamp(sens.reduce((a,x)=>a+x.health,0) + fx.health, 0, 100);
    const weight = (k.weight + b.weight)*fx.weightM + sz.weight + (s.id==='s4'?10:4);
    const comfort = clamp(100-(weight-30)*1.6, 20, 96);
    let fashion = clamp(k.fashion*.45 + s.fashion*.27 + d.fashion*.18 + sz.fashionMod + fx.fashion + (st.id==='luxury'?8:st.id==='modern'?4:0), 0, 100);
    let durab = clamp(k.durab + fx.durab + (st.id==='sporty'?5:0), 0, 100);
    // supplier quality influence
    const qm = (this.sup('chips',g.suppliers.chips).qualM + this.sup('display',g.suppliers.display).qualM +
                this.sup('battery',g.suppliers.battery).qualM + this.sup('sensors',g.suppliers.sensors).qualM)/4
                * this.sup('assembly',g.suppliers.assembly).qualM;
    const quality = clamp((perf*.28 + d.quality*.27 + durab*.22 + Math.min(health,80)*.23) * qm * fx.qualM, 0, 100);
    return {perf, bat, hours, health, fashion, durab, comfort, weight, quality, qm};
  },

  styleAffinity(styleId, segId){
    const m = {sporty:{fitness:10,budget:4}, classic:{health:9,pro:4}, modern:{budget:6,pro:6,fitness:3}, luxury:{fashion:13,pro:6}};
    return (m[styleId]||{})[segId]||0;
  },

  segFit(spec, a, segId, g){
    const seg = SEGMENTS.find(s=>s.id===segId);
    const base = a.perf*seg.w.perf + a.bat*seg.w.bat + a.health*seg.w.health + a.fashion*seg.w.fashion + a.durab*seg.w.durab + a.comfort*seg.w.comfort;
    let bonus = this.styleAffinity(spec.style, segId);
    if(g){ const fx=this.techFx(g);
      bonus += fx.fitAll;                                                  // sustain: +fit all segments
      if(fx.fitGps && segId==='fitness' && spec.sensors.includes('gps')) bonus += fx.fitGps; // gpsplus
    }
    return clamp(base + bonus, 0, 100);
  },

  /* shared component cost — everything except the source-specific assembly term (§4.3) */
  compCost(spec, g, mods){
    mods = mods||{};
    const p = spec, sup=g.suppliers;
    const cpu=this.comp('cpu',p.cpu).cost * this.sup('chips',sup.chips).costM * (mods.chipCost||1);
    const disp=this.comp('display',p.display).cost * this.sup('display',sup.display).costM;
    const bat=this.comp('battery',p.battery).cost * this.sup('battery',sup.battery).costM;
    const sens=p.sensors.reduce((a,id)=>a+this.comp('sensors',id).cost,0) * this.sup('sensors',sup.sensors).costM;
    const body=this.comp('casing',p.casing).cost + this.comp('strap',p.strap).cost;
    return cpu+disp+bat+sens+body;
  },
  /* per-order quantity scale discount (qty in thousands), automation lowers the thresholds */
  qtyDiscount(qty, g){
    const sm = this.techFx(g).scaleM;
    return qty>=200*sm?0.18: qty>=100*sm?0.12: qty>=50*sm?0.07: qty>=20*sm?0.03:0;
  },
  /* economies-of-scale curve keyed to a factory's BUILT capacity (k units/qtr) — §4.3 */
  scaleCurve(cap){ return clamp(1.25 - 0.30*Math.log10(Math.max(1,cap)/50), 0.78, 1.25); },
  ownFactoryResearchM(g){ return this.techFx(g).ownCostM; },
  /* own-factory reliability ramp 0→1 over ~3 qtrs (2 with factoryauto) */
  factoryRamp(factory, g){
    const fast = this.techFx(g).factoryRampFast? 2 : 3;
    return clamp(((g.round||1) - (factory.builtRound||1)) / fast, 0, 1);
  },
  reliabilityResearchBonus(g){ const fx=this.techFx(g); return fx.factoryRel + (fx.relia? fx.relia/250 : 0); },
  factoryRel(factory, g){ return clamp(0.90 + 0.07*this.factoryRamp(factory,g) + this.reliabilityResearchBonus(g), 0, 0.99); },
  setupCost(cityId, capacity){ const c=cityById(cityId)||{buildIdx:1}; return (3.0e6 + 16e3*capacity) * c.buildIdx; },
  fixedCost(cityId, capacity){ const c=cityById(cityId)||{buildIdx:1}; return (0.25e6 + 1.7e3*capacity) * c.buildIdx; },
  /* construction lead time (quarters) — larger factories/warehouses take longer to stand up */
  factoryBuildQuarters(cap){ return clamp(Math.ceil((+cap||50)/150), 1, 4); },
  whBuildQuarters(cap){ return clamp(Math.ceil((+cap||300000)/1000000), 1, 3); },
  factoryReady(p,g){ return p && p.status!=='mothballed' && p.status!=='building' && (p.readyRound==null || p.readyRound<=(g.round||1)); },
  whReady(w,g){ return !!w && w.status!=='building' && (w.readyRound==null || w.readyRound<=(g.round||1)); },
  readyWarehouses(g){ return (g.warehouses||[]).filter(w=>this.whReady(w,g)); },
  /* unit cost for an OWN factory at routed quantity qty (k units) — §4.3 */
  factoryUnitCost(spec, g, factory, qty, mods){
    const city = cityById(factory.city) || {laborIdx:1};
    const fx = this.techFx(g);
    let c = (this.compCost(spec,g,mods) + ASM_BASE * city.laborIdx * this.scaleCurve(factory.capacity));
    c *= (1 - this.qtyDiscount(qty,g));
    c *= fx.costM;
    c *= this.ownFactoryResearchM(g);
    return c;
  },
  /* unit cost for an OUTSOURCER (FoxLine / partner) at routed quantity qty (k units) */
  outsourcerUnitCost(spec, g, outs, qty, mods){
    const fx = this.techFx(g);
    const disc = this.contractDiscount(g, outs.id);
    let c = (this.compCost(spec,g,mods) + ASM_BASE * outs.costM * (1-disc));
    c *= (1 - this.qtyDiscount(qty,g));
    c *= fx.costM;
    return c;
  },
  /* discount granted by an active long-term volume commitment with this partner */
  contractDiscount(g, partnerId){
    const ks=(g.mfgNet&&g.mfgNet.contracts)||[]; let d=0;
    for(const k of ks) if(k.partnerId===partnerId && k.quartersLeft>0) d=Math.max(d,k.discount);
    return d;
  },
  /* every production source available this quarter: own active factories + mfgNet outsourcers */
  prodSources(g){
    const out=[];
    for(const pl of (g.factories||[])) if(pl.status!=='mothballed' && pl.status!=='building')
      out.push({kind:'factory', id:pl.id, ref:pl, city:pl.city, name:(cityById(pl.city)||{city:pl.city}).city+' factory',
                region:(cityById(pl.city)||{region:'asia'}).region, capacity:pl.capacity, rel:this.factoryRel(pl,g)});
    const net=this.ensureMfgNet(g);
    for(const o of net.outsourcers)
      out.push({kind:'outs', id:o.id, ref:o, city:null, name:o.name, country:o.country,
                region:OUTSOURCER_REGION[o.country]||'asia', capacity:o.capacity, rel:o.rel});
    return out;
  },
  /* unit cost of a source for a spec at routed qty (k units) */
  sourceUnitCost(src, spec, g, qty, mods){
    return src.kind==='factory'? this.factoryUnitCost(spec,g,src.ref,qty,mods) : this.outsourcerUnitCost(spec,g,src.ref,qty,mods);
  },
  /* public unitCost: cheapest ACHIEVABLE unit cost across all sources (drives design/pricing
     displays). With no factories and the legacy default supplier this reproduces FoxLine's cost. */
  unitCost(spec, g, qty, mods){
    const srcs = this.prodSources(g);
    if(!srcs.length){ // no network at all — fall back to the chosen legacy assembly supplier
      const o = this.sup('assembly', (g.suppliers&&g.suppliers.assembly)||'cna');
      const fx=this.techFx(g);
      return (this.compCost(spec,g,mods) + ASM_BASE*o.costM) * (1-this.qtyDiscount(qty,g)) * fx.costM;
    }
    let best=Infinity;
    for(const s of srcs) best=Math.min(best, this.sourceUnitCost(s, spec, g, qty, mods));
    return best;
  },

  /* ===== Manufacturing network state (DESIGN-production-expansion) ===== */
  ensureMfgNet(g){
    if(!g.mfgNet){
      g.mfgNet = {
        outsourcers: SUPPLIERS.assembly.map(a=>({
          id:a.id, name:a.name, country:a.country, lat:a.lat, lon:a.lon,
          costM:a.costM, qualM:a.qualM, lead:a.lead, rel:a.rel, capacity:a.capacity,
          ordersHist:[], relationship:50, lastExpansion:null })),
        contracts: [],
        latent: LATENT_OUTSOURCERS.map(o=>({...o})),
      };
    }
    g.mfgNet.outsourcers = g.mfgNet.outsourcers || [];
    g.mfgNet.contracts   = g.mfgNet.contracts   || [];
    g.mfgNet.latent      = g.mfgNet.latent      || LATENT_OUTSOURCERS.map(o=>({...o}));
    return g.mfgNet;
  },
  outsourcerById(g, id){ return this.ensureMfgNet(g).outsourcers.find(o=>o.id===id); },
  totalNetworkCap(g){ return this.prodSources(g).reduce((a,s)=>a+s.capacity,0); },
  activeFactories(g){ return (g.factories||[]).filter(p=>p.status!=='mothballed' && p.status!=='building'); },
  buildingFactories(g){ return (g.factories||[]).filter(p=>p.status==='building'); },
  factoryFixedTotal(g){ return (g.factories||[]).reduce((a,p)=>a + (p.status==='building'?0:this.fixedCost(p.city,p.capacity)*(p.status==='mothballed'?0.30:1)), 0); },

  /* Optimal (greedy least-cost) allocation of a gen's planned k-units across sources.
     freeCap: {srcId:kRemaining} mutated in place. mode: 'cost' | 'rel'.
     Own-factory fixed cost is sunk this quarter, so marginal cost = variable factoryUnitCost. */
  autoAllocate(spec, g, plannedK, freeCap, mode, mods){
    const srcs = this.prodSources(g);
    const alloc = {};
    let remaining = plannedK;
    // marginal-cost ranking; reliability mode adds a penalty for shaky sources
    const ranked = srcs.map(s=>{
      const mc = this.sourceUnitCost(s, spec, g, Math.max(1,plannedK), mods);
      const pen = mode==='rel'? (1-s.rel)*60 : 0;
      return {s, score: mc + pen};
    }).sort((a,b)=>a.score-b.score);
    for(const {s} of ranked){
      if(remaining<=0) break;
      const free = freeCap[s.id]!=null? freeCap[s.id] : s.capacity;
      const take = Math.min(remaining, free);
      if(take>0){ alloc[s.id]=(alloc[s.id]||0)+take; freeCap[s.id]=free-take; remaining-=take; }
    }
    return {alloc, unfilled:remaining};
  },
  blendedCost(spec, g, alloc, mods){
    let units=0, cost=0;
    for(const id in alloc){ const k=alloc[id]; if(k<=0) continue;
      const src=this.prodSources(g).find(s=>s.id===id); if(!src) continue;
      cost += k*this.sourceUnitCost(src, spec, g, k, mods); units+=k; }
    return units>0? cost/units : this.unitCost(spec,g,Math.max(1,units),mods);
  },

  /* ===== Logistics / two-leg freight (DESIGN-logistics-warehouses + assembly §6) ===== */
  ensureLogistics(g){
    g.warehouses = g.warehouses || [];
    if(g.freightMode==null) g.freightMode='absorb';
    if(g.whAlloc===undefined) g.whAlloc=null; // per-warehouse online-fulfilment allocation; null = auto
    for(const w of g.warehouses) if(!w.stock) w.stock={}; // finished-goods held at the warehouse, by SKU
    return g;
  },
  whStockTotal(w){ return w&&w.stock? Object.values(w.stock).reduce((a,b)=>a+b,0) : 0; },
  whCapex(cityId, capacity){ const c=cityById(cityId)||{buildIdx:1}; return (5.0e5 + 0.55*capacity)*c.buildIdx; },
  whOpex (cityId, capacity){ const c=cityById(cityId)||{buildIdx:1}; return (8.0e4 + 0.12*capacity)*c.buildIdx; },
  /* the factory/assembly city that sources outbound freight: the source carrying the most
     allocated volume this quarter, else the cheapest seed outsourcer's region proxy city */
  sourceCityForFreight(g, allocTotals){
    // allocTotals: {srcId:kUnits} aggregated across all gens (optional)
    if(allocTotals){
      let bestId=null,best=-1; for(const id in allocTotals) if(allocTotals[id]>best){best=allocTotals[id];bestId=id;}
      if(bestId){ const pl=(g.factories||[]).find(p=>p.id===bestId); if(pl) return pl.city;
        const o=this.outsourcerById(g,bestId); if(o){ const reg=OUTSOURCER_REGION[o.country]||'asia';
          // pick a roster city in that region as the freight origin proxy
          const cc=CITIES.find(c=>c.region===reg); return cc? cc.id : 'szx'; } }
    }
    const aps=this.activeFactories(g); if(aps.length) return aps[0].city;
    return 'szx';
  },
  /* cheapest route to a country (region): direct-from-source vs via any warehouse.
     Returns {total, inb, last, via}. via = warehouse id, or null for direct. */
  routeToCountry(g, country, srcCityId){
    const reg = country.region;
    const whs = this.readyWarehouses(g);                 // only operational warehouses can hold/ship stock
    if((this.activeFactories(g).length===0) && whs.length===0){
      // calibration baseline: legacy flat direct rates from the chosen assembly node
      const asm=this.sup('assembly',(g.suppliers&&g.suppliers.assembly)||'cna');
      const t=(FREIGHT_RATES[asm.id==='mxa'?'mx':'asia'])[reg];
      return {total:t, inb:0, last:t, via:null};
    }
    const dt=REGION_FREIGHT[(cityById(srcCityId)||{region:'asia'}).region][reg];
    let best={total:dt, inb:0, last:dt, via:null}; // direct = no warehouse stocking leg, all last-leg
    for(const w of whs){ const inb=inbound(srcCityId,w.city), last=lastLeg(w.city,reg), t=inb+last;
      if(t<best.total) best={total:t, inb, last, via:w.id}; }
    return best;
  },
  freightToCountry(g, country, srcCityId){ return this.routeToCountry(g,country,srcCityId).total; },
  /* direct-from-factory rate (region→region, all last-leg, no warehouse) — used for OFFLINE channels */
  directRate(g, reg, srcCityId){
    if((this.activeFactories(g).length===0) && this.readyWarehouses(g).length===0){
      const asm=this.sup('assembly',(g.suppliers&&g.suppliers.assembly)||'cna');
      return (FREIGHT_RATES[asm.id==='mxa'?'mx':'asia'])[reg];
    }
    return REGION_FREIGHT[(cityById(srcCityId)||{region:'asia'}).region][reg];
  },
  /* ONLINE route: online orders ship ONLY from a warehouse, never direct. Picks the cheapest
     operating warehouse that reaches the region, preferring ones that actually hold stock.
     Returns {total, inb, last, via} or {via:null, none:true} when no warehouse is operating. */
  onlineRouteToCountry(g, country, srcCityId){
    const whs=this.readyWarehouses(g);
    if(!whs.length) return {total:0, inb:0, last:0, via:null, none:true};
    const reg=country.region;
    const stocked=whs.filter(w=>this.whStockTotal(w)>0);
    const pool=stocked.length? stocked : whs;
    let best=null,bt=Infinity;
    for(const w of pool){ const inb=inbound(srcCityId,w.city), last=lastLeg(w.city,reg), t=inb+last;
      if(t<bt){ bt=t; best={total:t, inb, last, via:w.id}; } }
    return best;
  },
  /* freight allocation for the P&L. byCountry: {cc:{units, onlineUnits}}. ONLINE units route via a
     warehouse (two-leg: inbound + last-leg); OFFLINE (store/distributor/carrier) ships direct from
     the factory. Returns itemized freight, inbound/last split, pass-through reimbursement and per-WH load. */
  computeFreight(g, byCountry, srcCityId, mods){
    mods=mods||{}; const fm=mods.freight||1;
    const passFor=c=>((g.freightModeByRegion&&g.freightModeByRegion[c.region])||g.freightMode||'absorb')==='passthrough';
    const whLoad={}; for(const w of this.readyWarehouses(g)) whLoad[w.id]=0;
    let inboundF=0, lastF=0, recovered=0, overflow=0;
    const order=ACTIVE().slice().sort((a,b)=>(byCountry[b.id]?byCountry[b.id].units:0)-(byCountry[a.id]?byCountry[a.id].units:0));
    const perCountry={};
    for(const c of order){
      const cm=byCountry[c.id]; const tot=(cm&&cm.units)||0; if(tot<=0){ perCountry[c.id]={total:0,via:null}; continue; }
      const onU=Math.min(tot, Math.max(0,(cm&&cm.onlineUnits)||0)); const offU=Math.max(0, tot-onU);
      const dd=this.directRate(g, c.region, srcCityId);
      let pay=0, onVia=null;
      // OFFLINE — direct from factory, no warehouse leg
      if(offU>0){ lastF+=offU*dd; pay+=offU*dd; }
      // ONLINE — via the cheapest stocked warehouse serving the region
      if(onU>0){ const r=this.onlineRouteToCountry(g,c,srcCityId);
        if(r && r.via!=null){ inboundF+=onU*r.inb; lastF+=onU*r.last; pay+=onU*r.total; whLoad[r.via]+=onU; onVia=r.via; }
        else { lastF+=onU*dd; pay+=onU*dd; } }
      if(passFor(c)) recovered += pay;
      perCountry[c.id]={total: tot>0? pay/tot : 0, via:onVia};
    }
    const freight=(inboundF+lastF)*fm;
    return {freight, inbound:inboundF*fm, last:lastF*fm, recovered:recovered*fm, whLoad, overflow, perCountry};
  },

  /* ===== Online fulfilment via warehouses =====
     Online demand is served first from finished goods positioned at a warehouse. Store /
     distributor / carrier orders ship direct from factories. What no warehouse can serve:
       rules v1 — lost as a stockout;
       rules v2 — ships direct from the factory pool at full cross-border freight, but only
                  DIRECT_ONLINE_CONV of it converts (DESIGN-insolvency-and-direct-fulfilment §5). */
  onlineShareCell(g, c, segId, round){ // share of a cell's won units that come via the online channel
    const parts=this.covParts(g,c,segId,round); const sum=parts.reduce((a,p)=>a+p.cov,0);
    const on=parts.find(p=>p.id==='online'); return (sum>0&&on)? on.cov/sum : (on?1:0);
  },
  onlineUnitsProxy(g, c){ // estimated online units/qtr for a country (UI planning + auto-allocate)
    let onW=0, totW=0;
    for(const s of SEGMENTS){ const d=this.segDemand(c,s.id,g.round); const parts=this.covParts(g,c,s.id,g.round);
      const sum=parts.reduce((a,p)=>a+p.cov,0); const on=parts.find(p=>p.id==='online');
      onW+=d*(on?on.cov:0); totW+=d*sum; }
    const share = totW>0? onW/totW : 1;
    const last=g.history.length? g.history[g.history.length-1] : null;
    const base=(last&&last.byCountry&&last.byCountry[c.id])? (last.byCountry[c.id].units||0)
               : this.qDemand(c,g.round)*this.countryCoverage(g,c,g.round)*0.4;
    return base*share;
  },
  /* auto-allocate online-bound production to warehouses, by where the demand routes (cheapest WH) */
  whAutoAlloc(g){
    const whs=this.readyWarehouses(g); const out={}; for(const w of whs) out[w.id]=0;
    if(!whs.length) return out;
    const srcCity=this.sourceCityForFreight(g,null);
    for(const c of ACTIVE()){
      const u=this.onlineUnitsProxy(g,c); if(u<=0) continue;
      let best=null,bt=Infinity; // cheapest warehouse route (online cannot ship direct)
      for(const w of whs){ const t=inbound(srcCity,w.city)+lastLeg(w.city,c.region); if(t<bt){bt=t;best=w;} }
      if(best) out[best.id]+=u;
    }
    for(const w of whs) out[w.id]=Math.min(out[w.id], w.capacity);
    return out;
  },
  /* resolved per-warehouse online supply this quarter (player allocation, else auto), capped by capacity */
  whSupply(g){
    const whs=this.readyWarehouses(g); const byWh={}; let total=0;
    const auto = (g.whAlloc==null)? this.whAutoAlloc(g) : null;
    for(const w of whs){ let v = auto? (auto[w.id]||0) : ((g.whAlloc&&g.whAlloc[w.id])||0);
      v=Math.max(0, Math.min(v, w.capacity)); byWh[w.id]=v; total+=v; }
    return {byWh, total};
  },

  /* ===== Models & generations (§4) ===== */
  GEN_CAP:3, // max in-production generations per model (§3.1)
  STATUS:{
    development:{icon:'🔧', color:'var(--warn)', label:'in development'},
    current:    {icon:'●',  color:'var(--acc)',  label:'current'},
    legacy:     {icon:'◐',  color:'var(--acc2)', label:'legacy'},
    sellthrough:{icon:'◌',  color:'var(--mut)',  label:'sell-through'},
    retired:    {icon:'✕',  color:'var(--dim)',  label:'retired'},
  },
  /* derived, never stored stale (§4.1) */
  genStatus(m, gv){
    if(gv.dev) return 'development';
    if(gv.production>0){
      const top = Math.max(...m.gens.filter(x=>!x.dev && x.production>0).map(x=>x.gen));
      return gv.gen===top ? 'current' : 'legacy';
    }
    return gv.inventory>0 ? 'sellthrough' : 'retired';
  },
  liveGens(m){ return m.gens.filter(gv=>['current','legacy','sellthrough'].includes(this.genStatus(m,gv))); },
  prodGens(m){ return m.gens.filter(gv=>!gv.dev && gv.production>0); },
  devGen(m){ return m.gens.find(gv=>gv.dev)||null; },
  currentGen(m){
    const pg=this.prodGens(m);
    if(pg.length) return pg.reduce((a,b)=>a.gen>b.gen?a:b);
    const lv=this.liveGens(m);
    if(lv.length) return lv.reduce((a,b)=>a.gen>b.gen?a:b);
    return m.gens.filter(x=>!x.dev).reduce((a,b)=>a.gen>b.gen?a:b, m.gens[0]);
  },
  genName(m, gv){ return m.gens.length>1 ? m.name+' '+gv.gen : m.name; }, // auto-naming (§5.3)
  activeModels(g){ return g.products.filter(m=>m.active); },
  liveSkus(g){ const out=[];
    for(const m of this.activeModels(g)) for(const gv of this.liveGens(m)) out.push({key:m.id+'-'+gv.gen, m, gv});
    return out;
  },
  /* the reason to iterate: rivals' fit improves with every generation (fitBonus in
     competitorState), so standing still means falling behind in Quality points. */
  compGenNum(round){ return Math.floor((round-1)/3)+1; }, // rivals refresh every 3 quarters (§6.3)

  /* ===== Drafts & engineering (§5.2) ===== */
  startDraft(g, m){
    if(m.draft || this.devGen(m)) return false;
    const src = m.gens.filter(x=>!x.dev).reduce((a,b)=>a.gen>b.gen?a:b); // clone the newest gen's spec
    m.draft = {spec: JSON.parse(JSON.stringify(src.spec)), base: src.gen};
    return true;
  },
  draftChanges(m){
    const d=m.draft; if(!d) return [];
    const base=m.gens.find(x=>x.gen===d.base).spec, s=d.spec, ch=[];
    for(const [f,label] of [['cpu','CPU'],['display','Display'],['battery','Battery'],['casing','Case'],
                            ['strap','Strap'],['size','Size'],['style','Design'],['color','Finish']])
      if(base[f]!==s[f]) ch.push({f, label});
    if([...base.sensors].sort().join()!==[...s.sensors].sort().join()) ch.push({f:'sensors', label:'Sensors'});
    return ch;
  },
  /* $0.5M base + $0.25M per changed spec field (sensors count once; finish-only flat $0.3M), cap $2.5M */
  draftCost(m, g){
    const ch=this.draftChanges(m); if(!ch.length) return 0;
    const em = g? this.techFx(g).engCostM : 1; // flexfab: −40% engineering cost
    if(ch.every(c=>c.f==='color')) return Math.round(0.3e6*em);
    return Math.round(Math.min(2.5e6, 0.5e6 + 0.25e6*ch.length)*em);
  },
  commitDraft(g, m){
    const chs=this.draftChanges(m), cost=this.draftCost(m, g);
    if(!m.draft || !chs.length || g.cash<cost || this.devGen(m)) return false;
    g.cash -= cost;
    const gv={gen:m.nextGen, spec:m.draft.spec, price:0, production:0, inventory:0, launched:null,
              dev:true, committed:g.round, engCost:cost, lifetimeSold:0};
    m.gens.push(gv); m.nextGen++; m.draft=null;
    (g.pendingEng=g.pendingEng||[]).push({model:m.name, gen:gv.gen, cost, changes:chs.map(c=>c.label).join(', ')});
    g.news.unshift({icon:'🔧', when:QUARTER_NAMES(g.round), text:m.name+' '+gv.gen+' committed to engineering ('+fmtM(cost)+') — on sale '+QUARTER_NAMES(g.round+1)+'.'});
    return true;
  },
  /* scrap remaining inventory at 15% salvage (§6.1) */
  writeOff(g, m, gv){
    const val = gv.inventory*this.unitCost(gv.spec,g,0)*0.15;
    g.cash += val; gv.inventory=0; gv.production=0; gv.wound=true;
    if(!gv.eol){ gv.eol=g.round;
      g.news.unshift({icon:'🪦', when:QUARTER_NAMES(g.round), text:this.genName(m,gv)+' written off and retired — '+fmtK(gv.lifetimeSold||0)+' lifetime units sold, '+fmtM(val)+' salvage recovered.'});
    }
    return val;
  },

  /* completed-tech set a rival has researched (DESIGN-tech-tree §8). Empty if research disabled. */
  rivalDone(g, cid){ return (g && g.rivalTech && g.rivalTech[cid]) ? g.rivalTech[cid].done : []; },
  /* a rival's effective spec: base design with the best components it has unlocked swapped in (§8) */
  rivalSpec(c, done){
    const d = {...c.design, sensors:[...c.design.sensors]};
    const u = computeTechFx(done).unlocks;
    for(const id of ['c6','c5','c4']) if(u.has(id)){ d.cpu=id; break; }
    for(const id of ['b5','b4'])      if(u.has(id)){ d.battery=id; break; }
    if(u.has('d5')) d.display='d5'; else if(u.has('d4')) d.display='d4';
    if(u.has('k5')) d.casing='k5';
    for(const s of ['ecg','bp','glu']) if(u.has(s) && !d.sensors.includes(s)) d.sensors.push(s);
    return d;
  },
  competitorState(c, round, g){
    const md = ACFG.mods; // instructor: competitors' existing tech & brand levels
    // drift now carries half the old weight; the rest comes from the tree (techFx on the rival's done set, §8)
    const fitBonus = c.drift.qual*0.5*(round-1)*md.tech + (md.tech-1)*10;
    const price = Math.max(c.price*0.55, c.price + c.drift.price*(round-1));
    const brand = clamp(c.brand*md.brand + 0.4*(round-1), 0, 100);
    return {fitBonus, price, brand};
  },

  competitorFit(c, segId, round, g){
    const done = this.rivalDone(g, c.id);
    const fake = this.rivalSpec(c, done);
    const g0 = {rnd:{done}, suppliers:{chips:'tsw',display:'krd',battery:'krb',sensors:'jps',assembly:'cna'}};
    const a = this.attrs(fake, g0);
    const st = this.competitorState(c, round, g);
    let f = this.segFit(fake, a, segId, g0) + st.fitBonus;
    if(c.focus.includes(segId)) f += 8;
    return clamp(f, 0, 100);
  },

  /* ===== Rivals research the tree (DESIGN-tech-tree §8) ===== */
  ensureRivalTech(g){
    g.rivalTech = g.rivalTech || {};
    for(const c of COMPETITORS) if(!g.rivalTech[c.id]) g.rivalTech[c.id] = {rp:0, done:[], active:null};
    return g.rivalTech;
  },
  // archetype scoring weights — how much each rival values a given effect field
  _rivalWeights(cid){
    const W = {
      // branchPref nudges a rival down its archetype's home branch so its path stays legible in Market Intel (§8)
      aurion:   {fashion:1.2, qualM:1.1, health:0.9, perf:1.0, unlock:1.3, default:0.7, branchPref:['materials','silicon']},
      pulsefit: {batLife:1.2, durab:1.1, fitGps:1.5, health:1.0, unlock:0.9, default:0.6, branchPref:['sensors','silicon']},
      nexa:     {costM:1.6, scaleM:1.2, default:0.3, branchPref:['manufacturing']},
      strato:   {costM:1.1, batLife:0.9, fitGps:1.0, default:0.6, branchPref:['manufacturing','silicon']},
    };
    return W[cid] || W.strato;
  },
  scoreRivalNode(cid, node){
    const w = this._rivalWeights(cid), f = node.fx||{};
    let s = 0;
    const add = (field, mag)=> s += (w[field]!=null? w[field] : w.default) * mag;
    if(f.perf)    add('perf', f.perf);
    if(f.health)  add('health', f.health);
    if(f.fashion) add('fashion', f.fashion);
    if(f.durab)   add('durab', f.durab);
    if(f.batLife) add('batLife', (f.batLife-1)*60);
    if(f.qualM)   add('qualM', (f.qualM-1)*200);
    if(f.weightM) add('weightM', (1-f.weightM)*60);
    if(f.costM)   add('costM', (1-f.costM)*120);
    if(f.scaleM)  add('scaleM', (1-f.scaleM)*40);
    if(f.relia)   add('relia', f.relia);
    if(f.fitAll)  add('fitAll', f.fitAll*2);
    if(f.fitGps)  add('fitGps', f.fitGps);
    if(f.engCostM)add('engCostM', (1-f.engCostM)*30);
    if(f.shortfallM) add('shortfallM', (1-f.shortfallM)*30);
    if(f.unlock)  add('unlock', 14);
    if(w.branchPref){ const i=w.branchPref.indexOf(node.branch); if(i>=0) s += (i===0? 6 : 3); } // home-branch focus
    return s - 0.15*node.rp/100;
  },
  pickRivalNode(cid, done){
    let best=null, bestScore=-1e9;
    for(const n of TECH_TREE){
      if(done.includes(n.id)) continue;
      if(!n.req.every(q=>done.includes(q))) continue;
      const sc = this.scoreRivalNode(cid, n);
      if(sc>bestScore){ bestScore=sc; best=n.id; }
    }
    return best;
  },
  advanceRivalTech(g, round){
    if(ACFG.mods.tech<=0) return; // instructor can disable rival research via the tech modifier
    const rt = this.ensureRivalTech(g);
    for(const c of COMPS()){
      const st = rt[c.id];
      st.rp += (RND_TUNE.rivalRP[c.id]||80) * ACFG.mods.tech;
      if(!st.active) st.active = this.pickRivalNode(c.id, st.done);
      let guard=0;
      while(st.active && st.rp >= TECH_BY_ID[st.active].rp && guard++<8){
        st.rp -= TECH_BY_ID[st.active].rp;
        st.done.push(st.active);
        st.active = this.pickRivalNode(c.id, st.done);
      }
    }
  },

  /* ===== Capitalism 2–style Overall Rating (manual ch.7) =====
     R = Quality×QC/60 + Brand×BC/60 + (Std−Price)/Std × PC
     Quality = segment design fit (0–100). Brand = Brand Rating (0–100, see brandRating below).
     Std = segment reference price × country income. Concerns QC+BC+PC = 100 per segment.
     /60 normalization: a "standard" 60-rated quality or brand contributes exactly its
     concern weight in points. Price term: 0 at reference, +PC at free, negative above
     reference (capped at −1.5×PC ≈ 2.5× ref). Floor 1: bad products still sell trickles.
     priceConcernM scales PC (Consumer-slowdown event: price sensitivity up). */
  overallRating(seg, country, fit, brand, price, priceConcernM=1){
    const cc=seg.concerns, std=seg.refPrice*country.income;
    const priceTerm=clamp((std-Math.max(price||1,1))/std, -1.5, 1);
    return Math.max(fit*cc.qc/60 + brand*cc.bc/60 + priceTerm*cc.pc*priceConcernM, 1);
  },
  /* Cap2: Brand Rating = loyalty + awareness (manual ch.7, "brand rating is the sum of
     awareness and loyalty"). Loyalty (g.brand, 0–100) is earned slowly via sales, quality
     and satisfaction; awareness (0–1) is bought with campaigns and decays fast.
     Where a distributor controls the channel, the player's campaigns don't apply — their
     generic push acts like a flat 30% awareness (DIST_PUSH_AW). */
  DIST_PUSH_AW: 0.30,
  brandRating(loyalty, aw){ return clamp(0.6*loyalty + 40*aw, 0, 100); },
  effAwareness(ctrl, aw){ return ctrl*aw + (1-ctrl)*this.DIST_PUSH_AW; },
  /* ===== Brand strategy (Cap2 manual ch.7, "Brand Strategy") =====
     range  — products in the same product class share one brand: one loyalty score and
              one awareness pool. Every campaign builds the shared brand, which applies
              to ALL products. (Cap2's Corporate Brand collapses to Range here because
              the company sells a single product class — smartwatches.)
     unique — each product carries its own brand: per-product loyalty and awareness.
              A campaign builds only that product's brand, and a new product's brand
              must be built from scratch.
     Switching strategy mid-game resets all brand ratings to zero (Cap2 manual p.115). */
  brandStrategy(g){ return g.brandStrat||'range'; },
  isUnique(g){ return this.brandStrategy(g)==='unique'; },
  pBrand(g, m){ // lazily init per-product brand fields (older saves / strategy migration)
    if(m.bLoyal==null) m.bLoyal = g.brand!=null? g.brand : 10;
    if(!m.bAw) m.bAw = Object.fromEntries(SEGMENTS.map(s=>[s.id, (g.awareness? g.awareness[s.id] : 0.05)||0]));
    return m;
  },
  loyaltyOf(g, m){ return this.isUnique(g)? this.pBrand(g,m).bLoyal : g.brand; },
  awOf(g, m, segId){ return this.isUnique(g)? this.pBrand(g,m).bAw[segId] : g.awareness[segId]; },
  /* the Brand Rating a given product competes with in a given segment × channel (world aggregate) */
  myBrandRating(g, m, segId, ctrl){ return this.brandRating(this.loyaltyOf(g,m), this.effAwareness(ctrl, this.awOf(g,m,segId))); },
  /* ===== Per-country brand (player & rivals) =====
     Brand is local: every country holds its own loyalty score and awareness pool toward each
     manufacturer. Campaigns raise awareness only in the countries they run in; loyalty is
     earned country by country through local sales share. g.brand / g.awareness / m.bLoyal /
     m.bAw remain as demand-weighted world aggregates for the scorecard, topbar and charts. */
  ensureBrandCo(g){
    if(!g.brandCo){ g.brandCo={};
      for(const c of COUNTRIES) g.brandCo[c.id]={loyal: g.brand!=null? g.brand:10,
        aw: Object.fromEntries(SEGMENTS.map(s=>[s.id,(g.awareness? g.awareness[s.id]:0.12)||0]))};
    }
    for(const c of COUNTRIES) if(!g.brandCo[c.id]) g.brandCo[c.id]={loyal:g.brand||0, aw:Object.fromEntries(SEGMENTS.map(s=>[s.id,0]))};
    return g.brandCo;
  },
  pBrandCo(g, m){ // per-model per-country store (Unique Brand strategy)
    this.pBrand(g,m);
    if(!m.brandCo){ m.brandCo={};
      for(const c of COUNTRIES) m.brandCo[c.id]={loyal:m.bLoyal, aw:{...m.bAw}};
    }
    for(const c of COUNTRIES) if(!m.brandCo[c.id]) m.brandCo[c.id]={loyal:m.bLoyal||0, aw:Object.fromEntries(SEGMENTS.map(s=>[s.id,0]))};
    return m.brandCo;
  },
  brandIn(g, m, cc){ return this.isUnique(g)? this.pBrandCo(g,m)[cc] : this.ensureBrandCo(g)[cc]; },
  myBrandRatingIn(g, m, cc, segId, ctrl){ const e=this.brandIn(g,m,cc);
    return this.brandRating(e.loyal, this.effAwareness(ctrl, e.aw[segId])); },
  /* rivals: per-country rating derived from their distribution footprint — strong where they
     have a launched presence, weak where only gray imports reach. */
  compBrandIn(comp, cc, round){
    const st=this.competitorState(comp,round), p=this.compPresence(comp,cc,round);
    return clamp(st.brand*(p? (p.cheap? .70+.25*p.s : .80+.30*p.s) : .55), 0, 100);
  },
  compAwIn(comp, cc, round){
    const p=this.compPresence(comp,cc,round);
    return clamp(comp.aw*ACFG.mods.brand*(p? (p.cheap? .75+.20*p.s : .85+.30*p.s) : .50), 0, 1);
  },
  compRatingIn(comp, cc, round){ return this.brandRating(this.compBrandIn(comp,cc,round), this.compAwIn(comp,cc,round)); },
  /* campaign country group: m.mkt.countries (array of country ids) — missing/empty = worldwide */
  campaignCountries(m){
    const act = ACTIVE();
    const a = m.mkt && Array.isArray(m.mkt.countries)? m.mkt.countries.filter(cc=>act.some(c=>c.id===cc)) : [];
    return a.length? a : act.map(c=>c.id);
  },
  /* budget concentration: media cost scales with audience, so the same budget focused on a
     smaller selection of countries buys more awareness per buyer there (capped ×4) */
  campaignConc(m, round){
    const sel=this.campaignCountries(m).reduce((a,cc)=>a+this.qDemand(this.country(cc),round),0);
    return sel>0? Math.min(4, this.worldDemand(round)/sel) : 1;
  },
  /* rating → utility: cube so rating gaps matter (90 vs 60 ⇒ ≈3.4:1, not 1.5:1),
     then gate by distribution reach — the rating competes only for buyers it can reach. */
  ratingUtil(R, coverage){
    return Math.pow(R/100,3)*coverage;
  },

  /* est. share of each segment's demand if the quarter ran today — mirrors the advance()
     rating math (Overall Rating = quality + brand + price points, share ∝ (R/100)³ ×
     distribution reach, demand-weighted across countries), without event modifiers
     or stock limits. */
  segSharesEst(g, skus, round){
    const comps=COMPS();
    const compSts=comps.map(c=>this.competitorState(c,round));
    const pAttrs=skus.map(sk=>this.attrs(sk.gv.spec,g));
    const out={};
    for(const seg of SEGMENTS){
      const pFits=skus.map((sk,i)=>this.segFit(sk.gv.spec,pAttrs[i],seg.id,g));
      const cFits=comps.map(c=>this.competitorFit(c,seg.id,round,g));
      const o={mine:skus.map(()=>0), comps:comps.map(()=>0), D:0};
      for(const c of ACTIVE()){
        const D=this.segDemand(c,seg.id,round), meta=this.covMeta(g,c,seg.id,round);
        const us=comps.map((comp,i)=>this.ratingUtil(this.overallRating(seg,c,cFits[i],this.brandRating(this.compBrandIn(comp,c.id,round),this.compAwIn(comp,c.id,round)),compSts[i].price),
          this.compCov(comp,c,seg.id,round)));
        const ms=skus.map((sk,i)=>this.ratingUtil(this.overallRating(seg,c,pFits[i],this.myBrandRatingIn(g,sk.m,c.id,seg.id,meta.ctrl),sk.gv.price), meta.cov));
        const tot=us.reduce((a,b)=>a+b,0)+ms.reduce((a,b)=>a+b,0)||1;
        o.D+=D;
        ms.forEach((u,i)=>o.mine[i]+=D*u/tot);
        us.forEach((u,i)=>o.comps[i]+=D*u/tot);
      }
      out[seg.id]=o;
    }
    return out;
  },

  /* ===== Country demographics & demand sizing ===== */
  country(id){ return COUNTRIES.find(c=>c.id===id); },
  method(id){ return METHODS.find(m=>m.id===id); },
  /* smartwatch adopters as share of population: ≈8% at income 0.4 up to ≈22% at income 1.3 */
  penetration(inc){ return clamp(0.08 + (inc-0.4)*(0.14/0.9), 0.05, 0.24); },
  REPL:0.045, // base quarterly replacement/purchase rate, scaled by the calibration constant
  cal(){ // one-time calibration so world Q1 demand matches the old engine (~2.9M units/qtr)
    if(!this._cal){ let s=0; for(const c of COUNTRIES) s += c.pop*1e6*this.penetration(c.income)*this.REPL;
      this._cal = 2.9e6/s; }
    return this._cal;
  },
  potBuyers(c, segId){ const p = c.pop*1e6*this.penetration(c.income); return segId? p*PROFILES[c.profile][segId] : p; },
  qDemand(c, round){ return c.pop*1e6*this.penetration(c.income)*this.REPL*this.cal()*Math.pow(ACFG.growth,(round||1)-1); },
  segDemand(c, segId, round){ return this.qDemand(c, round)*PROFILES[c.profile][segId]; },
  worldDemand(round){ return ACTIVE().reduce((a,c)=>a+this.qDemand(c,round),0); },
  segWorldDemand(segId, round){ return ACTIVE().reduce((a,c)=>a+this.segDemand(c,segId,round),0); },

  /* ===== Player distribution & coverage ===== */
  mkt(g, cc){ if(!g.markets) g.markets={};
    return g.markets[cc] || (g.markets[cc]={dist:false, sub:false, n:0, flagship:false, carrier:false, built:0}); },
  hasPaid(m){ return !!(m && (m.dist || m.sub || m.carrier)); },
  storesNeeded(c, round){ // full-coverage store count; grows with the market
    return Math.max(1, Math.ceil(this.qDemand(c,round)*4/(14000*c.urban))); },
  storeCov(g, c, round){ const m=(g.markets||{})[c.id]; if(!m||!m.sub||!m.n) return 0;
    return Math.min(.95, Math.pow(m.n/this.storesNeeded(c,round), .9)); },
  /* per-cell coverage contributions: [{id, cov, control, fee}] */
  covParts(g, c, segId, round){
    const m=(g.markets||{})[c.id]||{};
    const parts=[{id:'online', cov:c.ecom*.45*this.method('online').segCov[segId], control:1, fee:.08}];
    if(m.dist) parts.push({id:'dist', cov:.65*this.method('dist').segCov[segId], control:0, fee:.28});
    if(m.sub){ let sc=this.storeCov(g,c,round)*this.method('stores').segCov[segId];
      if(m.flagship && (segId==='pro'||segId==='fashion')) sc=Math.min(.97, sc*1.18+.05);
      if(sc>0) parts.push({id:'stores', cov:sc, control:1, fee:.12}); }
    if(m.carrier){ const cv=.5*this.method('carrier').segCov[segId];
      if(cv>0) parts.push({id:'carrier', cov:cv, control:.6, fee:.32}); }
    return parts;
  },
  /* combined coverage + control share (campaigns apply) + blended fee for a country×segment cell */
  covMeta(g, c, segId, round){
    const ps=this.covParts(g,c,segId,round);
    let miss=1, tot=0, ctrl=0, fee=0;
    for(const p of ps){ miss*=(1-p.cov); tot+=p.cov; ctrl+=p.cov*p.control; fee+=p.cov*p.fee; }
    return {cov:Math.min(.97, 1-miss), ctrl: tot? ctrl/tot:1, fee: tot? fee/tot:.08};
  },
  coverage(g, c, segId, round){ return this.covMeta(g,c,segId,round).cov; },
  countryCoverage(g, c, round){ // demand-weighted across segments (for tables)
    let dw=0, cw=0; for(const s of SEGMENTS){ const d=this.segDemand(c,s.id,round); dw+=d; cw+=d*this.coverage(g,c,s.id,round); }
    return dw? cw/dw : 0;
  },
  ctrlFraction(g, round){ // share of world demand where your campaigns apply (marketing screen)
    let dw=0, cw=0; for(const c of ACTIVE()) for(const s of SEGMENTS){
      const d=this.segDemand(c,s.id,round), m=this.covMeta(g,c,s.id,round); dw+=d*m.cov; cw+=d*m.cov*m.ctrl; }
    return dw? cw/dw : 1;
  },

  /* ===== Market entry/exit actions (used by UI and bots) ===== */
  enterMethod(g, cc, mid){
    const c=this.country(cc), m=this.mkt(g,cc), meth=this.method(mid);
    if(!c||!meth||mid==='online') return false;
    if(mid==='dist' && !m.dist){ if(g.cash<meth.entry) return false; g.cash-=meth.entry; m.dist=true; m.distR=g.round; return true; }
    if(mid==='carrier' && !m.carrier){ if(g.cash<meth.entry) return false; g.cash-=meth.entry; m.carrier=true; m.carrierR=g.round; return true; }
    if(mid==='stores' && !m.sub){ if(g.cash<meth.entry) return false; g.cash-=meth.entry; m.sub=true; m.subR=g.round; return true; }
    return false;
  },
  /* same-quarter undo: full refund; once the quarter advances the deal is established and locked */
  ACT_RKEY:{dist:'distR', stores:'subR', carrier:'carrierR'},
  isEstablished(g, m, mid){
    const on = mid==='dist'? m.dist : mid==='stores'? m.sub : mid==='carrier'? m.carrier : false;
    return on && m[this.ACT_RKEY[mid]]!==g.round;
  },
  cancelMethod(g, cc, mid){
    const m=this.mkt(g,cc), meth=this.method(mid), rKey=this.ACT_RKEY[mid];
    if(!meth || !rKey || m[rKey]!==g.round) return false;
    if(mid==='dist' && m.dist) m.dist=false;
    else if(mid==='carrier' && m.carrier) m.carrier=false;
    else if(mid==='stores' && m.sub){ g.cash += m.n*3.5e5 + (m.flagship?FLAGSHIP_COST:0); m.n=0; m.sub=false; m.flagship=false; m.built=0; }
    else return false;
    g.cash += meth.entry; delete m[rKey];
    return true;
  },
  exitMethod(g, cc, mid){
    const m=this.mkt(g,cc);
    if(mid==='dist') m.dist=false;
    else if(mid==='carrier') m.carrier=false;
    else if(mid==='stores' && m.sub){ g.cash-=m.n*STORE_SEVERANCE; m.n=0; m.sub=false; m.flagship=false; m.built=0; }
    return true;
  },
  buildStores(g, cc, n){ // returns number actually built (8/qtr limit, cash check)
    const m=this.mkt(g,cc); if(!m.sub) return 0;
    let built=0;
    while(built<n && (m.built||0)<STORE_BUILD_LIMIT && g.cash>=3.5e5){ g.cash-=3.5e5; m.n++; m.built=(m.built||0)+1; built++; }
    return built;
  },
  closeStores(g, cc, n){
    const m=this.mkt(g,cc); if(!m.sub) return 0;
    let closed=0;
    while(closed<n && m.n>0){ g.cash-=STORE_SEVERANCE; m.n--; if(m.built>0) m.built--; closed++; }
    return closed;
  },
  setFlagship(g, cc){
    const m=this.mkt(g,cc);
    if(!m.sub || m.flagship || g.brand<FLAGSHIP_BRAND || g.cash<FLAGSHIP_COST) return false;
    g.cash-=FLAGSHIP_COST; m.flagship=true; return true;
  },
  totalStores(g){ return Object.values(g.markets||{}).reduce((a,m)=>a+(m.n||0),0); },

  /* ===== Competitor footprints (pure function of round — evolves without per-game state) ===== */
  compPresence(comp, cc, round){
    const md = ACFG.mods; // instructor: share scales footprint strength, capital scales expansion speed
    const b=comp.presence[cc];
    if(b) return {m:b.m, s:Math.min(.95, b.s*md.share + .02*(round-1))};
    if(comp.expand){ const i=comp.expand.indexOf(cc);
      if(i>=0){ const entered=Math.floor(1.5*(round-1)*md.capital); // ~1–2 new countries per quarter at medium capital
        if(i<entered) return {m:comp.expandMethod||'dist', s:Math.min(.75, (.3+.04*(entered-i))*md.share)}; } }
    if(comp.everywhere){ const c=this.country(cc);
      return {m:'dist', s:clamp((comp.everywhere[c.profile]||.4)*md.share, .05, .95), cheap:true}; }
    return null;
  },
  compCov(comp, c, segId, round){
    const p=this.compPresence(comp, c.id, round);
    if(!p) return clamp(.12*c.ecom*(segId==='budget'?1:.8), .03, .95); // gray imports only
    let cv=p.s*comp.covProfile[segId];
    if(p.cheap) cv*=.75; // Nexa's everywhere-cheap network: low coverage quality
    return clamp(cv, .05, .95);
  },
  compCountryCov(comp, c, round){ // demand-profile-weighted, for intel display
    let dw=0, cw=0; for(const s of SEGMENTS){ const sh=PROFILES[c.profile][s.id]; dw+=sh; cw+=sh*this.compCov(comp,c,s.id,round); }
    return dw? cw/dw : 0;
  },
  /* top uncontested country×segment cells — "white space" hints */
  whiteSpace(g, round){
    const cells=[];
    for(const c of ACTIVE()) for(const s of SEGMENTS){
      const maxComp=Math.max(...COMPS().map(k=>this.compCov(k,c,s.id,round)));
      if(maxComp<.2){ cells.push({c, seg:s, D:this.segDemand(c,s.id,round), mine:this.coverage(g,c,s.id,round)}); }
    }
    return cells.sort((a,b)=>b.D-a.D).slice(0,3);
  },

  /* §6.1 utility of one SKU in a country×segment cell — mirrors the inline "me" calc in advance(),
     used to inject rival human teams' SKUs into the shared choice set. Freight omitted (≈0 under the
     default absorb mode; its second-order effect only matters in the rival's own booking pass). */
  skuCellUtil(g, sk, c, seg, round, pcM){
    const meta = this.covMeta(g, c, seg.id, round);
    const fit  = this.segFit(sk.gv.spec, this.attrs(sk.gv.spec, g), seg.id, g);
    const br   = this.myBrandRatingIn(g, sk.m, c.id, seg.id, meta.ctrl);
    return this.ratingUtil(this.overallRating(seg, c, fit, br, sk.gv.price, pcM), meta.cov);
  },

  /* §6.1 advanceWorld — process a whole shared public market in one synchronized pass.
     `teams` = [{team, game}]. Every company (human team + scripted AI filler) competes in one
     market: one market-wide event roll, a union choice set, demand shared by utility. Each team
     then runs its own downstream pipeline (production → financials → scorecard → news). */
  advanceWorld(world, teams){
    teams = teams.filter(x=>x && x.game); if(!teams.length) return {};
    const round = world.round || (teams[0].game.round);
    const ai = Math.max(0, Math.min(world.aiCompanies!=null?world.aiCompanies : 3, COMPETITORS.length, 8 - teams.length));
    for(const {game} of teams) game.numComps = ai;          // shared AI-filler count (§6.2 cap: ≤8 companies)
    applyGameCfg(teams[0].game);
    // 1) one set of market-wide events for the whole class (§6.1 step 1)
    const mods={}, roundEvents=[]; let hitRival=null;
    for(const ev of EVENTS){
      if(Math.random() < ev.p*ACFG.eventM){ Object.assign(mods, ev.mod(teams[0].game)); roundEvents.push(ev);
        if(ev.id==='rivalRecall' && ai>0) hitRival = COMPS()[Math.floor(Math.random()*COMPS().length)].id;
        if(roundEvents.length>=2) break; }
    }
    const pcM = mods.elast||1;
    // 2) build the union choice set — every team's live SKUs, keyed by country|segment (§6.1 step 2)
    const cell = {};
    for(const {team, game} of teams){ applyGameCfg(game);
      for(const sk of this.liveSkus(game)){
        for(const c of ACTIVE()){ for(const seg of SEGMENTS){
          const u = this.skuCellUtil(game, sk, c, seg, round, pcM);
          if(u>0){ const k=c.id+'|'+seg.id; (cell[k]=cell[k]||[]).push({tid:team.id, u}); }
        }}
      }
    }
    // 3) snapshot pre-state for replay (§7.3)
    world.snapshots = world.snapshots || {};
    try{ world.snapshots[round] = { teams:Object.fromEntries(teams.map(t=>[t.team.id, JSON.parse(JSON.stringify(t.game))])) }; }catch(e){}
    // 4) run each company's pass against the shared field (§6.1 steps 3-4)
    const results = {};
    for(const {team, game} of teams){
      const rivalUtil = (cId,segId)=>{ const arr=cell[cId+'|'+segId]; if(!arr) return 0; let s=0; for(const e of arr) if(e.tid!==team.id) s+=e.u; return s; };
      results[team.id] = this.advance(game, { mods, roundEvents, hitRival, rivalUtil });
    }
    // 5) class wire + advance the world (§6.1 step 5)
    world.wire = world.wire || [];
    const ranked = teams.map(t=>({t:t.team, r:results[t.team.id]})).filter(x=>x.r).sort((a,b)=>(b.r.composite||0)-(a.r.composite||0));
    if(ranked.length){
      world.wire.unshift({icon:'🏆', when:QUARTER_NAMES(round), text:ranked[0].t.name+' leads the market after '+QUARTER_NAMES(round)+' — composite '+Math.round(ranked[0].r.composite||0)+', '+((ranked[0].r.share||0)*100).toFixed(1)+'% share.'});
      const tp = ranked.slice().sort((a,b)=>(b.r.profit||0)-(a.r.profit||0))[0];
      if(tp) world.wire.unshift({icon:'💰', when:QUARTER_NAMES(round), text:tp.t.name+' posted the quarter\'s top profit at '+fmtM(tp.r.profit||0)+'.'});
      if(roundEvents.length) world.wire.unshift({icon:roundEvents[0].icon||'⚡', when:QUARTER_NAMES(round), text:'Market-wide: '+roundEvents.map(e=>e.label).join(' · ')+' hit every company this quarter.'});
    }
    if(world.wire.length>40) world.wire.length=40;
    world.round = round + 1;
    world.status = 'results';
    return results;
  },

  advance(g, opts){
    opts = opts || {};                               // §6.1 shared-market hook (Team Play & Classroom Mode)
    applyGameCfg(g); // instructor game setup: active countries, rival count & strength modifiers
    this.ensureFinance(g); // back-fill financing state for old saves before the quarter runs
    this.normalizeRnd(g);
    const round = g.round;
    // In a shared (human-vs-human) market the world rolls ONE set of market-wide events and passes
    // them in, so chip shortages / downturns hit every team identically (§6.1 step 1). Solo play rolls its own.
    let mods, roundEvents;
    if(opts.mods){ mods = Object.assign({}, opts.mods); roundEvents = (opts.roundEvents||[]).slice(); if(opts.hitRival) g._hitRival = opts.hitRival; }
    else {
      mods = {}; roundEvents = [];
      // random events (frequency set by the instructor: off / rare / normal / frequent)
      for(const ev of EVENTS){
        if(Math.random() < ev.p*ACFG.eventM){ Object.assign(mods, ev.mod(g)); roundEvents.push(ev);
          if(ev.id==='rivalRecall') g._hitRival = COMPS()[Math.floor(Math.random()*COMPS().length)].id;
          if(roundEvents.length>=2) break; }
      }
    }
    // ===== R&D: budget → research points (DESIGN-tech-tree §5) =====
    // Spend the quarterly budget, convert to RP at diminishing returns, bank it on the active
    // node, roll completions through the queue (overflow carries forward, Civ-style).
    let rndSpend = 0;
    const rndCompleted = [];                                  // techs finished this quarter (for the results report)
    const rnd = g.rnd;
    if(!rnd.active && rnd.queue.length) rnd.active = rnd.queue.shift();
    if(rnd.active && (rnd.budget||0) > 0){
      rndSpend = rnd.budget;                                  // continuous R&D spend in the P&L
      rnd.bank[rnd.active] = (rnd.bank[rnd.active]||0) + RP_PER_QTR(rnd.budget);
      let guard = 0;
      while(rnd.active && (rnd.bank[rnd.active]||0) >= TECH_BY_ID[rnd.active].rp && guard++ < 12){
        const node = TECH_BY_ID[rnd.active];
        const overflow = rnd.bank[rnd.active] - node.rp;
        rnd.done.push(node.id); delete rnd.bank[rnd.active]; rndCompleted.push(node.id);
        g.news.unshift({icon:node.icon, when:QUARTER_NAMES(round), text:`R&D complete: ${node.name}. ${nodeEffectText(node)}`});
        rnd.active = rnd.queue.length ? rnd.queue.shift() : null;
        if(rnd.active) rnd.bank[rnd.active] = (rnd.bank[rnd.active]||0) + overflow; // overflow → next node
      }
    }
    // rivals research the same tree (§8)
    this.advanceRivalTech(g, round);
    // ── Manufacturing network: per-source allocation & reliability (DESIGN-production-expansion §5) ──
    this.ensureMfgNet(g); this.ensureLogistics(g);
    const fxq = this.techFx(g);
    const srcs = this.prodSources(g);
    const netCap = srcs.reduce((a,s)=>a+s.capacity,0);
    const AM = this.activeModels(g);
    let totalPlanned = 0; // k units, summed over every in-production generation (§6.1)
    for(const m of AM) for(const gv of this.prodGens(m)) totalPlanned += gv.production;
    let capScale = (netCap>0 && totalPlanned>netCap)? netCap/totalPlanned : 1;
    const cap = netCap;
    if(capScale<1){
      g.news.unshift({icon:'🏭', when:QUARTER_NAMES(round), text:`Network capacity exceeded (${Math.round(totalPlanned)}k planned vs ${Math.round(netCap)}k available). Orders scaled down ${Math.round((1-capScale)*100)}% — build a factory or expand a partner.`});
    }
    // component-supplier reliability (chips/display/battery/sensors) — a global shortfall draw,
    // separate from the per-source assembly reliability resolved below; digitwin halves it.
    const compRel = [this.sup('chips',g.suppliers.chips), this.sup('display',g.suppliers.display),
                     this.sup('battery',g.suppliers.battery), this.sup('sensors',g.suppliers.sensors)]
                     .reduce((a,s)=>a*s.rel, 1);
    let compShortfall = 1;
    if(Math.random() > compRel){ compShortfall = 1 - (0.18 + Math.random()*0.14)*fxq.shortfallM;
      roundEvents.push({icon:'📦', label:'Component shortfall', text:`A component supplier missed delivery targets — only ${Math.round(compShortfall*100)}% of all units shipped (combined component reliability ${Math.round(compRel*100)}%).`}); }
    // per-source reliability draw this quarter (own factories ramp; partners use their rel)
    const srcDraw={};
    for(const s of srcs){ let f=1; if(Math.random()>s.rel){ f=1-(0.14+Math.random()*0.14)*fxq.shortfallM;
        roundEvents.push({icon:'📦', label:'Shortfall: '+s.name, text:`${s.name} shipped only ${Math.round(f*100)}% of the units routed there (reliability ${Math.round(s.rel*100)}%).`}); }
      srcDraw[s.id]=f; }
    // allocate each producing generation across sources (stored gv.alloc, else optimal), capacity-aware
    const freeCap={}; for(const s of srcs) freeCap[s.id]=s.capacity;
    const genUC={}, genProdK={}, genAlloc={}, allocTotals={};
    const prodSkus = this.liveSkus(g).filter(sk=>sk.gv.production>0)
                       .sort((a,b)=>b.gv.production-a.gv.production);
    for(const sk of prodSkus){
      const plannedK = sk.gv.production*capScale;
      let alloc; const stored=sk.gv.alloc;
      if(stored && Object.keys(stored).length){
        alloc={}; const sum=Object.values(stored).reduce((a,b)=>a+b,0)||1;
        for(const id in stored){ if(freeCap[id]==null) continue; const want=plannedK*(stored[id]/sum);
          const take=Math.min(want, freeCap[id]); if(take>0){ alloc[id]=take; freeCap[id]-=take; } }
        let rem=plannedK-Object.values(alloc).reduce((a,b)=>a+b,0);
        if(rem>0.5){ const top=this.autoAllocate(sk.gv.spec,g,rem,freeCap,'cost',mods).alloc;
          for(const id in top) alloc[id]=(alloc[id]||0)+top[id]; }
      } else {
        alloc=this.autoAllocate(sk.gv.spec,g,plannedK,freeCap,(g.allocMode==='rel'?'rel':'cost'),mods).alloc;
      }
      genAlloc[sk.key]=alloc;
      genUC[sk.key]=this.blendedCost(sk.gv.spec,g,alloc,mods);
      let producedK=0; for(const id in alloc){ producedK+=alloc[id]*(srcDraw[id]||1); allocTotals[id]=(allocTotals[id]||0)+alloc[id]; }
      genProdK[sk.key]=producedK;
    }
    // record per-outsourcer order volume this quarter (for autonomous expansion & negotiation §7)
    for(const o of g.mfgNet.outsourcers){ o.ordersHist=o.ordersHist||[]; o.ordersHist.push(Math.round(allocTotals[o.id]||0)); if(o.ordersHist.length>12) o.ordersHist.shift(); }
    // take-or-pay: a long-term commitment under-ordered this quarter incurs a penalty (§8.2)
    let commitPenalty=0; const commitNews=[];
    for(const k of g.mfgNet.contracts){ if(k.quartersLeft<=0) continue;
      const allocK=allocTotals[k.partnerId]||0;
      if(allocK < k.qtyCommit-0.5){ const o=this.outsourcerById(g,k.partnerId); const ucRep=o? ASM_BASE*o.costM*1.2 : 14;
        const pen=k.penaltyRate*(k.qtyCommit-allocK)*1000*ucRep; commitPenalty+=pen;
        commitNews.push({partner:o?o.name:k.partnerId, under:k.qtyCommit-allocK, pen}); } }
    const srcCityFreight = this.sourceCityForFreight(g, allocTotals);
    // marketing → awareness/brand — campaigns are per model, per selected country group (§6.4)
    // Brand strategy (Cap2 ch.7): range — campaigns feed ONE shared per-country awareness store
    // that applies to all products; unique — a campaign builds only its own product's awareness.
    const unique = this.isUnique(g);
    const co = this.ensureBrandCo(g);
    let mktTotal = 0, consistencySum = 0, nProd = 0;
    // awareness decays ~20%/qtr in every country
    if(unique) for(const m of g.products){ const mc=this.pBrandCo(g,m); for(const c of ACTIVE()) for(const s of SEGMENTS) mc[c.id].aw[s.id]*=0.80; }
    else for(const c of ACTIVE()) for(const s of SEGMENTS) co[c.id].aw[s.id]*=0.80;
    const modelMatch = {};
    for(const m of AM){
      nProd++;
      const budget = m.mkt.budget*1e6; mktTotal += budget;
      const ch = AD_CHANNELS.find(c=>c.id===m.mkt.channel)||AD_CHANNELS[0], cr = CREATIVES.find(c=>c.id===m.mkt.creative)||CREATIVES[0];
      const match = ch.match[m.targetSeg]*0.55 + cr.match[m.targetSeg]*0.45;
      modelMatch[m.id] = match;
      consistencySum += match;
      // channel-shaped gain (DESIGN-media-channels): every segment gains by its own channel ×
      // creative match (non-target segments at 45%) — the channel decides WHERE awareness lands,
      // the budget decides how much. Concentrating on fewer countries multiplies per-country gain.
      const base = (budget/1e6)*0.16*this.campaignConc(m, round);
      const mc = unique? this.pBrandCo(g,m) : co;
      for(const cc of this.campaignCountries(m)){
        const aw = mc[cc].aw;
        for(const s of SEGMENTS){
          const ms = ch.match[s.id]*0.55 + cr.match[s.id]*0.45;
          aw[s.id] = Math.min(1, aw[s.id] + base*ms*(s.id===m.targetSeg? 1 : 0.45));
        }
      }
    }
    const consistency = nProd? consistencySum/nProd : 0;
    // store build counters reset — the 8/qtr construction limit applies per planning quarter
    for(const cc in (g.markets||{})) g.markets[cc].built=0;
    // demand simulation — iterates country × segment; every live generation is its own SKU (§7.1)
    const pcM = mods.elast||1, demandM = mods.demand||1; // mods.elast scales price concern (downturn: price sensitivity up)
    // per-country landed freight $/unit + who-pays mode (DESIGN-logistics §5): under pass-through
    // the customer's effective price rises by freight, dampening price-sensitive demand.
    const freightCty={}, passCty={};
    for(const c of ACTIVE()){ freightCty[c.id]=this.freightToCountry(g,c,srcCityFreight);
      passCty[c.id]=(((g.freightModeByRegion&&g.freightModeByRegion[c.region])||g.freightMode||'absorb')==='passthrough'); }
    const skus = this.liveSkus(g);
    const skuByKey = Object.fromEntries(skus.map(s=>[s.key,s]));
    const perSku = {};
    for(const sk of skus) perSku[sk.key]={demand:0, onlineDemand:0, sold:0, revenue:0, bySeg:{}, feeW:0};
    const compUnits = Object.fromEntries(COMPS().map(c=>[c.id,0]));
    let marketUnits = 0;
    const pAttrs = Object.fromEntries(skus.map(sk=>[sk.key,this.attrs(sk.gv.spec,g)]));
    const pFits = {};
    for(const sk of skus){ pFits[sk.key]={}; for(const s of SEGMENTS) pFits[sk.key][s.id]=this.segFit(sk.gv.spec,pAttrs[sk.key],s.id,g); }
    const compSts={}, compFits={};
    for(const c of COMPS()){ compSts[c.id]=this.competitorState(c,round,g); compFits[c.id]={};
      for(const s of SEGMENTS) compFits[c.id][s.id]=this.competitorFit(c,s.id,round,g); }
    const byCountry = {}; const regionUnits = Object.fromEntries(REGIONS.map(r=>[r.id,0]));
    let unservedTotal = 0, ctrlUnitsW = 0, covDemandW = 0;
    for(const c of ACTIVE()){
      const cm = {units:0, onlineUnits:0, revenue:0, unserved:0, demand:0, cov:0, byModel:{}, ctrlW:0};
      let covW = 0;
      for(const seg of SEGMENTS){
        const D = this.segDemand(c, seg.id, round)*demandM*((mods.segBoost||{})[seg.id]||1);
        marketUnits += D; cm.demand += D;
        const meta = this.covMeta(g, c, seg.id, round);
        const onShare = this.onlineShareCell(g, c, seg.id, round); // online channel share of this cell
        covW += D*meta.cov;
        const utils = [];
        for(const comp of COMPS()){
          const st = compSts[comp.id];
          let u = this.ratingUtil(this.overallRating(seg, c, compFits[comp.id][seg.id], this.brandRating(this.compBrandIn(comp,c.id,round), this.compAwIn(comp,c.id,round)), st.price, pcM),
                  this.compCov(comp, c, seg.id, round));
          if(mods.rivalHit && comp.id===(g._hitRival||COMPS()[0].id)) u*=0.6;
          utils.push({type:'comp', id:comp.id, u});
        }
        // §6: campaigns only apply where you control the channel — awareness feeds Brand Rating
        // (range: every SKU competes with the shared brand; unique: with its own model's brand)
        let meU = 0;
        const effFreight = passCty[c.id]? (freightCty[c.id]||0) : 0; // pass-through raises effective price
        for(const sk of skus){
          const u = this.ratingUtil(this.overallRating(seg, c, pFits[sk.key][seg.id], this.myBrandRatingIn(g, sk.m, c.id, seg.id, meta.ctrl), sk.gv.price + effFreight, pcM),
                    meta.cov);
          utils.push({type:'me', id:sk.key, u}); meU += u;
        }
        // §6.1 shared market: rival human teams' live SKUs compete in the same choice set. Their
        // utility pressure reduces this team's share; each rival books its own units in its own pass.
        if(opts.rivalUtil){ const ru = opts.rivalUtil(c.id, seg.id); if(ru>0) utils.push({type:'rival', id:'rival', u:ru}); }
        const tot = utils.reduce((a,x)=>a+x.u,0)||1;
        for(const x of utils){
          const units = D * x.u/tot;
          if(x.type==='me'){
            perSku[x.id].demand += units; perSku[x.id].bySeg[seg.id]=(perSku[x.id].bySeg[seg.id]||0)+units;
            perSku[x.id].onlineDemand += units*onShare; // online portion needs warehouse stock to fulfil
            perSku[x.id].feeW += units*meta.fee;
            cm.units += units; cm.onlineUnits += units*onShare; cm.revenue += units*skuByKey[x.id].gv.price*(1-meta.fee);
            const mid_ = skuByKey[x.id].m.id;
            cm.byModel[mid_]=(cm.byModel[mid_]||0)+units; cm.ctrlW += units*meta.ctrl;
            ctrlUnitsW += units*meta.ctrl; covDemandW += units;
          } else if(x.type==='comp') compUnits[x.id]+=units; // rival-team utility just dilutes share (booked in its own pass)
        }
        // unserved-demand estimate: share you'd win at ~full coverage minus actual share
        if(meU>0 && meta.cov<0.92){
          const fullU = meU*0.97/Math.max(meta.cov,.02);
          const fullShare = fullU/(tot-meU+fullU);
          cm.unserved += D*Math.max(0, fullShare - meU/tot);
        }
      }
      cm.cov = cm.demand? covW/cm.demand : 0;
      unservedTotal += cm.unserved;
      byCountry[c.id]=cm; regionUnits[c.region]+=cm.units;
    }
    const brandCtrl = covDemandW? ctrlUnitsW/covDemandW : 1; // sales-weighted marketing control
    // ── Warehouse positioning + online fulfilment (DESIGN-logistics): finished goods are shipped
    // to warehouses and held there; ONLINE orders ship only from that warehouse stock, while OFFLINE
    // (store / distributor / carrier) ships direct from the factory pool. Unsold warehouse stock
    // carries forward. w.stock = {skuKey: units} positioned at the warehouse. ──
    const readyWh = this.readyWarehouses(g);
    for(const w of readyWh){ if(!w.stock) w.stock={}; }
    // per-SKU online demand (set during the demand loop) + the unpositioned factory pool
    const onDemBy={}; let totalOnlineDemand=0;
    const factoryPool={}, plannedBy={};
    for(const sk of skus){
      const planned=(genProdK[sk.key]!=null? genProdK[sk.key] : sk.gv.production*capScale)*1000*compShortfall;
      plannedBy[sk.key]=planned;
      const whHeld=readyWh.reduce((a,w)=>a+(w.stock[sk.key]||0),0);        // already positioned (carried over)
      factoryPool[sk.key]=Math.max(0,(sk.gv.inventory||0)-whHeld)+planned;   // unpositioned goods + new production
      const od=perSku[sk.key].onlineDemand||0; onDemBy[sk.key]=od; totalOnlineDemand+=od;
    }
    // ship requested units into each warehouse from the shared factory pool (highest online-demand SKUs first)
    const whWant=this.whSupply(g).byWh;
    let reqTotal=0, recvTotal=0, capBlocked=0, prodBlocked=0;
    const skuByOnline=skus.slice().sort((a,b)=>(onDemBy[b.key]||0)-(onDemBy[a.key]||0));
    for(const w of readyWh){
      const cur=Object.values(w.stock).reduce((a,b)=>a+b,0);
      const headroom=Math.max(0, w.capacity-cur);
      const requested=Math.max(0, whWant[w.id]||0); reqTotal+=requested;
      let avail=0; for(const k in factoryPool) avail+=factoryPool[k];
      let canTake=Math.min(requested, headroom, avail);
      let received=0;
      for(const sk of skuByOnline){ if(canTake<=1) break; const take=Math.min(factoryPool[sk.key], canTake);
        if(take>0){ factoryPool[sk.key]-=take; w.stock[sk.key]=(w.stock[sk.key]||0)+take; received+=take; canTake-=take; } }
      recvTotal+=received;
      const short=requested-received;
      if(short>1){ if(headroom<requested && headroom<=avail) capBlocked+=short; else prodBlocked+=short; }
    }
    if(reqTotal>1 && recvTotal<reqTotal-1000){
      const bits=[];
      if(prodBlocked>1000) bits.push(`${fmtK(prodBlocked)} short on production (raise output or lower the allocation)`);
      if(capBlocked>1000) bits.push(`${fmtK(capBlocked)} blocked by full warehouses (expand capacity)`);
      g.news.unshift({icon:'📦', when:QUARTER_NAMES(round), text:`Warehouse restock shortfall — shipped ${fmtK(recvTotal)} of ${fmtK(reqTotal)} units allocated${bits.length?': '+bits.join('; '):''}.`});
    }
    // production, inventory, sales — OFFLINE drains the factory pool, ONLINE drains warehouse stock (§7.4 step 3)
    let revenue=0, cogs=0, producedCost=0, unitsSold=0, totalDemand=0, stockoutLoss=0, prodDemand=0, onlineLost=0, directOnlineUnits=0;
    const v2 = rulesV(g) >= 2;
    const prodReport = [];
    for(const sk of skus){
      const gv=sk.gv, m=sk.m;
      const stPre = this.genStatus(m,gv);
      const planned = plannedBy[sk.key];
      const qtyK = gv.production*capScale;
      const uc = genUC[sk.key]!=null? genUC[sk.key] : this.unitCost(gv.spec, g, qtyK, mods); // blended source cost (§5.4)
      producedCost += planned*uc;
      const dem = perSku[sk.key].demand;
      const onDem = onDemBy[sk.key]||0, offDem = Math.max(0, dem-onDem);
      // OFFLINE: ship direct from the factory pool
      const offSold = Math.min(offDem, factoryPool[sk.key]); factoryPool[sk.key]-=offSold;
      // ONLINE: fulfil only from warehouse stock (across all operating warehouses)
      let whHeld=0; for(const w of readyWh) whHeld+=(w.stock[sk.key]||0);
      const onSold = Math.min(onDem, whHeld);
      let need=onSold; for(const w of readyWh){ if(need<=0.5) break; const s=w.stock[sk.key]||0; if(s<=0) continue;
        const take=Math.min(s,need); w.stock[sk.key]=s-take; need-=take; }
      // v2: whatever no warehouse could serve ships DIRECT from the factory pool at full
      // cross-border freight (computeFreight already prices it), but slow delivery loses
      // buyers — only DIRECT_ONLINE_CONV of the remainder converts. v1: that demand is lost.
      let directSold = 0;
      if(v2){
        const remaining = Math.max(0, onDem-onSold);
        directSold = Math.min(remaining*DIRECT_ONLINE_CONV, Math.max(0, factoryPool[sk.key]));
        factoryPool[sk.key] -= directSold; directOnlineUnits += directSold;
      }
      onlineLost += Math.max(0, onDem-onSold-directSold);
      const sold = offSold+onSold+directSold;
      // remaining finished goods (factory pool + leftover warehouse stock) carry forward as this gen's inventory
      let whLeft=0; for(const w of readyWh) whLeft+=(w.stock[sk.key]||0);
      gv.inventory = Math.max(0, factoryPool[sk.key]) + whLeft;
      if(gv.inventory < 1) gv.inventory = 0;
      gv.lifetimeSold = (gv.lifetimeSold||0) + sold;
      const fee = dem>0 ? perSku[sk.key].feeW/dem : .08;
      const rev = sold * gv.price * (1-fee);
      revenue += rev; cogs += sold*uc; unitsSold += sold; totalDemand += dem;
      if(gv.production>0){ stockoutLoss += Math.max(0, dem-sold); prodDemand += dem; } // inventory OR online-warehouse shortfall
      perSku[sk.key].sold = sold; perSku[sk.key].revenue=rev; perSku[sk.key].uc=uc;
      prodReport.push({id:sk.key, mid:m.id, gen:gv.gen, model:m.name, name:this.genName(m,gv), status:stPre,
        demand:Math.round(dem), sold:Math.round(sold), produced:Math.round(planned),
        inventory:Math.round(gv.inventory), revenue:rev, price:gv.price, uc, fee, seg:m.targetSeg, directOnline:Math.round(directSold),
        stockout: (gv.production>0 && dem>0) ? Math.max(0,(dem-sold))/dem : 0, bySeg:perSku[sk.key].bySeg});
    }
    if(v2 && (directOnlineUnits>500 || onlineLost>500)){
      // Name the region with the most online demand that no operating warehouse serves.
      const whRegions = new Set(readyWh.map(w=>(cityById(w.city)||{}).region));
      const byReg = {}; for(const c of ACTIVE()){ const cm=byCountry[c.id]; if(cm && !whRegions.has(c.region)) byReg[c.region]=(byReg[c.region]||0)+(cm.onlineUnits||0); }
      const topReg = Object.keys(byReg).sort((a,b)=>byReg[b]-byReg[a])[0];
      const regName = topReg ? (REGIONS.find(r=>r.id===topReg)||{name:topReg}).name : null;
      const directF = directOnlineUnits * this.directRate(g, topReg||'na', srcCityFreight);
      if(directOnlineUnits>500){
        g.news.unshift({icon:'🌐', when:QUARTER_NAMES(round), text:`Online orders shipped direct: ${fmtK(directOnlineUnits)} units went cross-border at full freight (${fmtM(directF)})${onlineLost>500?` and ${fmtK(onlineLost)} orders were lost to slow delivery`:''}.${regName?` A warehouse in ${regName} would have captured most of them (Logistics screen).`:''}`});
      } else {
        g.news.unshift({icon:'🌐', when:QUARTER_NAMES(round), text:`Online stockout — ${fmtK(onlineLost)} online orders went unfulfilled: warehouse stock ran short of online demand (${fmtK(totalOnlineDemand)} online units demanded this quarter). Ship more production to warehouses (Production screen) or expand warehouse capacity.`});
      }
    } else if(!v2 && onlineLost>500){
      const why = readyWh.length
        ? `warehouse stock ran short of online demand (${fmtK(totalOnlineDemand)} online units demanded this quarter)`
        : `online orders can only ship from warehouses and you have none operating`;
      g.news.unshift({icon:'🌐', when:QUARTER_NAMES(round), text:`Online stockout — ${fmtK(onlineLost)} online orders went unfulfilled: ${why}. ${readyWh.length?'Ship more production to warehouses (Production screen) or expand warehouse capacity.':'Build a warehouse (Logistics screen) and allocate production to it.'}`});
    }
    const stockoutRate = prodDemand>0 ? stockoutLoss/prodDemand : 0;
    // §7.4 step 4: auto-retire — the quarter a sell-through gen's inventory hits zero it vanishes
    for(const m of AM) for(const gv of m.gens){
      if(!gv.dev && gv.launched && gv.production===0 && gv.inventory===0 && !gv.eol){
        gv.eol = round;
        g.news.unshift({icon:'🪦', when:QUARTER_NAMES(round), text:this.genName(m,gv)+' reaches end of life — '+fmtK(gv.lifetimeSold||0)+' lifetime units sold.'});
      }
      if(!gv.dev && gv.launched && gv.production===0) gv.wound = true; // wind-down is one-directional across quarters
    }
    // scale per-country results to what was actually sold (inventory constraint applies globally)
    const sellRatio = totalDemand>0 ? unitsSold/totalDemand : 0;
    for(const cc in byCountry){ const cm=byCountry[cc]; cm.units*=sellRatio; cm.onlineUnits*=sellRatio; cm.revenue*=sellRatio; cm.ctrlW*=sellRatio;
      for(const k in cm.byModel) cm.byModel[k]*=sellRatio; }
    // costs — two-leg freight (DESIGN-logistics §4.4): inbound (source→warehouse) + last-leg
    // (warehouse→customer), routed by the capacity-aware greedy allocator. With zero factories &
    // zero warehouses this reproduces the legacy flat FREIGHT_RATES baseline (calibration note).
    const frRes = this.computeFreight(g, byCountry, srcCityFreight, mods);
    const freight = frRes.freight;
    const freightInbound = frRes.inbound, freightLast = frRes.last, freightRecovered = frRes.recovered;
    revenue += freightRecovered; // pass-through: customers reimbursed the freight they were charged
    if(frRes.overflow>50000){ const wOver=(g.warehouses||[]).find(w=>frRes.whLoad[w.id]>=w.capacity*0.999);
      g.news.unshift({icon:'⚠️', when:QUARTER_NAMES(round), text:`A warehouse shipped at capacity — ${fmtK(frRes.overflow)} units were served direct-from-source at full freight. Consider expanding capacity.`}); }
    const whOpexTotal = this.readyWarehouses(g).reduce((a,w)=>a+this.whOpex(w.city,w.capacity),0);
    const factoryFixed = this.factoryFixedTotal(g);
    const storeCount = this.totalStores(g);
    const storeOps = storeCount*7e4; // $70k/qtr per store
    // sell-through inventory is marked down 20% for obsolescence (§7.3)
    const invValue = skus.reduce((a,sk)=>a + sk.gv.inventory*this.unitCost(sk.gv.spec,g,sk.gv.production,mods)
                                            *(this.genStatus(sk.m,sk.gv)==='sellthrough'?0.8:1), 0);
    const holding = invValue*0.025;
    // $300k/qtr per model + $100k/qtr per additional in-production generation — co-selling carries a cost (§7.3)
    const extraGens = AM.reduce((a,m)=>a+Math.max(0,this.prodGens(m).length-1),0);
    // owned-factory fixed cost (every qtr, even idle — the build-vs-buy lesson §4.4) + warehouse opex
    const overhead = 1.2e6 + 0.3e6*AM.length + 0.1e6*extraGens + factoryFixed + whOpexTotal;
    // ── Financing: term-debt service (DESIGN-corporate-finance §5.5) ──
    // Walk player-initiated loans & bonds: accrue interest, amortise principal, or pay the
    // balloon at maturity. Principal movements hit cash directly (not P&L); interest feeds EBT.
    const MIN_CASH = 2e6;                              // solvency floor (also the revolver trigger)
    const revolverRate = g.revolverPenalty!=null? g.revolverPenalty : 0.035;
    let termInterest = 0;
    for(const d of g.debts){
      termInterest += d.rateQ * d.face;
      if(d.amort==='equal'){ const pay = d.face/Math.max(1,d.qLeft); d.face -= pay; g.cash -= pay; }
      d.qLeft -= 1;
      if(d.qLeft<=0 && d.amort==='bullet'){
        g.cash -= d.face;
        g.news.unshift({icon:'💵', when:QUARTER_NAMES(round), text:`${d.kind==='bond'?'Bond':'Loan'} matured — ${fmtM(d.face)} principal repaid in full.`});
        d.face = 0;
      }
    }
    g.debts = g.debts.filter(d=>d.face>1);
    // The revolver is now the emergency overdraft at a penalty rate (§5.4).
    const interest = g.loan*revolverRate + termInterest;
    const ebt = revenue - cogs - mktTotal - rndSpend - freight - storeOps - holding - overhead - interest - commitPenalty;
    const tax = ebt>0 ? ebt*0.24 : 0;
    const profit = ebt - tax;
    g.cumProfit += profit;
    g.grossCumProfit = (g.grossCumProfit||0) + profit;   // pre-dividend cumulative (§12) — profitability scorecard reads this
    // cash: COGS already part of producedCost cash-wise; debt principal already settled above
    g.cash += revenue - producedCost - mktTotal - rndSpend - freight - storeOps - holding - overhead - interest - tax - commitPenalty;
    for(const cn of commitNews) g.news.unshift({icon:'📑', when:QUARTER_NAMES(round), text:`Take-or-pay penalty: you ordered ${fmtK(cn.under*1000)} units below your ${cn.partner} commitment — ${fmtM(cn.pen)} charged.`});
    // ── Dividends (§8.2): paid out of retained earnings, never into insolvency ──
    let divPaid = 0;
    if(profit > 0 && g.divRatio > 0){
      divPaid = Math.min(profit * g.divRatio, Math.max(0, g.cash - MIN_CASH));
      if(divPaid > 0){
        g.cash       -= divPaid;
        g.cumProfit  -= divPaid;                        // dividends reduce retained earnings → lower book equity
        g.divPaidCum += divPaid;
        g.ownerWealth += divPaid * this.ownerPct(g);    // founder pockets their share
        g.news.unshift({icon:'💸', when:QUARTER_NAMES(round), text:`Dividend paid: ${fmtM(divPaid)} (${Math.round(g.divRatio*100)}% of net income) to shareholders. Your ${Math.round(this.ownerPct(g)*100)}% stake earned ${fmtM(divPaid*this.ownerPct(g))}.`});
      }
    }
    // ── Emergency overdraft / repayment (§5.4) ──
    g.revolverUsedQ = false;
    let revolverDraw = 0, odPenalty = 0;
    if(g.cash < MIN_CASH){ const draw = MIN_CASH-g.cash; g.loan += draw; g.cash += draw; g.revolverUsedQ = true; revolverDraw = draw; }
    if(v2){
      // §4.1: the finance-score deduction grows with the overdraft as a multiple of book
      // equity (a floor stands in once equity is gone), from −10 when touched to −85 at 1.5×.
      if(g.revolverUsedQ || g.loan>0){
        const eqNow = this.bookEquity(g);
        const odBase = Math.max(1, eqNow>0? eqNow : (g.startCash||20e6)*0.25);
        odPenalty = 10 + 50*Math.min(1.5, g.loan/odBase);
      }
      if(revolverDraw>0){
        const ratio = g.loan/Math.max(1, this.bookEquity(g));
        g.news.unshift({icon:'🚨', when:QUARTER_NAMES(round), text:`Emergency overdraft drawn: ${fmtM(revolverDraw)} at ${(revolverRate*100).toFixed(1)}%/qtr to stay solvent. Outstanding: ${fmtM(g.loan)}${ratio>0? ` (${ratio.toFixed(1)}× your book equity)`:''}. This quarter it cost ${Math.round(odPenalty)} finance points and about $${(revolverDraw/Math.max(1,g.shares||1)).toFixed(2)} per share. Refinance with a cheaper term loan or raise equity to stop the bleed.`});
      }
    } else if(revolverDraw>0){
      g.news.unshift({icon:'🚨', when:QUARTER_NAMES(round), text:`Emergency overdraft drawn: ${fmtM(revolverDraw)} at ${(revolverRate*100).toFixed(1)}%/qtr penalty rate to stay solvent. Outstanding overdraft: ${fmtM(g.loan)} — refinance with a cheaper term loan or raise equity to stop the bleed.`});
    }
    if(!g.revolverUsedQ && g.loan>0 && g.cash>8e6){ const rep=Math.min(g.loan, g.cash-8e6); g.loan-=rep; g.cash-=rep;
      if(g.loan<=1) g.news.unshift({icon:'✅', when:QUARTER_NAMES(round), text:`Emergency overdraft cleared — back off the penalty rate.`}); }
    // ── Insolvency watch (§4.3, v2): cumulative losses have exhausted book equity. The company
    // keeps trading; debt markets, dividends and buybacks close until equity is positive again.
    if(v2){
      const wasIns = !!g.insolvent;
      const eqNow = this.bookEquity(g);
      g.insolvent = eqNow <= 0;
      if(g.insolvent && !wasIns){ g.insolventSince = round;
        g.news.unshift({icon:'⚠️', when:QUARTER_NAMES(round), text:`Insolvency watch. Cumulative losses have exhausted your book equity (${fmtM(eqNow)}). Banks and bond markets are closed to you and dividends and buybacks are suspended until equity turns positive. Your company keeps trading — fix the losses, or raise equity to recapitalise.`}); }
      if(!g.insolvent && wasIns){ g.insolventSince = null;
        g.news.unshift({icon:'✅', when:QUARTER_NAMES(round), text:`Back to solvency. Book equity is positive again (${fmtM(eqNow)}). Borrowing, dividends and buybacks reopen — your credit rating will take a few quarters to recover.`}); }
    }
    // brand & satisfaction (per live SKU)
    const avgQ = skus.length? skus.reduce((a,sk)=>a+pAttrs[sk.key].quality,0)/skus.length : 0;
    let satSum=0;
    for(const sk of skus){
      const a=pAttrs[sk.key];
      const expected = 30 + sk.gv.price/9;
      const pr = prodReport.find(r=>r.id===sk.key);
      sk.gv.sat = clamp(58 + (a.quality-expected)*0.7 + this.loyaltyOf(g,sk.m)*0.06 - pr.stockout*38, 5, 98);
      satSum+=sk.gv.sat;
    }
    const satisfaction = skus.length? satSum/skus.length : 0;
    // §6: loyalty (Cap2 ch.7) — earned by sales, quality and satisfaction, never bought
    // with ad money. Campaigns buy awareness instead, which feeds Brand Rating directly.
    // Sales through distributors build loyalty at half rate — it's their banner, not yours.
    const flagships = Object.values(g.markets||{}).filter(m=>m.flagship).length;
    // loyalty is earned COUNTRY BY COUNTRY through local sales share, marketing control,
    // quality and satisfaction. A flagship store boosts loyalty mainly in its own country.
    // World aggregates (g.brand, m.bLoyal, g.awareness, m.bAw) are demand-weighted averages.
    const loyalGain = (localShare, ctrlC, consist, isFlag, q, sat) =>
      Math.min(3.0, localShare*33)*(0.5+0.5*ctrlC)*(0.4+consist*0.6)
      + (isFlag? 0.5 : Math.min(0.3, flagships*0.05))
      + (q-55)*0.05 + (sat-60)*0.04 - 0.7;
    let dwT=0; const dWs={};
    for(const c of ACTIVE()){ dWs[c.id]=this.qDemand(c,round); dwT+=dWs[c.id]; }
    if(unique){
      // Unique Brand: each product's per-country loyalty is earned by ITS OWN local sales.
      // g.brand becomes the sales-weighted company average (scorecard, flagships, topbar).
      let wSum=0, lSum=0;
      for(const m of AM){
        const mc = this.pBrandCo(g,m);
        const mSold = prodReport.filter(r=>r.mid===m.id).reduce((a,r)=>a+r.sold,0);
        const mSkus = skus.filter(sk=>sk.m.id===m.id);
        const mQ   = mSkus.length? mSkus.reduce((a,sk)=>a+pAttrs[sk.key].quality,0)/mSkus.length : avgQ;
        const mSat = mSkus.length? mSkus.reduce((a,sk)=>a+sk.gv.sat,0)/mSkus.length : satisfaction;
        let lw=0;
        for(const c of ACTIVE()){
          const cm=byCountry[c.id], e=mc[c.id];
          const ctrlC = cm.units>0? cm.ctrlW/cm.units : 1;
          e.loyal = clamp(e.loyal + loyalGain(cm.demand>0? (cm.byModel[m.id]||0)/cm.demand:0, ctrlC,
                      modelMatch[m.id]||0, !!((g.markets||{})[c.id]||{}).flagship, mQ, mSat), 0, 100);
          lw += dWs[c.id]*e.loyal;
        }
        m.bLoyal = clamp(lw/dwT, 0, 100);
        for(const s of SEGMENTS) m.bAw[s.id] = ACTIVE().reduce((a,c)=>a+dWs[c.id]*mc[c.id].aw[s.id],0)/dwT;
        wSum += mSold+1; lSum += (mSold+1)*m.bLoyal;
      }
      if(wSum) g.brand = clamp(lSum/wSum, 0, 100);
    } else {
      // Range Brand: one shared per-country loyalty score for all products
      let lw=0;
      for(const c of ACTIVE()){
        const cm=byCountry[c.id], e=co[c.id];
        const ctrlC = cm.units>0? cm.ctrlW/cm.units : 1;
        e.loyal = clamp(e.loyal + loyalGain(cm.demand>0? cm.units/cm.demand:0, ctrlC,
                    consistency, !!((g.markets||{})[c.id]||{}).flagship, avgQ, satisfaction), 0, 100);
        lw += dWs[c.id]*e.loyal;
      }
      g.brand = clamp(lw/dwT, 0, 100);
      for(const s of SEGMENTS) g.awareness[s.id] = ACTIVE().reduce((a,c)=>a+dWs[c.id]*co[c.id].aw[s.id],0)/dwT;
    }
    for(const cc in byCountry){ delete byCountry[cc].byModel; delete byCountry[cc].ctrlW; }
    // scorecard
    const totalSold = unitsSold + Object.values(compUnits).reduce((a,b)=>a+b,0);
    const share = totalSold>0 ? unitsSold/totalSold : 0;
    const invTurns = invValue>0 ? cogs/invValue : 4;
    const unservedRate = (totalDemand+unservedTotal)>0 ? unservedTotal/(totalDemand+unservedTotal) : 0;
    const sc = {
      // profitability reads GROSS cumulative profit so paying dividends doesn't perversely tank it (§12)
      profitability: clamp(50 + Math.sign(g.grossCumProfit)*Math.sqrt(Math.abs(g.grossCumProfit)/1e6/round)*16, 0, 100),
      share: clamp(share*700, 0, 100),
      brand: g.brand,
      quality: avgQ,
      satisfaction,
      operations: clamp(92 - stockoutRate*130 - unservedRate*28 - Math.max(0,3-invTurns)*9, 0, 100),
      innovation: clamp(g.rnd.done.reduce((a,id)=>a+(RND_TUNE.tierPts[(TECH_BY_ID[id]||{}).tier]||0),0) + (g.rnd.active?5:0) + AM.length*4
                  + Math.min(24, AM.reduce((a,m)=>a+Math.max(0,m.gens.filter(x=>x.launched).length-1),0)*6), 0, 100), // tier-weighted: deep beats wide (DESIGN-tech-tree §10)
      // finance rewards a WELL-MANAGED capital structure, not just zero debt (§10):
      // healthy leverage + comfortable coverage + a liquidity buffer, penalising emergency-revolver
      // reliance and unaffordable dividends.
      finance: (()=>{
        const eq = this.bookEquity(g);
        const de = this.totalDebt(g)/Math.max(1, eq);
        const annInt = interest*4;
        const cov = annInt>1? (profit*4)/annInt : 99;
        let s = 70;
        s += de<=0.1? -4 : de<=0.3? 5 : de<=1.0? 10 : de<=1.5? 5 : de<=2.0? -6 : -22;  // best in a 0.3–1.0× band
        s += cov>=12? 10 : cov>=6? 6 : cov>=3? 2 : cov>=1.5? -6 : -16;
        s += Math.min(12, Math.max(0,(g.cash/1e6))*0.5);
        if(v2){ s -= odPenalty; if(g.insolvent) s = 0; }   // §4.1 scaled penalty; §4.3 floors while insolvent
        else if(g.revolverUsedQ) s -= 18;
        if(g.divRatio>0 && (profit<=0 || g.revolverUsedQ)) s -= 12;
        return clamp(s, 0, 100);
      })(),
    };
    // §9.3: composite-level control penalty (graduated, never a game-over) for low ownership.
    const ctrlPen = this.controlPenalty(g);
    const composite = (Object.values(sc).reduce((a,b)=>a+b,0)/8) * (1 - ctrlPen);
    // warn the quarter the founder crosses below a control band
    {
      const pct = this.ownerPct(g);
      const band = this.controlBand(pct);
      if(band.key!=='majority' && (g._lastCtrlBand||'majority')!==band.key && pct<(g._lastCtrlPct!=null?g._lastCtrlPct:1)){
        g.news.unshift({icon:'⚠️', when:QUARTER_NAMES(round), text:`Ownership warning: your stake is now ${Math.round(pct*100)}% — "${band.label}". ${band.key==='lost'?'You are effectively a minority holder; the steepest score penalty applies.':'A composite-score penalty applies. Buy back shares or grow earnings to fund with debt instead of dilution.'}`});
      }
      g._lastCtrlBand = band.key; g._lastCtrlPct = pct;
    }
    // events news
    for(const ev of roundEvents) g.news.unshift({icon:ev.icon, when:QUARTER_NAMES(round), text:ev.text||ev.label});
    if(stockoutRate>0.12) g.news.unshift({icon:'🚨', when:QUARTER_NAMES(round), text:`Stockouts: you missed ${fmtK(stockoutLoss)} units of demand (${Math.round(stockoutRate*100)}%). Customers bought rivals instead — consider raising production or safety stock.`});
    // coverage gap news: the biggest market where customers couldn't reach you
    const worstCov = ACTIVE().map(c=>({c, cm:byCountry[c.id]})).filter(x=>x.cm.unserved>15000)
                      .sort((a,b)=>b.cm.unserved-a.cm.unserved)[0];
    if(worstCov){
      const m=(g.markets||{})[worstCov.c.id];
      const covPct=Math.round(worstCov.cm.cov*100);
      g.news.unshift({icon:'🛒', when:QUARTER_NAMES(round),
        text: (m&&m.sub? `Your ${m.n} store${m.n===1?'':'s'} reached only ${covPct}% of ${worstCov.c.name}'s buyers`
                       : `Your distribution reached only ${covPct}% of ${worstCov.c.name}'s buyers`)
              +` — est. ${fmtK(worstCov.cm.unserved)} units of demand went unserved. Expand coverage in the Distribution screen.`});
    }
    const best = prodReport.slice().sort((a,b)=>b.revenue-a.revenue)[0];
    if(best) g.news.unshift({icon:'📈', when:QUARTER_NAMES(round), text:`${QUARTER_NAMES(round)} results: ${fmtK(unitsSold)} units sold, revenue ${fmtM(revenue)}, ${profit>=0?'profit':'loss'} ${fmtM(Math.abs(profit))}. Best seller: ${best.name} (${fmtK(best.sold)} units).`});
    // ── Manufacturing network endogenous systems (§7, §8.2, §10) ──
    const totalProducedK = prodReport.reduce((a,r)=>a+r.produced,0)/1000;
    // mature factory ramp (persist the derived ramp for display) & update partner relationships
    for(const pl of (g.factories||[])) pl.ramp = this.factoryRamp(pl, g);
    for(const o of g.mfgNet.outsourcers){
      const util=(allocTotals[o.id]||0)/Math.max(1,o.capacity);
      const target=clamp(util*140,0,100); o.relationship=clamp((o.relationship!=null?o.relationship:50)+(target-o.relationship)*0.25,0,100);
    }
    // autonomous partner expansion (§7.2): heavy, steady, trusted customers earn investment
    for(const o of g.mfgNet.outsourcers){
      const h=o.ordersHist||[]; if(h.length<3) continue;
      const last3=h.slice(-3), recentUtil=(last3.reduce((a,b)=>a+b,0)/3)/Math.max(1,o.capacity);
      const trend=h[h.length-1]-h[h.length-3];
      if(recentUtil>0.70 && trend>=0 && o.relationship>55 && !(o.lastExpansion&&o.lastExpansion.round>=round-1)){
        const dCap=Math.round(o.capacity*(0.15+0.10*Math.random())); o.capacity+=dCap;
        o.lastExpansion={round, amount:dCap};
        g.news.unshift({icon:'🤝', when:QUARTER_NAMES(round), text:`${o.name} added ${fmtK(dCap*1000)}/qtr of assembly capacity (now ${fmtK(o.capacity*1000)}) — your steady orders made the investment pay for them.`});
      }
    }
    // decrement contracts & expire
    for(const k of g.mfgNet.contracts){ k.quartersLeft-=1;
      if(k.quartersLeft===0){ const o=this.outsourcerById(g,k.partnerId);
        g.news.unshift({icon:'📑', when:QUARTER_NAMES(round), text:`Volume commitment with ${o?o.name:k.partnerId} has expired — discount and take-or-pay terms end.`}); } }
    g.mfgNet.contracts = g.mfgNet.contracts.filter(k=>k.quartersLeft>0);
    // unlock latent outsourcers whose round has arrived (§10)
    g.mfgNet.latent = (g.mfgNet.latent||[]).filter(o=>{
      if(o.unlockRound<=round){ g.mfgNet.outsourcers.push({id:o.id, name:o.name, country:o.country, lat:o.lat, lon:o.lon,
          costM:o.costM, qualM:o.qualM, lead:o.lead, rel:o.rel, capacity:o.capacity, ordersHist:[], relationship:50, lastExpansion:null});
        g.news.unshift({icon:'🏭', when:QUARTER_NAMES(round), text:`New outsourcing option: ${o.name} (${o.country}) is now taking orders — cost ×${o.costM.toFixed(2)}, ${Math.round(o.rel*100)}% reliable, ${fmtK(o.capacity*1000)}/qtr. It appears on your Production allocation.`});
        return false; }
      return true; });
    // capacity-vs-utilization snapshot for the Production chart (§11.3)
    const foxline=g.mfgNet.outsourcers.find(o=>o.id==='cna');
    const capSnap={ own:this.activeFactories(g).reduce((a,p)=>a+p.capacity,0),
      foxline:foxline?foxline.capacity:0,
      other:g.mfgNet.outsourcers.filter(o=>o.id!=='cna').reduce((a,o)=>a+o.capacity,0),
      used:Math.round(totalProducedK) };
    // balance-sheet asset values: book value of built capacity = cumulative capex sunk
    // (setupCost/whCapex are constructed so Σ over assets == total cash spent building them).
    const factoriesVal = (g.factories||[]).reduce((a,p)=>a + this.setupCost(p.city, p.capacity), 0);
    const warehousesVal = (g.warehouses||[]).reduce((a,w)=>a + this.whCapex(w.city, w.capacity), 0);
    // history
    const result = {round, label:QUARTER_NAMES(round), revenue, cogs, marketing:mktTotal, rndSpend, freight, freightInbound, freightLast, whOpex:whOpexTotal, factoryFixed, commitPenalty, capSnap, storeOps, holding, overhead, interest, tax, factoriesVal, warehousesVal,
      profit, cumProfit:g.cumProfit, grossCumProfit:g.grossCumProfit, unitsSold:Math.round(unitsSold), share, brand:g.brand, satisfaction, avgQ,
      stockoutRate, cash:g.cash, loan:g.loan, sc, composite, products:prodReport,
      // ── financing snapshot (DESIGN-corporate-finance) ──
      totDebt:this.totalDebt(g), termDebt:this.termDebt(g), paidIn:g.paidIn, bookEquity:this.bookEquity(g),
      shares:g.shares, ownerShares:g.ownerShares, ownerPct:this.ownerPct(g),
      divRatio:g.divRatio, divPaid, divPaidCum:g.divPaidCum, ownerWealth:g.ownerWealth,
      revolverUsedQ:g.revolverUsedQ, rating:this.creditRating(g, g.history), leverage:this.leverage(g), controlPenalty:ctrlPen,
      revolverDraw, odPenalty:Math.round(odPenalty), insolvent:!!g.insolvent, directOnlineUnits:Math.round(directOnlineUnits), onlineLost:Math.round(onlineLost),
      byCountry, unserved:Math.round(unservedTotal), unservedRate,
      coverageAvg: marketUnits>0 ? ACTIVE().reduce((a,c)=>a+byCountry[c.id].cov*byCountry[c.id].demand,0)/marketUnits : 0,
      ctrlShare: brandCtrl, storeCount,
      events:roundEvents.map(e=>({icon:e.icon,label:e.label})),
      rndCompleted: rndCompleted.map(id=>({icon:TECH_BY_ID[id].icon, name:TECH_BY_ID[id].name, tier:TECH_BY_ID[id].tier, desc:TECH_BY_ID[id].desc})),
      rndActive: rnd.active? {icon:TECH_BY_ID[rnd.active].icon, name:TECH_BY_ID[rnd.active].name} : null,
      compShare: Object.fromEntries(COMPS().map(c=>[c.id, totalSold>0? compUnits[c.id]/totalSold:0]))};
    g.history.push(result);
    this.ensureStock(g); // price the quarter (also back-fills pre-stock-era saves)
    // decision log (for instructor)
    g.log.push({round, label:QUARTER_NAMES(round), by:(g._advanceBy||null),  // §3.7/§4.2 attribution: who submitted this quarter
      decisions:{
        products: skus.map(sk=>({name:this.genName(sk.m,sk.gv), seg:sk.m.targetSeg, price:sk.gv.price, production:sk.gv.production,
          status:this.genStatus(sk.m,sk.gv),
          comps:`${this.comp('cpu',sk.gv.spec.cpu).name} · ${this.comp('display',sk.gv.spec.display).name} · ${this.comp('battery',sk.gv.spec.battery).name} · ${this.comp('casing',sk.gv.spec.casing).name}`,
          mkt:`${fmtM(sk.m.mkt.budget*1e6)} via ${AD_CHANNELS.find(c=>c.id===sk.m.mkt.channel).name} ("${CREATIVES.find(c=>c.id===sk.m.mkt.creative).name}")`})),
        engineering: (g.pendingEng||[]).slice(), // spec diffs + spend per committed generation (§6.6)
        suppliers: Object.fromEntries(Object.entries(g.suppliers).map(([k,v])=>[k, this.sup(k==='chips'?'chips':k, v).name])),
        markets: (()=>{ const act=Object.entries(g.markets||{}).filter(([,m])=>this.hasPaid(m));
          if(!act.length) return 'Cross-border online only';
          return act.map(([cc,m])=>{ const c=this.country(cc);
            return c.name+' ('+[m.sub?(m.flagship?'⭐':'')+'🏪'+m.n:'', m.dist?'🤝':'', m.carrier?'📡':''].filter(Boolean).join(' ')+')';
          }).join(' · ');
        })(),
        rnd: 'Budget '+fmtM(g.rnd.budget||0)+'/qtr'+(g.rnd.active? ' · researching '+TECH_BY_ID[g.rnd.active].name : '')+' · '+g.rnd.done.length+' techs done',
        factories: (g.factories||[]).filter(p=>p.status!=='mothballed').map(p=>(cityById(p.city)||{city:p.city}).city+' '+p.capacity+'k').join(', ')||'none',
        warehouses: (g.warehouses||[]).map(w=>(cityById(w.city)||{city:w.city}).city).join(', ')||'none',
        freightMode: g.freightMode||'absorb',
      }});
    g.pendingEng=[];
    g.round++;
    // construction completes — factories/warehouses scheduled to finish by the new quarter go live (§4 build lead time)
    for(const p of (g.factories||[])) if(p.status==='building' && p.readyRound!=null && p.readyRound<=g.round){
      p.status='active'; p.builtRound=g.round;
      g.news.unshift({icon:'🏭', when:QUARTER_NAMES(g.round), text:(cityById(p.city)||{city:p.city}).city+' factory is now online ('+p.capacity+'k/qtr) and beginning its ramp-up.'}); }
    for(const w of (g.warehouses||[])) if(w.status==='building' && w.readyRound!=null && w.readyRound<=g.round){
      w.status='ready';
      g.news.unshift({icon:'📦', when:QUARTER_NAMES(g.round), text:(cityById(w.city)||{city:w.city}).city+' warehouse is now operational ('+fmtK(w.capacity)+'/qtr) and routing shipments.'}); }
    // launch development generations into the new quarter (§7.4 step 1 / §5.2):
    // committed in Q n, on sale from Q n+1, inheriting the old current gen's production and price + $30
    if(g.round <= g.maxRounds) for(const m of AM){
      const dv = this.devGen(m);
      if(dv && dv.committed < g.round){
        dv.dev=false; dv.launched=g.round;
        const others = this.prodGens(m).filter(x=>x!==dv);
        const src = others.length? others.reduce((a,b)=>a.gen>b.gen?a:b) : null;
        dv.production = src? src.production : 40;
        dv.price = (src? src.price : ((this.liveGens(m).filter(x=>x!==dv).sort((a,b)=>b.gen-a.gen)[0]||{price:199}).price)) + 30;
        // 3-in-production cap (§3.1): the oldest gen auto-winds down at launch — no hidden failures
        while(this.prodGens(m).length > this.genCap(g)){
          const old = this.prodGens(m).reduce((a,b)=>a.gen<b.gen?a:b);
          old.production=0; old.wound=true;
          g.news.unshift({icon:'◌', when:QUARTER_NAMES(g.round), text:this.genName(m,old)+' winds down ('+this.genCap(g)+' generations in production max) — now selling through '+fmtK(old.inventory)+' units of inventory.'});
        }
        g.news.unshift({icon:'🚀', when:QUARTER_NAMES(g.round), text:m.name+' '+dv.gen+' launches at '+fmt$(dv.price)+', producing '+dv.production+'k/qtr — defaults inherited from '+(src?'Gen '+src.gen:'scratch')+', adjust them this quarter.'});
      }
    }
    // rival generational cadence (§6.3): the quarter a rival ships a new gen, aging hardware visibly loses share
    if(g.round <= g.maxRounds && g.round>1 && (g.round-1)%3===0){
      const n=this.compGenNum(g.round);
      for(const c of COMPS()) g.news.unshift({icon:'📡', when:QUARTER_NAMES(g.round), text:c.name+' launches the '+(c.model||c.name)+' '+n+' — their hardware improves, so older designs fall behind in quality points.'});
    }
    if(g.round > g.maxRounds){ g.finished=true;
      g.news.unshift({icon:'🏁', when:QUARTER_NAMES(g.maxRounds), text:`Simulation complete after ${g.maxRounds} quarters. Final composite score: ${Math.round(composite)}/100. Your Final Performance Review — ranking, report card and strategy debrief — is in the 🏁 Final Review tab.`});
    }
    return result;
  },

  demandBreakdown(g, m, gv, segId){
    const seg=SEGMENTS.find(s=>s.id===segId), round=g.round, cc=seg.concerns;
    const a=this.attrs(gv.spec,g);
    const fit=this.segFit(gv.spec,a,segId,g);
    const others=this.liveSkus(g).filter(sk=>!(sk.m.id===m.id && sk.gv.gen===gv.gen))
      .map(sk=>({sk, f:this.segFit(sk.gv.spec,this.attrs(sk.gv.spec,g),segId,g)}));
    const comps=COMPS().map(c=>({c, st:this.competitorState(c,round,g), f:this.competitorFit(c,segId,round,g)}));
    let dW=0, covW=0, myUnits=0, brW=0, pTermW=0;
    for(const c of ACTIVE()){
      const D=this.segDemand(c,segId,round);
      const meta=this.covMeta(g,c,segId,round);
      const myBR=this.myBrandRatingIn(g, m, c.id, segId, meta.ctrl);
      const u=this.ratingUtil(this.overallRating(seg,c,fit,myBR,gv.price), meta.cov);
      let tot=u;
      for(const {c:k,st,f} of comps)
        tot+=this.ratingUtil(this.overallRating(seg,c,f,this.brandRating(this.compBrandIn(k,c.id,round),this.compAwIn(k,c.id,round)),st.price), this.compCov(k,c,segId,round));
      for(const {sk,f} of others)
        tot+=this.ratingUtil(this.overallRating(seg,c,f,this.myBrandRatingIn(g, sk.m, c.id, segId, meta.ctrl),sk.gv.price), meta.cov);
      const std=seg.refPrice*c.income;
      dW+=D; covW+=D*meta.cov; brW+=D*myBR;
      pTermW+=D*clamp((std-gv.price)/std,-1.5,1);
      if(tot>0) myUnits+=D*u/tot;
    }
    const brandR=brW/dW, priceTerm=pTermW/dW;
    return {fit, brandR, priceTerm,
            qualityPts:fit*cc.qc/60, brandPts:brandR*cc.bc/60, pricePts:priceTerm*cc.pc,
            coverage:covW/dW, segShare:myUnits/dW, covReach:covW/dW};
  },

  /* same decomposition for a competitor's product — lets the player inspect a rival's
     Overall Rating with identical math (their share of "others" includes the player). */
  compBreakdown(g, comp, segId){
    const seg=SEGMENTS.find(s=>s.id===segId), round=g.round, cc=seg.concerns;
    const st=this.competitorState(comp,round,g);
    const fit=this.competitorFit(comp,segId,round,g);
    const skus=this.liveSkus(g).map(sk=>({sk, f:this.segFit(sk.gv.spec,this.attrs(sk.gv.spec,g),segId,g)}));
    const otherComps=COMPS().filter(k=>k.id!==comp.id).map(k=>({k, st:this.competitorState(k,round,g), f:this.competitorFit(k,segId,round,g)}));
    let dW=0, covW=0, myUnits=0, pTermW=0, brW=0;
    for(const c of ACTIVE()){
      const D=this.segDemand(c,segId,round);
      const cov=this.compCov(comp,c,segId,round);
      const meta=this.covMeta(g,c,segId,round);
      const cBR=this.brandRating(this.compBrandIn(comp,c.id,round), this.compAwIn(comp,c.id,round));
      const u=this.ratingUtil(this.overallRating(seg,c,fit,cBR,st.price), cov);
      let tot=u;
      for(const {sk,f} of skus)
        tot+=this.ratingUtil(this.overallRating(seg,c,f,this.myBrandRatingIn(g, sk.m, c.id, segId, meta.ctrl),sk.gv.price), meta.cov);
      for(const {k,st:s2,f} of otherComps)
        tot+=this.ratingUtil(this.overallRating(seg,c,f,this.brandRating(this.compBrandIn(k,c.id,round),this.compAwIn(k,c.id,round)),s2.price), this.compCov(k,c,segId,round));
      const std=seg.refPrice*c.income;
      dW+=D; covW+=D*cov; brW+=D*cBR;
      pTermW+=D*clamp((std-st.price)/std,-1.5,1);
      if(tot>0) myUnits+=D*u/tot;
    }
    const priceTerm=pTermW/dW, brandR=brW/dW;
    return {fit, brandR, priceTerm, price:st.price, loyalty:st.brand, aw:comp.aw,
            qualityPts:fit*cc.qc/60, brandPts:brandR*cc.bc/60, pricePts:priceTerm*cc.pc,
            coverage:covW/dW, segShare:myUnits/dW, covReach:covW/dW};
  },

  latestComposite(g){ return g.history.length? g.history[g.history.length-1].composite : null; },

  /* ---------- shareholder value (§ stock performance) ---------- */
  isPublic(g){ return true; }, // §3: every game is a public company now
  /* book equity reads the running paid-in total (was g.startCash) so equity raises and
     buybacks flow through correctly. At migration paidIn===startCash, so nothing shifts. */
  bookEquity(g, r){ return (g.paidIn!=null? g.paidIn : (g.startCash||20e6)) + (r? r.cumProfit : g.cumProfit); },
  /* ── Financing helpers (DESIGN-corporate-finance §4) ── */
  ownerPct(g){ this.ensureFinance(g); return clamp((g.ownerShares||g.shares)/Math.max(1,g.shares), 0, 1); },
  termDebt(g){ this.ensureFinance(g); return (g.debts||[]).reduce((a,d)=>a+d.face,0); },
  totalDebt(g){ return (g.loan||0) + this.termDebt(g); },
  leverage(g, r){ return this.totalDebt(g) / Math.max(1, this.bookEquity(g, r)); },
  /* annualised interest run-rate on all current debt (revolver + term + bonds) */
  annualInterest(g){
    this.ensureFinance(g);
    const rev = (g.loan||0) * (g.revolverUsedQ? (g.revolverPenalty||0.035) : 0.02);
    const term = (g.debts||[]).reduce((a,d)=>a+d.rateQ*d.face, 0);
    return (rev + term) * 4;
  },
  intCoverage(g, H, i){
    const ann = this.annualInterest(g);
    if(ann <= 1) return 99;
    return this.earnTTM(H, i==null? H.length-1 : i) / ann;
  },
  /* credit rating from leverage (D/E) and interest coverage — drives the rate (§5.2) */
  RATINGS:['AAA','AA','A','BBB','BB','B','CCC'],
  RATING_SPREAD:{AAA:0.002, AA:0.004, A:0.007, BBB:0.011, BB:0.018, B:0.026, CCC:0.038},
  creditRating(g, H){
    this.ensureFinance(g);
    const r = (H && H.length)? H[H.length-1] : null;
    const de = this.leverage(g, r);
    const cov = (H && H.length)? this.intCoverage(g, H, H.length-1) : 99;
    // score: lower leverage + higher coverage ⇒ better. 0..6 index into RATINGS.
    let pts = 0;
    pts += de<=0.3?0 : de<=0.6?1 : de<=1.0?2 : de<=1.5?3 : de<=2.0?4.5 : 6;
    pts += cov>=12?0 : cov>=8?0.5 : cov>=5?1 : cov>=3?2 : cov>=1.5?3 : 4.5;
    if(this.bookEquity(g,r)<=0) pts = 6;            // negative equity ⇒ junk
    const idx = clamp(Math.round(pts/1.8), 0, 6);
    return this.RATINGS[idx];
  },
  /* quoted quarterly rate for a NEW facility, given rating + tenor premium (§5.2) */
  TENOR_PREMIUM:{4:0, 8:0.003, 12:0.006, 16:0.008, 20:0.010},
  loanRate(g, H, tenorQ, isBond){
    const rating = this.creditRating(g, H);
    const baseQ = 0.015;
    let spread = this.RATING_SPREAD[rating];
    if(isBond) spread *= 0.7;                       // bond markets reward scale/disclosure for IG firms
    const prem = this.TENOR_PREMIUM[tenorQ]!=null? this.TENOR_PREMIUM[tenorQ] : 0.006;
    return Math.round((baseQ + spread + prem)*1e4)/1e4;
  },
  /* remaining borrowing capacity under the D/E covenant (§5.3) */
  borrowCapacity(g, H){
    this.ensureFinance(g);
    if(g.insolvent) return 0;                               // insolvency watch (§4.3)
    const r = (H && H.length)? H[H.length-1] : null;
    const maxDebt = Math.max(0, this.bookEquity(g, r)) * (g.maxDE!=null? g.maxDE : 2.0);
    return Math.max(0, maxDebt - this.totalDebt(g));
  },
  /* control status band for an ownership fraction (§9.2) */
  controlBand(pct){
    return pct>=0.50? {key:'majority', label:'Majority control', cls:'pos'}
         : pct>=0.33? {key:'working',  label:'Working control',  cls:''}
         : pct>=0.25? {key:'contested',label:'Contested',        cls:'neg'}
         :            {key:'lost',     label:'Lost control',     cls:'neg'};
  },
  /* §12: lightweight AI-rival finance discipline (v1 — no rival balance sheet, just a persona-keyed
     score reflecting leverage discipline + sustainable payout, respecting instrument toggles). */
  rivalFinanceScore(c, g){
    const fin = c.fin || {targetDE:0.7, payer:false};
    const de = fin.targetDE;
    let s = 70;
    s += de<=0.1? -4 : de<=0.3? 5 : de<=1.0? 10 : de<=1.5? 4 : -8;   // same healthy-band shape as the player
    // a steady, affordable dividend is a mild positive — only when dividends are in play for the class
    if(fin.payer && instrEnabled(g,'dividend')) s += 6;
    return clamp(s, 0, 100);
  },
  /* graduated composite penalty for low ownership (§9.3) — never a game-over */
  controlPenalty(g){
    const pct = this.ownerPct(g);
    const base = pct>=0.50? 0 : pct>=0.33? 0.04 : pct>=0.25? 0.10 : 0.18;
    return base * (g.controlSeverity!=null? g.controlSeverity : 1);
  },
  /* migrate any save (old or new) to carry the financing state. Idempotent. */
  ensureFinance(g){
    if(!g) return;
    if(g.startCash==null) g.startCash = 20e6;
    if(g.shares==null) g.shares = Math.max(1, Math.round(g.startCash/10));
    if(g.paidIn==null) g.paidIn = g.startCash;            // bookEquity now reads paidIn (== startCash at migration)
    if(g.ownerShares==null) g.ownerShares = g.shares;     // old saves = 100% founder ⇒ no control penalty
    if(!Array.isArray(g.debts)) g.debts = [];
    if(!Array.isArray(g.seedHolders)) g.seedHolders = [];
    if(g.divRatio==null) g.divRatio = 0;
    if(g.ownerWealth==null) g.ownerWealth = 0;
    if(g.divPaidCum==null) g.divPaidCum = 0;
    if(g.grossCumProfit==null) g.grossCumProfit = g.cumProfit||0;  // before any dividends, gross == retained
    if(g.revolverUsedQ==null) g.revolverUsedQ = false;
    if(g.maxDE==null) g.maxDE = 2.0;
    if(g.revolverPenalty==null) g.revolverPenalty = 0.035;
    if(g.controlSeverity==null) g.controlSeverity = 1;
    if(g.bondThreshold==null) g.bondThreshold = 40e6;
    g.publicCo = true;
  },
  /* trailing-twelve-month earnings, annualized when fewer than 4 quarters exist */
  earnTTM(H, i){
    const t = H.slice(Math.max(0, i-3), i+1);
    return t.length? t.reduce((a,x)=>a+x.profit,0)*(4/t.length) : 0;
  },
  /* Deterministic stock model (GLO-BUS/Capsim style): price anchors on book value per
     share, scaled by a quality multiple from ROE, revenue growth, scorecard and leverage,
     then smoothed (markets don't reprice everything in one quarter). IPO at $10. */
  stockStep(g, H, i, prev){
    const r = H[i];
    const equity = (r.bookEquity!=null)? r.bookEquity : this.bookEquity(g, r);
    const roe = this.earnTTM(H,i) / Math.max(equity, (g.startCash||20e6)*0.25);
    const grow = i>0 && H[i-1].revenue>0 ? clamp(r.revenue/H[i-1].revenue-1, -0.6, 0.6) : 0;
    const debt = (r.totDebt!=null)? r.totDebt : (r.loan||0);   // total debt incl. term loans & bonds
    const lev = equity>0? debt/equity : 2;
    // §8.3 dividend signal: income investors pay up for a SUSTAINABLE payout; an
    // unsustainable one (drawn on the emergency revolver, or payout > earnings) reverses.
    const divR = r.divRatio||0;
    const sustainable = divR>0 && !r.revolverUsedQ && (r.profit||0)>0;
    const divTerm = divR<=0? 0 : (sustainable? Math.min(0.25, divR)*0.5 : -0.25);
    // §10 EPS dilution: shares issued above the IPO float dilute EPS and press the multiple;
    // buybacks (shares below the IPO float) lift it. Symmetric around the IPO share count.
    const ipoShares = Math.max(1, (g.startCash||20e6)/10);
    const sharesNow = (r.shares!=null)? r.shares : (g.shares||ipoShares);
    const dilution = clamp(sharesNow/ipoShares - 1, -0.6, 4) * 0.06;
    const mult = clamp(1 + roe*4.5 + grow*1.2 + (r.composite-50)/120 - lev*0.15 + divTerm - dilution, 0.12, 7);
    const bvps = Math.max(0, equity)/(sharesNow||2e6);
    // §4.2 (v2): an emergency draw knocks draw ÷ shares off the target — Capsim's exact rule.
    const odHit = rulesV(g)>=2 ? (r.revolverDraw||0)/(sharesNow||2e6) : 0;
    const target = Math.max(0.25, bvps*mult - odHit);
    const base = prev==null? 10 : prev;
    return Math.round(Math.max(0.25, base*0.55 + target*0.45)*100)/100;
  },
  /* fill stockPrice into any history rows missing it (new quarters + old-save migration) */
  ensureStock(g){
    if(!g || !g.history || !g.history.length) return;
    if(g.startCash==null) g.startCash = 20e6;
    if(g.shares==null) g.shares = Math.max(1, Math.round(g.startCash/10));
    this.ensureFinance(g);
    const H = g.history;
    if(H[H.length-1].stockPrice!=null) return;
    let prev = null;
    for(let i=0;i<H.length;i++){
      if(H[i].stockPrice==null) H[i].stockPrice = this.stockStep(g, H, i, prev);
      prev = H[i].stockPrice;
    }
    g.stockPrice = prev;
  },
};

/* ===== ES module surface (added during the lift; original prototype used globals) ===== */
export {
  Engine, newGame, newProduct,
  applyGameCfg, ACFG, ACTIVE, COMPS, gameCfgOf, instrEnabled, rulesV, topCountryIds,
  migrateGameV4, migrateGameV5, migrateGameV6,
  COMPONENTS, SUPPLIERS, REGIONS, PROFILES, COUNTRIES, METHODS, CITIES,
  SEGMENTS, AD_CHANNELS, CREATIVES, RND_TUNE, RP_PER_QTR,
  TECH_TREE, TECH_BY_ID, TECH_BRANCHES, computeTechFx,
  STRATEGIES, BRAND_STRATS, COMPETITORS, EVENTS, LESSONS, QUIZ,
  CAP_LEVELS, COMP_LEVELS, GROWTH_RATES, EVENT_RATES, DEFAULT_GAME_CFG,
  QUARTER_NAMES, FREIGHT_RATES, REGION_FREIGHT, DIRECT_ONLINE_CONV,
  FLAGSHIP_COST, FLAGSHIP_BRAND, STORE_SEVERANCE, STORE_BUILD_LIMIT,
  makeMfgNet, clamp, fmtM, fmtK, fmt$,
};
